import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { CurrencyProvider } from './contexts/CurrencyContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AuthModal from './components/AuthModal'
import Navigation from './components/Navigation'
import Hero from './components/Hero'
import Services from './components/Services'
import Team from './components/Team'
import About from './components/About'
import Reviews from './components/Reviews'
import Footer from './components/Footer'
import ForgotPasswordModal from './components/ForgotPasswordModal'
import ResetPassword from './components/ResetPassword'
import VerifyResetCode from './components/VerifyResetCode'
import VerifyEmailCode from './components/VerifyEmailCode'
import AppResetPasswordBridge from './components/AppResetPasswordBridge'
// Lazy load modals that aren't needed on initial page load
const BookingModal = lazy(() => import('./components/BookingModal'))
const ServiceBookingModal = lazy(() => import('./components/ServiceBookingModal'))
const CategoryModal = lazy(() => import('./components/CategoryModal'))
const SpecialistModal = lazy(() => import('./components/SpecialistModal'))
const AlertModal = lazy(() => import('./components/AlertModal'))
const PaymentModal = lazy(() => import('./components/PaymentModal'))

// Lazy load heavy components for code splitting
const Dashboard = lazy(() => import('./components/Dashboard'))
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./components/TermsOfService'))
const TermsOfUse = lazy(() => import('./components/TermsOfUse'))
const ContactPage = lazy(() => import('./components/ContactPage'))
const DeleteAccountPage = lazy(() => import('./components/DeleteAccountPage'))
const OffersPage = lazy(() => import('./components/OffersPage'))
const PackageDetailPage = lazy(() => import('./components/PackageDetailPage'))
const AllServicesPage = lazy(() => import('./components/AllServicesPage'))
import { getCategories } from './services/categoryService'
import { getServices } from './services/serviceService'
import { getProfessionals, clearProfessionalsCache, isServiceExcludedForProfessional, filterProfessionalsForService } from './services/professionalService'
import { getSalon } from './services/salonService'
import { useSalon } from './contexts/SalonContext'
import { getTaxPolicy } from './services/taxService'
import { getCancellationPolicy } from './services/cancellationPolicyService'
import { getReschedulePolicy } from './services/reschedulePolicyService'
import { getReviews, createReview, hasUserReviewedProfessionalForAppointment, recomputeAndPersistProfessionalRating, getReviewsByUser, getReviewsForUserAppointments } from './services/reviewService'
import { createAppointment, createMultiDayAppointments, getUserAppointments, getAvailableTimeSlots, findBestAvailableProfessional, cancelAppointment, cancelAppointmentsByGroupBookingId, updateAppointmentStatus, updateAppointmentPayment } from './services/appointmentService'
import { sendNotification, NOTIFICATION_TYPES, getNotifications, markNotificationAsRead, getUnreadNotificationCount, appendAdminCancellationReason } from './services/notificationService'
import { openNotificationDestination } from './utils/notificationNavigation'
import {
  readBookingCart,
  buildCartItem,
  findCategoryForService,
  mergeCartItems,
  cartToBookingServices,
  resolveCartSpecialist,
  dispatchRestoreBookingCart,
  BOOKING_CART_STORAGE_KEY,
} from './utils/bookingCartUtils'
import { transformCategoriesWithServices, transformProfessionals, transformSalon, transformReviews, transformService } from './services/dataTransformer'
import { recordAppointmentPayment } from './services/appointmentPaymentService'
import { evaluateDurationOverflow, parseServiceDurationMinutes } from './utils/openHoursOverflow'
import { distributeServicesForMultiDay } from './utils/multiDayServices'
import { getAllServices, convertAppointmentToBooking, formatDateForDisplay, formatTimeForDisplay, formatTimeFromDB, calculateBookingTotal, calculateBookingDuration, getPaymentStatus, isAppointmentExpired, isAppointmentAtLeastOneHourAway, getBookingProfessionalDisplay, getBookingProfessionalsForReview, getDealProfessionalsForReview, getDealReviewKey, areAllPackageVisitsCompleted, parseServiceDuration, calculateServiceTimeRange, parseAppointmentDateTime, formatRelativeTime, buildNotificationFromBooking, getDealRescheduleDateConstraints, getGroupBookingId, findDealSiblingBookings, getDealPrimaryBooking, resolvePackageDealForReschedule, isOfferDealBooking } from './components/Dashboard/dashboardUtils'
import { formatPrice } from './utils/currencyUtils'
import { computeDealDiscount } from './utils/dealPricing'
import { useCurrency } from './contexts/CurrencyContext'
import { supabase } from './config/supabase'
// RescheduleModal is now replaced by BookingModal with rescheduleMode prop
import FavoriteServiceModal from './components/FavoriteServiceModal'
import FavoriteProfessionalModal from './components/FavoriteProfessionalModal'
import { DEMO_USER_ID, isDemoMode } from './utils/demoMode'
import { belongsToSalon } from './utils/salonScope'
import {
  savePendingBookingDraft,
  loadPendingBookingDraft,
  clearPendingBookingDraft,
} from './utils/pendingBookingDraft'
import {
  SCHEDULING_MODE_PER_SERVICE,
  canUsePerServiceScheduling,
  applyScheduleToServices,
  earliestSelectionStart,
  serviceScheduleKey,
} from './utils/serviceScheduling'
import { buildOfferPolicyFields, getOfferPolicyFlags } from './utils/offerPackagePolicy'
import {
  createPaymentHoldFromPending,
  confirmPaymentHold,
  releasePaymentHold,
} from './utils/paymentHoldUtils'

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

// After login/OAuth, return to the deals package page when booking was started there.
const getPostAuthRedirectPath = () => {
  try {
    const path = sessionStorage.getItem('dealBookingReturnPath')
    if (path && path.startsWith('/offers')) return path
  } catch (_) {
    // no-op
  }
  return '/'
}

// OAuth Callback Component - handles OAuth redirects
const AuthCallback = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isRedirecting, setIsRedirecting] = useState(false)
  
  useEffect(() => {
    // If user is already signed in, redirect immediately
    if (user && !isRedirecting) {
      console.log('✅ User already signed in, redirecting immediately...')
      setIsRedirecting(true)
      navigate(getPostAuthRedirectPath(), { replace: true })
      return
    }
  }, [user, navigate, isRedirecting])
  
  useEffect(() => {
    const handleCallback = async () => {
      if (isRedirecting) return // Don't process if already redirecting
      
      console.log('🔐 AuthCallback component mounted')
      console.log('📍 Current URL:', window.location.href)
      console.log('📍 Search:', window.location.search)
      console.log('📍 Hash:', window.location.hash)

      // PKCE flow: Supabase redirects back with ?code=...
      // We must exchange the code for a session.
      const url = new URL(window.location.href)
      const code = url.searchParams.get('code')
      const oauthError = url.searchParams.get('error') || url.searchParams.get('error_description')

      if (oauthError) {
        console.error('❌ OAuth error:', oauthError)
        setIsRedirecting(true)
        setTimeout(() => navigate(getPostAuthRedirectPath(), { replace: true }), 1000)
        return
      }

      if (code) {
        // Check if we already have a session (code might have been exchanged already)
        const { data: { session: existingSession } } = await supabase.auth.getSession()
        if (existingSession?.user) {
          console.log('✅ Session already exists, skipping code exchange')
          setIsRedirecting(true)
          navigate(getPostAuthRedirectPath(), { replace: true })
          return
        }
        
        console.log('🔁 PKCE code detected, exchanging for session...')
        const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href)
        if (error) {
          // If error is about code already used or missing verifier, check for existing session
          if (error.message?.includes('code verifier') || error.message?.includes('non-empty')) {
            console.warn('⚠️ Code exchange error (may be duplicate), checking for existing session...')
            const { data: { session: checkSession } } = await supabase.auth.getSession()
            if (checkSession?.user) {
              console.log('✅ Session exists despite exchange error, proceeding...')
              setIsRedirecting(true)
              navigate(getPostAuthRedirectPath(), { replace: true })
              return
            }
          }
          console.error('❌ OAuth code exchange failed:', error)
          setIsRedirecting(true)
          setTimeout(() => navigate(getPostAuthRedirectPath(), { replace: true }), 1000)
          return
        }

        // Clean URL (keep path, remove code params)
        window.history.replaceState({}, document.title, window.location.pathname)
        
        // Check account status in background (non-blocking)
        if (data?.user) {
          // Run account status check in background - don't block redirect
          const checkAccountStatus = async () => {
            try {
              const userId = data.user.id
              let accountStatus = 'active'
              
              // Check account status from database (source of truth)
              const { data: profileData } = await supabase
                .from('profiles')
                .select('account_status')
                .eq('id', userId)
                .maybeSingle()
              
              if (profileData) {
                accountStatus = profileData.account_status || 'active'
              }
              
              // Also check metadata as fallback
              const metadataStatus = data.user.user_metadata?.account_status || 
                                   data.user.raw_user_meta_data?.account_status || 
                                   null
              
              const finalStatus = accountStatus || metadataStatus || 'active'
              
              // If account is inactive, sign out immediately and show message
              if (finalStatus === 'inactive') {
                console.log('🚫 Account is inactive - signing out OAuth user')
                await supabase.auth.signOut()
                
                // Show alert to contact admin
                alert('Your account has been deactivated. Please contact the administrator to reactivate your account.')
                
                // Redirect to home
                navigate(getPostAuthRedirectPath(), { replace: true })
                return
              }
            } catch (statusError) {
              console.error('Error checking account status after OAuth:', statusError)
              // Continue if check fails - don't block login due to check error
            }
          }
          
          // Run check in background (non-blocking)
          checkAccountStatus().catch(err => {
            console.error('Account status check failed:', err)
          })
          
          console.log('✅ OAuth session exchanged, ensuring profile exists...')
          // Run profile creation in background, don't wait
          import('./services/authService').then(({ getOrCreateProfile }) => {
            getOrCreateProfile(data.user).catch(err => {
              console.warn('⚠️ Failed to ensure profile after OAuth:', err)
            })
          })
        }
        
        // Redirect immediately after code exchange
        setIsRedirecting(true)
        console.log('✅ Redirecting to home after successful OAuth...')
        navigate(getPostAuthRedirectPath(), { replace: true })
        return
      }
      
      // Password recovery links should open the reset page, not the OAuth callback
      const hash = window.location.hash
      if (hash.includes('type=recovery')) {
        console.log('🔑 Recovery link detected — redirecting to reset password page')
        navigate(`/reset-password${hash}`, { replace: true })
        return
      }

      // Check if we have OAuth tokens in the hash
      const isOAuthCallback = hash && (
        hash.includes('access_token=') || 
        hash.includes('error=')
      )
      
      if (isOAuthCallback) {
        console.log('✅ OAuth callback detected in /auth/callback route')
        console.log('🔍 Full hash:', hash.substring(0, 300))
        
        // Parse hash to check what we have
        const hashParams = new URLSearchParams(hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        const error = hashParams.get('error')
        
        console.log('🔍 Hash analysis:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          error,
          hashLength: hash.length
        })
        
        if (error) {
          console.error('❌ OAuth error:', error, hashParams.get('error_description'))
          setTimeout(() => {
            navigate(getPostAuthRedirectPath(), { replace: true })
          }, 2000)
          return
        }
        
        // Supabase with detectSessionInUrl: true will automatically process this
        // Wait longer for processing
        console.log('⏳ Waiting for Supabase to process OAuth callback...')
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        // Check if session was created
        let { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        // If no session and we have tokens, try manual session creation
        if (!session && accessToken && refreshToken) {
          console.log('⚠️ Auto-detection failed, trying manual session creation...')
          try {
            const { data: setSessionData, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
            
            if (!setSessionError) {
              const { data: { session: newSession } } = await supabase.auth.getSession()
              session = newSession
            }
          } catch (manualError) {
            console.error('❌ Manual session creation failed:', manualError)
          }
        }
        
        if (session?.user) {
          // Check account status in background (non-blocking) - don't delay redirect
          const checkAccountStatus = async () => {
            try {
              const userId = session.user.id
              let accountStatus = 'active'
              
              // Check account status from database (source of truth)
              const { data: profileData } = await supabase
                .from('profiles')
                .select('account_status')
                .eq('id', userId)
                .maybeSingle()
              
              if (profileData) {
                accountStatus = profileData.account_status || 'active'
              }
              
              // Also check metadata as fallback
              const metadataStatus = session.user.user_metadata?.account_status || 
                                   session.user.raw_user_meta_data?.account_status || 
                                   null
              
              const finalStatus = accountStatus || metadataStatus || 'active'
              
              // If account is inactive, sign out immediately and show message
              if (finalStatus === 'inactive') {
                console.log('🚫 Account is inactive - signing out OAuth user')
                await supabase.auth.signOut()
                
                // Show alert to contact admin
                alert('Your account has been deactivated. Please contact the administrator to reactivate your account.')
                
                // Redirect to home
                navigate(getPostAuthRedirectPath(), { replace: true })
                return
              }
            } catch (statusError) {
              console.error('Error checking account status after OAuth:', statusError)
              // Continue if check fails - don't block login due to check error
            }
          }
          
          // Run check in background (non-blocking)
          checkAccountStatus().catch(err => {
            console.error('Account status check failed:', err)
          })
          
          console.log('✅ OAuth session created successfully:', session.user.email)
          
          // Ensure profile is created/updated after OAuth (don't wait)
          import('./services/authService').then(({ getOrCreateProfile }) => {
            getOrCreateProfile(session.user).catch(err => {
              console.warn('⚠️ Failed to ensure profile after OAuth:', err)
            })
          })
          
          // Redirect immediately (don't wait for account status check)
          setIsRedirecting(true)
            navigate(getPostAuthRedirectPath(), { replace: true })
        } else {
          console.warn('⚠️ OAuth callback but no session found')
          console.warn('⚠️ Check Supabase Dashboard redirect URL configuration')
          // Still redirect to home after a delay
          setIsRedirecting(true)
          setTimeout(() => {
            navigate(getPostAuthRedirectPath(), { replace: true })
          }, 1000)
        }
      } else {
        // No hash-based callback; for PKCE this is fine as long as we exchanged the code above.
        // Check if user is already signed in (code was processed by AuthContext)
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          console.log('✅ User already signed in, redirecting to home')
          setIsRedirecting(true)
          navigate(getPostAuthRedirectPath(), { replace: true })
        } else {
          // Redirect to home anyway after a short delay
        console.log('ℹ️ No hash-based OAuth callback detected, redirecting to home')
          setIsRedirecting(true)
          setTimeout(() => navigate(getPostAuthRedirectPath(), { replace: true }), 500)
        }
      }
    }
    
    handleCallback()
  }, [navigate, isRedirecting])
  
  // Show loading state while processing (but redirect immediately if user exists)
  if (isRedirecting || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
        <p className="text-gray-600">Completing sign in...</p>
        <p className="text-xs text-gray-500 mt-2">Please wait</p>
      </div>
    </div>
  )
}

// Scroll to top component for route changes
const ScrollToTopOnRouteChange = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const prevPathRef = useRef(null)
  
  useEffect(() => {
    // Keep the shared booking cart when moving between home and /services (and other pages).
    // Cart is cleared only after a successful booking / explicit clear — not on route change.
    // (OAuth return and keepBookingCart flags are no longer needed for wipe-prevention.)

    // Disable browser scroll restoration if not already disabled
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    
    // Check if we need to scroll to top from a page refresh
    const scrollFlag = sessionStorage.getItem('_scroll_to_top')
    if (scrollFlag === 'true') {
      // Force scroll to top multiple times to ensure it sticks
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      // Do it again after a tiny delay to override any browser restoration
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 10)
      sessionStorage.removeItem('_scroll_to_top')
    }
    
    // Mark that React Router has navigated (so HTML script doesn't redirect)
    // This flag prevents the HTML script redirect from firing on React Router navigation
    if (pathname !== '/') {
      sessionStorage.setItem('_react_router_nav', 'true')
    }
    
    // Scroll to top on route change
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0

    prevPathRef.current = pathname
  }, [pathname, navigate])
  
  return null
}

/** Keeps deal booking on the package/deals URL instead of home. */
const DealBookingLocationGuard = () => {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const goToDealPath = () => {
      try {
        const path = sessionStorage.getItem('dealBookingReturnPath')
        if (!path || !path.startsWith('/offers')) return
        const pathnameOnly = path.split('?')[0]
        if (location.pathname !== pathnameOnly) {
          navigate(path, { replace: true })
        }
      } catch (_) {
        // no-op
      }
    }
    window.addEventListener('ensureDealBookingPath', goToDealPath)
    return () => window.removeEventListener('ensureDealBookingPath', goToDealPath)
  }, [navigate, location.pathname])

  // Once we're back on the deals page with a pending offer, resume booking.
  useEffect(() => {
    try {
      const returnPath = sessionStorage.getItem('dealBookingReturnPath')
      if (!returnPath || !returnPath.startsWith('/offers')) return
      if (location.pathname !== returnPath.split('?')[0]) return
      const raw = sessionStorage.getItem('pendingBookOffer')
      if (!raw) return
      sessionStorage.removeItem('pendingBookOffer')
      window.dispatchEvent(new CustomEvent('bookPackageOffer', { detail: JSON.parse(raw) }))
    } catch (_) {
      // no-op
    }
  }, [location.pathname])

  return null
}

// Home Page Component with scroll handling
const HomePage = ({
  scrollToServices,
  categories,
  specialists,
  favoriteSpecialists,
  favoriteServices,
  bookings,
  onCategoryClick,
  onFavoriteSpecialist,
  onFavoriteService,
  onSpecialistClick,
  onBookClick,
  onConfirmBooking,
  onBookServiceFromFavorites,
  onBookSpecialistFromFavorites,
  onOfferClick,
  onRequireLogin,
  reviews,
  salon
}) => {
  const location = useLocation()
  const processedStateRef = useRef(null)
  const handlersRef = useRef({ onBookServiceFromFavorites, onBookSpecialistFromFavorites, specialists, onOfferClick })
  const [preSelectedService, setPreSelectedService] = useState(null)

  // Update handlers ref when they change
  useEffect(() => {
    handlersRef.current = { onBookServiceFromFavorites, onBookSpecialistFromFavorites, specialists, onOfferClick }
  }, [onBookServiceFromFavorites, onBookSpecialistFromFavorites, specialists, onOfferClick])

  useEffect(() => {
    // On page refresh, ignore hash navigation - only scroll if it's a React Router navigation
    const isRefresh = !sessionStorage.getItem('_react_router_nav') && !location.state?.scrollTo
    
    // Only handle scroll if it's NOT a refresh (i.e., it's a React Router navigation)
    if (!isRefresh && location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
    
    // Clear any hash on refresh to prevent scrolling to hash sections
    if (isRefresh && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname)
    }
    
    // Create a unique key for the current navigation state
    const stateKey = location.state?.bookOffer
      ? `offer-${location.state.bookOffer?.id || 'unknown'}`
      : location.state?.bookService 
      ? `service-${location.state.bookService?.id || 'unknown'}`
      : location.state?.bookSpecialist 
      ? `specialist-${location.state.bookSpecialist}`
      : null
    
    // Wait for catalog data before applying an offer booking (services must resolve)
    if (location.state?.bookOffer && (!categories || categories.length === 0)) {
      return
    }

    // Only process if we haven't processed this state yet
    if (stateKey && processedStateRef.current !== stateKey) {
      processedStateRef.current = stateKey
      
      const { onBookServiceFromFavorites, onBookSpecialistFromFavorites, specialists, onOfferClick } = handlersRef.current

      // Handle "Book Now" from the offers campaign page
      if (location.state?.bookOffer && onOfferClick) {
        onOfferClick(location.state.bookOffer)
        window.history.replaceState({ ...location.state, bookOffer: null }, '')
      }
      
      // Handle favorite service click from Dashboard
      if (location.state?.bookService && onBookServiceFromFavorites) {
        // Store the service for Services component to use
        setPreSelectedService(location.state.bookService)
        onBookServiceFromFavorites(location.state.bookService)
        // Clear the state to prevent re-triggering
        window.history.replaceState({ ...location.state, bookService: null }, '')
        // Clear pre-selected service after a delay to allow Services component to process it
        setTimeout(() => {
          setPreSelectedService(null)
        }, 2000)
      }
      
      // Handle favorite specialist click from Dashboard
      if (location.state?.bookSpecialist && onBookSpecialistFromFavorites) {
        // Find specialist from specialists array
        const specialist = specialists.find(s => s.id === location.state.bookSpecialist) || location.state.specialist
        if (specialist) {
          onBookSpecialistFromFavorites(specialist)
          // Clear the state to prevent re-triggering
          window.history.replaceState({ ...location.state, bookSpecialist: null, specialist: null }, '')
        }
      }
    }
    
    // Reset processed state when location pathname changes (new navigation)
    if (!stateKey) {
      processedStateRef.current = null
      setPreSelectedService(null)
    }
  }, [location.pathname, location.state, categories])

  // Open login when redirected from a gated offers page
  useEffect(() => {
    if (location.pathname !== '/') return
    if (sessionStorage.getItem('open_login_for_offers') !== 'true') return
    sessionStorage.removeItem('open_login_for_offers')
    if (typeof onRequireLogin === 'function') onRequireLogin()
  }, [location.pathname, onRequireLogin])

  // After professional modal "view cart": keep sticky sidebar, don't open booking modal
  useEffect(() => {
    if (location.pathname !== '/') return
    if (sessionStorage.getItem('keepBookingCart') !== '1') return

    sessionStorage.removeItem('keepBookingCart')
    sessionStorage.removeItem('openBookingFromCart')

    const timer = setTimeout(() => {
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      window.dispatchEvent(new Event('expandServicesBookingCart'))
    }, 120)
    return () => clearTimeout(timer)
  }, [location.pathname, location.state])

  return (
    <>
      <Hero 
        onBookNow={scrollToServices}
        onOfferClick={onOfferClick}
        onRequireLogin={onRequireLogin}
      />
      <Services 
        categories={categories}
        onCategoryClick={onCategoryClick}
        specialists={specialists}
        onConfirmBooking={onConfirmBooking}
        salon={salon}
        preSelectedService={preSelectedService}
        favoriteServices={favoriteServices}
        onFavoriteService={onFavoriteService}
      />
      <Team 
        specialists={specialists}
        favorites={favoriteSpecialists}
        onFavoriteClick={onFavoriteSpecialist}
        onSpecialistClick={onSpecialistClick}
        onBookClick={onBookClick}
      />
      <About salon={salon} />
      <Reviews reviews={reviews} />
      <Footer salon={salon} />
    </>
  )
}

// Navigation Wrapper Component (uses CurrencyContext)
const NavigationWrapper = ({
  user,
  appointments,
  favorites,
  specialists,
  categories,
  services,
  toggleFavorite,
  scrollToServices,
  setAuthMode,
  setShowAuth,
  bookings,
  setBookings,
  setAppointments,
  setBookingData,
  setShowServiceBooking,
  setServiceBookingStep,
  favoriteServicesWithAssociations,
  favoriteProfessionalsWithAssociations,
  setFavoriteServicesWithAssociations,
  setFavoriteProfessionalsWithAssociations,
  favoriteAppointments,
  setFavoriteAppointments,
  cancellationPolicy,
  reschedulePolicy,
  openRescheduleFeePayment,
  openServiceBookingWithServices,
  rescheduleGroupBookingId,
  setRescheduleGroupBookingId,
  pendingPackageReschedule,
  setPendingPackageReschedule,
  pendingPackageRescheduleRef,
}) => {
  const { currency } = useCurrency()
  const { profile } = useAuth()
  const { salonId, salonReady } = useSalon()
  const salonIdRef = useRef(salonId)
  salonIdRef.current = salonId
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [notificationFeed, setNotificationFeed] = useState([])
  const [dbNotifications, setDbNotifications] = useState([])
  const [readNotifications, setReadNotifications] = useState(() => {
    try {
      const stored = localStorage.getItem('readNotifications')
      if (stored) {
        const parsed = JSON.parse(stored)
        return Array.isArray(parsed) ? parsed : []
      }
    } catch (error) {
      console.error('Failed to parse read notifications', error)
    }
    return []
  })
  
  const favoriteSpecialists = specialists.filter(s => favorites?.specialists?.includes(s.id))
  const allServicesList = services && services.length > 0 ? services : getAllServices(categories)
  const favoriteServices = allServicesList.filter(s => favorites?.services?.includes(s.id))
  const [favoriteTab, setFavoriteTab] = useState('services')
  const [selectedFavoriteSpecialist, setSelectedFavoriteSpecialist] = useState(null)
  const [showFavoriteModal, setShowFavoriteModal] = useState(false)
  const [bookingFilter, setBookingFilter] = useState('upcoming')
  const [refreshingAppointments, setRefreshingAppointments] = useState(false)
  const [collapsedBookings, setCollapsedBookings] = useState({})
  const [showReviewForBooking, setShowReviewForBooking] = useState({})
  // reviewData: { [bookingId]: { [professionalId]: { rating, comment } } }
  const [reviewData, setReviewData] = useState({})
  const [activeReviewTab, setActiveReviewTab] = useState({})
  const [packageReviewDays, setPackageReviewDays] = useState({})
  const [submittingReviewBookingId, setSubmittingReviewBookingId] = useState(null)
  const [cancellingBookingId, setCancellingBookingId] = useState(null)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [rescheduleStep, setRescheduleStep] = useState(1) // Step 1 = Date/Time, Step 2 = Review
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [rescheduleBookingOverride, setRescheduleBookingOverride] = useState(null)
  const [rescheduleProfessionalIds, setRescheduleProfessionalIds] = useState([])
  const [rescheduleBookingData, setRescheduleBookingData] = useState({
    specialist: null,
    services: [],
    date: '',
    time: null,
    paymentMethod: null,
    discount: 0,
    discountCode: ''
  })

  const hydrateRescheduleServices = (services = [], sourceBooking = null) =>
    services.map((s) => {
      const dataPros = sourceBooking?.professionalData?.professionals || []
      const matchFromData = dataPros.find(
        (p) =>
          p &&
          (String(p.id) === String(s.professional_id) || String(p.id) === String(s.professional?.id))
      )
      const professional =
        (s.professional && s.professional.id ? s.professional : null) ||
        specialists.find((sp) => String(sp.id) === String(s.professional_id)) ||
        (matchFromData
          ? specialists.find((sp) => String(sp.id) === String(matchFromData.id)) || matchFromData
          : null)
      const serviceId = s.id || s.service_id || null
      const serviceName = s.name || s.service_name || s.title || null
      const nameLower = serviceName ? String(serviceName).toLowerCase().trim() : ''
      // Prefer current-salon catalog match (by id, then by name) so cross-salon
      // service UUIDs from older bookings still resolve to this salon's category.
      let cat =
        (categories || []).find((c) =>
          c.services?.some((cs) => serviceId && String(cs.id) === String(serviceId))
        ) || null
      if (!cat && nameLower) {
        cat = (categories || []).find((c) =>
          c.services?.some(
            (cs) => String(cs.name || '').toLowerCase().trim() === nameLower
          )
        ) || null
      }
      const catalogService =
        cat?.services?.find(
          (cs) =>
            (serviceId && String(cs.id) === String(serviceId)) ||
            (nameLower && String(cs.name || '').toLowerCase().trim() === nameLower)
        ) || null
      const categoryId = cat?.id || s.category_id || s.category?.id || null
      return {
        ...s,
        id: catalogService?.id || serviceId || s.id,
        name: catalogService?.name || serviceName || s.name,
        professional,
        selectedProfessional: s.selectedProfessional || professional,
        professional_id: s.professional_id || professional?.id || null,
        category_id: categoryId,
        category_name: cat?.name || s.category_name || s.categoryName || null,
        group: cat?.name || s.group || s.category_name || null,
      }
    })
  const [alert, setAlert] = useState({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null, loading: false, loadingText: '' })
  const [processingNotificationId, setProcessingNotificationId] = useState(null)
  const cancelInFlightRef = useRef(false)

  // Calculate bookings from appointments (needed for notifications)
  // Note: This uses appointments and bookings props, so it must be after state declarations
  // Using useMemo to recalculate when appointments or bookings change
  const allBookings = React.useMemo(() => {
    const merged = [
      ...(appointments || []).map(convertAppointmentToBooking),
      ...(bookings || []).filter(b => {
        return !appointments?.some(apt => apt.id === b.id)
      })
    ]
    if (!salonId) return merged
    return merged.filter((b) => belongsToSalon(b, salonId))
  }, [appointments, bookings, salonId])

  // Listen for reschedule fee payment success (dispatched from App.jsx)
  useEffect(() => {
    const handleRescheduleFeePaid = (event) => {
      const bookingId = event.detail?.bookingId
      if (!bookingId) return

      // Package reschedule continues in handleRescheduleFeePaymentSuccess
      if (pendingPackageRescheduleRef.current) return

      const booking = allBookings.find(b => b.id === bookingId)
      if (!booking) {
        console.warn('rescheduleFeePaid: booking not found for id', bookingId)
        return
      }

      setSelectedBooking(booking)
      // Initialize reschedule booking data from the appointment
      const effectiveBooking = rescheduleBookingOverride || booking
      setRescheduleBookingData({
        specialist: effectiveBooking.specialist || null,
        services: hydrateRescheduleServices(effectiveBooking.services || [], effectiveBooking),
        date: effectiveBooking.date || '',
        time: effectiveBooking.time || null,
        paymentMethod: effectiveBooking.paymentMethod || effectiveBooking.payment_method || null,
        discount: 0,
        discountCode: ''
      })
      // After paying reschedule fee, start at the first step (option selection)
      setRescheduleStep(0)
      setShowRescheduleModal(true)
    }

    window.addEventListener('rescheduleFeePaid', handleRescheduleFeePaid)
    return () => {
      window.removeEventListener('rescheduleFeePaid', handleRescheduleFeePaid)
    }
  }, [allBookings])

  // Fetch notifications from database
  const fetchDbNotifications = async () => {
    if (!user?.id) return
    const requestedSalonId = salonIdRef.current

    try {
      // Fetch notifications and unread count in parallel
      const [result, countResult] = await Promise.all([
        getNotifications(50, false, { salonId: requestedSalonId }),
        getUnreadNotificationCount({ salonId: requestedSalonId })
      ])

      if (requestedSalonId !== salonIdRef.current) return
      
      // Update unread count from database
      if (countResult && !countResult.error) {
        const nextCount = countResult.count || 0
        setUnreadNotificationCount(nextCount)
        try {
          localStorage.setItem('unreadNotificationCount', String(nextCount))
        } catch (e) {
          console.error('Failed to persist unreadNotificationCount to localStorage (NavigationWrapper):', e)
        }
      }
      
      if (result.data && Array.isArray(result.data)) {
        // Transform database notifications to match notificationFeed format
        const transformed = result.data.map(notif => ({
          id: notif.id,
          title: notif.title,
          message: notif.message,
          data: notif.data || null,
          // Keep a UI category for coloring/icons, but preserve raw type for actions.
          type:
            (notif.type === 'appointment_booked' ||
              notif.type === 'appointment_confirmed' ||
              notif.type === 'appointment_reassignment' ||
              notif.type === 'appointment_rescheduled')
              ? 'booked'
              : (notif.type === 'payment_success' ||
                  (typeof notif.message === 'string' && /refund/i.test(notif.message)))
                ? 'payment'
                : notif.type === 'favorite_added'
                  ? 'favorite'
                  : 'other',
          rawType: notif.type,
          timestamp: notif.created_at,
          timeLabel: formatRelativeTime(notif.created_at),
          isRead: notif.read || false,
          relatedId:
            notif.related_id ||
            notif.data?.relatedId ||
            notif.data?.appointment_id ||
            notif.data?.appointmentId ||
            null
        }))
        
        setDbNotifications(transformed)
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  // Fetch notifications from database
  useEffect(() => {
    setDbNotifications([])
    setUnreadNotificationCount(0)
    fetchDbNotifications()
    // Refresh notifications every 3 seconds
    const interval = setInterval(fetchDbNotifications, 3000)
    return () => clearInterval(interval)
  }, [user?.id, salonId])

  // Combine booking notifications with database notifications
  useEffect(() => {
    const readSet = new Set(readNotifications)

    // Prevent duplicates: skip synthetic booking notifications if a DB notification exists for that appointment
    const dbRelatedIds = new Set(
      dbNotifications
        .map(n => String(n.relatedId || n.related_id || n.data?.relatedId || n.data?.appointment_id || n.data?.appointmentId || ''))
        .filter(Boolean)
    )

    const bookingNotifications = allBookings
      .filter(booking => booking && !dbRelatedIds.has(String(booking.id)))
      .map(buildNotificationFromBooking)
      .filter(Boolean)
      .map(item => ({
        ...item,
        timeLabel: formatRelativeTime(item.timestamp),
        isRead: readSet.has(item.id),
        source: 'booking'
      }))

    // Combine and sort by timestamp (newest first)
    const combined = [...bookingNotifications, ...dbNotifications]
      .sort((a, b) => {
        const dateA = new Date(a.timestamp || 0).getTime()
        const dateB = new Date(b.timestamp || 0).getTime()
        return dateB - dateA
      })
      .map(item => ({
        ...item,
        isRead: item.isRead || readSet.has(item.id)
      }))

    // Ensure notification IDs are completely unique
    const seenIds = new Set()
    const allNotifications = combined.filter(item => {
      if (!item || !item.id) return false
      if (seenIds.has(item.id)) return false
      seenIds.add(item.id)
      return true
    })

    setNotificationFeed(allNotifications)

    // Clean up read notifications that no longer exist
    const existingIds = new Set(allNotifications.map(item => item.id))
    const filteredRead = readNotifications.filter(id => existingIds.has(id))
    const hasChanged =
      filteredRead.length !== readNotifications.length ||
      filteredRead.some((id, index) => id !== readNotifications[index])
    if (hasChanged) {
      setReadNotifications(filteredRead)
    }
  }, [allBookings, dbNotifications, readNotifications])

  useEffect(() => {
    try {
      localStorage.setItem('readNotifications', JSON.stringify(readNotifications))
    } catch (error) {
      console.error('Failed to store read notifications', error)
    }
  }, [readNotifications])

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    // Mark all as read in UI
    setNotificationFeed(prev => prev.map(notification => ({ ...notification, isRead: true })))
    
    // Update read notifications in localStorage
    const allIds = notificationFeed.map(n => n.id)
    setReadNotifications(allIds)
    
    // Mark unread database notifications as read
    try {
      const unreadDbNotifications = dbNotifications.filter(n => !n.isRead)
      await Promise.all(
        unreadDbNotifications.map(notif => markNotificationAsRead(notif.id))
      )
      
      // Refresh database notifications and unread count
      if (user?.id) {
        await fetchDbNotifications()
        // Immediately update unread count to 0 for instant UI feedback
        setUnreadNotificationCount(0)
      }
      
      // Dispatch custom event to notify NavigationWrapper to refresh count immediately
      window.dispatchEvent(new CustomEvent('notificationsMarkedAsRead'))
    } catch (error) {
      console.error('Error marking notifications as read:', error)
    }
  }

  const handleNotificationClick = async (notification) => {
    if (!notification) return

    if (!notification.isRead && notification.id) {
      setNotificationFeed((prev) =>
        prev.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item)),
      )
      setReadNotifications((prev) => (prev.includes(notification.id) ? prev : [...prev, notification.id]))
      try {
        await markNotificationAsRead(notification.id)
        await fetchDbNotifications()
      } catch (error) {
        console.warn('Failed to mark notification as read:', error)
      }
    }

    openNotificationDestination(notification, { setBookingFilter })
  }

  // Keep appointment after admin change (accept the update)
  const handleKeepAppointment = async (notification) => {
    if (!notification?.id) return
    // Instant UI via readNotifications (hides Keep/Cancel immediately)
    setReadNotifications((prev) => (prev.includes(notification.id) ? prev : [...prev, notification.id]))
    setDbNotifications((prev) =>
      (prev || []).map((item) =>
        item.id === notification.id ? { ...item, isRead: true } : item,
      ),
    )
    try {
      window.dispatchEvent(new CustomEvent('notificationsMarkedAsRead'))
    } catch (_) {
      // ignore
    }
    markNotificationAsRead(notification.id).catch((error) => {
      console.error('Error keeping appointment from notification:', error)
    })
  }

  // Handle decline after admin reschedule / reassignment (full refund)
  const handleDeclineReassignment = async (notification) => {
    if (!user?.id || !notification?.relatedId) return
    if (cancelInFlightRef.current || processingNotificationId) return

    setProcessingNotificationId(notification.id)
    setAlert({
      show: true,
      type: 'confirm',
      title: 'Cancel appointment?',
      message:
        'Because the salon changed your booking, cancelling issues a full refund for card payments (no cancellation fee).\n\nDo you want to cancel this appointment?',
      confirmText: 'Cancel Appointment',
      cancelText: 'Keep Appointment',
      loading: false,
      loadingText: 'Cancelling…',
      onCancel: () => {
        if (cancelInFlightRef.current) return
        setProcessingNotificationId(null)
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null, loading: false })
      },
      onConfirm: async () => {
        if (cancelInFlightRef.current) return
        cancelInFlightRef.current = true
        setAlert((prev) => ({
          ...prev,
          loading: true,
          loadingText: 'Cancelling…',
          onCancel: null,
        }))
        try {
          const appointmentId = notification.relatedId
          const { cancelAppointmentAfterAdminChange } = await import('./services/cancelFromAdminChangeService')
          const result = await cancelAppointmentAfterAdminChange(appointmentId)
          if (!result.success) {
            setAlert({
              show: true,
              type: 'error',
              title: 'Error',
              message: result.error || 'Failed to cancel appointment.',
              loading: false,
            })
            return
          }

          if (notification?.id) {
            await markNotificationAsRead(notification.id)
          }

          const refundNote = result.refunded
            ? ` A full refund of $${Number(result.refundAmountUSD || 0).toFixed(2)} has been processed.`
            : result.refundError
              ? ' Your appointment was cancelled; refund needs salon follow-up.'
              : ''

          await sendNotification(
            user.id,
            'Appointment Cancelled',
            `You cancelled after the salon changed your booking.${refundNote}`,
            NOTIFICATION_TYPES.APPOINTMENT_CANCELLED,
            appointmentId
          )

          await refreshAppointments()
          await fetchDbNotifications()

          setAlert({
            show: true,
            type: 'success',
            title: 'Appointment Cancelled',
            message: result.refunded
              ? `Cancelled. Full refund of $${Number(result.refundAmountUSD || 0).toFixed(2)} is on the way.`
              : 'Your appointment has been cancelled.',
            loading: false,
          })
        } catch (error) {
          console.error('Error declining reassignment:', error)
          setAlert({
            show: true,
            type: 'error',
            title: 'Error',
            message: 'Failed to cancel appointment. Please try again.',
            loading: false,
          })
        } finally {
          cancelInFlightRef.current = false
          setProcessingNotificationId(null)
        }
      },
    })
  }
  
  // Upcoming: soonest first (today before tomorrow). Past tabs: newest first.
  const sortBySoonestFirst = (list) => {
    return [...list].sort((a, b) => {
      const aDate = parseAppointmentDateTime(a)
      const bDate = parseAppointmentDateTime(b)
      const aTime = aDate ? aDate.getTime() : 0
      const bTime = bDate ? bDate.getTime() : 0
      return aTime - bTime
    })
  }

  const sortByNewestFirst = (list) => {
    return [...list].sort((a, b) => {
      const aDate = parseAppointmentDateTime(a)
      const bDate = parseAppointmentDateTime(b)
      const aTime = aDate ? aDate.getTime() : 0
      const bTime = bDate ? bDate.getTime() : 0
      return bTime - aTime
    })
  }

  const upcomingBookings = sortBySoonestFirst(
    allBookings.filter(b => {
    if (b.status === 'no_show' || b.status === 'missed') return false
    if (b.status === 'completed' || b.status === 'cancelled') return false
    const isActiveStatus = b.status === 'upcoming' || b.status === 'confirmed' || b.status === 'pending'
    return isActiveStatus
  })
  )
  
  const completedBookings = sortByNewestFirst(allBookings.filter(b => b.status === 'completed'))
  const cancelledBookings = sortByNewestFirst(allBookings.filter(b => b.status === 'cancelled'))
  const missedBookings = sortByNewestFirst(
    allBookings.filter(b => {
    return b.status === 'no_show' || b.status === 'missed'
  })
  )
  
  const getFilteredBookings = () => {
    switch (bookingFilter) {
      case 'completed':
        return completedBookings
      case 'cancelled':
        return cancelledBookings
      case 'missed':
        return missedBookings
      case 'upcoming':
      default:
        return upcomingBookings
    }
  }

  // Helper functions for cancellation / reschedule policy (inside NavigationWrapper)
  const getOfferFlagsFromBooking = (booking) => {
    const src = booking?.deal || booking?.offer || booking || {}
    const flags = getOfferPolicyFlags(src)
    return {
      allowReschedule: flags.allowReschedule,
      allowCancel: flags.allowCancel,
      overrideReschedule: flags.overrideReschedulePolicy,
      overrideCancel: flags.overrideCancelPolicy,
    }
  }

  const getCancellationFeeInfo = (booking) => {
    const noFee = { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    if (!booking || !cancellationPolicy) return noFee
    if (getOfferFlagsFromBooking(booking).overrideCancel) return noFee

    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) return noFee

    const now = new Date()
    const diffHours = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60)

    const feeEnabled =
      cancellationPolicy.feePolicyEnabled &&
      cancellationPolicy.feeWindowHours != null &&
      Number.isFinite(Number(cancellationPolicy.feeWindowHours)) &&
      cancellationPolicy.feePercentage != null &&
      Number.isFinite(Number(cancellationPolicy.feePercentage))

    if (!feeEnabled || diffHours <= 0) return noFee
    if (diffHours > Number(cancellationPolicy.feeWindowHours)) return noFee

    // total_amount on web is stored in PKR; use currency from context to label correctly
    const total = calculateBookingTotal(booking)
    const feePercentage = Number(cancellationPolicy.feePercentage)
    const feeAmount = total * (feePercentage / 100)

    return {
      appliesFee: feePercentage > 0,
      feePercentage,
      feeAmount,
      currencySymbol: currency || 'PKR',
    }
  }

  const getRescheduleFeeInfo = (booking) => {
    if (!booking || !reschedulePolicy) {
      return { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    }
    if (getOfferFlagsFromBooking(booking).overrideReschedule) {
      return { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    }

    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    }

    const now = new Date()
    const diffHours = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60)

    const feeEnabled =
      reschedulePolicy.feePolicyEnabled === true &&
      reschedulePolicy.feeWindowHours != null &&
      Number.isFinite(Number(reschedulePolicy.feeWindowHours)) &&
      reschedulePolicy.feePercentage != null &&
      Number.isFinite(Number(reschedulePolicy.feePercentage))

    if (!feeEnabled || diffHours <= 0) {
      return { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    }

    if (diffHours > Number(reschedulePolicy.feeWindowHours)) {
      return { appliesFee: false, feePercentage: 0, feeAmount: 0, currencySymbol: currency || 'PKR' }
    }

  const total = calculateBookingTotal(booking)
  const feePercentage = Number(reschedulePolicy.feePercentage)
  const feeAmount = total * (feePercentage / 100)

  const currencySymbol = currency || 'PKR'

  return {
    appliesFee: feePercentage > 0,
    feePercentage,
    feeAmount,
    currencySymbol,
  }
}

  const canCancelAppointment = (booking) => {
    if (!booking) return false
    if (!getOfferFlagsFromBooking(booking).allowCancel) return false
    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return false
    }
    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)

    // Offer override: skip salon cancel cutoff; still require future appointment
    if (getOfferFlagsFromBooking(booking).overrideCancel) {
      return diffMinutes > 0
    }
    
    // If we have an admin-defined cancellation policy, use that
    if (cancellationPolicy) {
      const cutoffEnabled =
        cancellationPolicy.cutoffPolicyEnabled &&
        typeof cancellationPolicy.cutoffHoursBefore === 'number'

      // Inside cutoff window -> cannot cancel at all
      if (cutoffEnabled && diffMinutes <= cancellationPolicy.cutoffHoursBefore * 60) {
        return false
      }

      // Otherwise allow cancel any time > 0 minutes before start
      return diffMinutes > 0
    }

    // Allow cancel if appointment is in the future
    return diffMinutes > 0
  }

  const getRescheduleCountFromNotes = (booking) => {
    const notes = booking?.notes || ''
    const match = notes.match(/\[RESCHEDULE_COUNT:(\d+)\]/)
    if (match && match[1]) {
      const n = parseInt(match[1], 10)
      return Number.isNaN(n) ? 0 : n
    }
    return 0
  }

  const canRescheduleAppointment = (booking) => {
    if (!booking) return false
    if (!getOfferFlagsFromBooking(booking).allowReschedule) return false
    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return false
    }
    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)

    // Offer override: skip salon cutoff/max; still require future appointment
    if (getOfferFlagsFromBooking(booking).overrideReschedule) {
      return diffMinutes > 0
    }

    // If we have an admin-defined reschedule policy, use that
    if (reschedulePolicy) {
      const cutoffEnabled =
        reschedulePolicy.cutoffPolicyEnabled &&
        typeof reschedulePolicy.cutoffHoursBefore === 'number'

      // Inside cutoff window -> cannot reschedule at all
      if (cutoffEnabled && diffMinutes <= reschedulePolicy.cutoffHoursBefore * 60) {
        return false
      }

      // Enforce max reschedules per admin settings
      if (reschedulePolicy.maxReschedulesEnabled && typeof reschedulePolicy.maxReschedules === 'number') {
        const currentCount = getRescheduleCountFromNotes(booking)
        if (currentCount >= reschedulePolicy.maxReschedules) {
          return false
        }
      }

      // Otherwise allow reschedule any time > 0 minutes before start
      return diffMinutes > 0
    }

    // Fallback: allow rescheduling if appointment is in the future
    return diffMinutes > 0
  }

  /**
   * Returns why rescheduling is blocked for this booking:
   * - 'cutoff'          → inside cutoff window
   * - 'maxReschedules'  → user has already used all allowed reschedules
   * - 'past'            → appointment time is in the past
   * - 'invalid'         → bad / missing date
   * - 'none'            → rescheduling is allowed
   */
  const getRescheduleBlockReason = (booking) => {
    if (!booking) return 'invalid'

    if (!getOfferFlagsFromBooking(booking).allowReschedule) {
      return 'offerBlocked'
    }

    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return 'invalid'
    }

    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)

    // Past appointments can never be rescheduled
    if (diffMinutes <= 0) {
      return 'past'
    }

    // Offer override: skip salon cutoff/max
    if (getOfferFlagsFromBooking(booking).overrideReschedule) {
      return 'none'
    }
    
    if (reschedulePolicy) {
      const cutoffEnabled =
        reschedulePolicy.cutoffPolicyEnabled &&
        typeof reschedulePolicy.cutoffHoursBefore === 'number'

      if (cutoffEnabled && diffMinutes <= reschedulePolicy.cutoffHoursBefore * 60) {
        return 'cutoff'
      }

      if (reschedulePolicy.maxReschedulesEnabled && typeof reschedulePolicy.maxReschedules === 'number') {
        const currentCount = getRescheduleCountFromNotes(booking)
        if (currentCount >= reschedulePolicy.maxReschedules) {
          return 'maxReschedules'
        }
      }
    }

    return 'none'
  }

  // Refresh appointments function
  // options.silent: when true (default), do not toggle refreshingAppointments — avoids full list "blink"
  // when syncing from realtime, sidebar focus, or another device (e.g. mobile app booking).
  const refreshAppointments = async (options = {}) => {
    const silent = options.silent !== false
    const effectiveUserId = user?.id || (isDemoMode() ? DEMO_USER_ID : null)
    if (!effectiveUserId || !setAppointments) return
    const requestedSalonId = salonIdRef.current
    
    try {
      if (!silent) {
        setRefreshingAppointments(true)
      }
      const result = await getUserAppointments(effectiveUserId, { upcomingOnly: false, salonId: requestedSalonId })
      if (requestedSalonId !== salonIdRef.current) return
      if (result.success) {
        const appointmentsData = result.data || []
        
        // Fetch all reviews for this user's appointments at once
        const appointmentIds = appointmentsData.map(apt => apt.id)
        let reviewsMap = {}
        
        if (appointmentIds.length > 0 && user?.id && !isDemoMode()) {
          const { data: reviewsData, error: reviewsError } = await getReviewsForUserAppointments(user.id, appointmentIds)
          if (reviewsError) {
            console.error('❌ Error fetching reviews:', reviewsError)
          } else if (reviewsData) {
            reviewsMap = reviewsData.reduce((acc, review) => {
              if (!review.appointment_id) return acc
              if (!acc[review.appointment_id]) acc[review.appointment_id] = []
              acc[review.appointment_id].push({
                professional_id: review.professional_id,
                rating: review.rating,
                comment: review.review_text || '',
                date: review.created_at,
              })
              return acc
            }, {})
            // Only log if reviews were found (reduce console noise)
            if (reviewsData.length > 0) {
              console.log('✅ Fetched reviews for appointments:', {
                totalReviews: reviewsData.length,
                appointmentIds: Object.keys(reviewsMap)
              })
            }
          }
        }
        
        // Attach reviews to appointments (array for multi-professional; keep `review` when fully covered)
        const appointmentsWithReviews = appointmentsData.map(appointment => {
          const reviews = reviewsMap[appointment.id] || []
          return {
            ...appointment,
            reviews,
            review: reviews.length > 0
              ? {
                  rating: reviews[0].rating,
                  comment: reviews[0].comment,
                  date: reviews[0].date,
                }
              : null,
          }
        })
        
        // Only log if reviews were attached (reduce console noise)
        const appointmentsWithReviewsCount = appointmentsWithReviews.filter(apt => apt.review).length
        if (appointmentsWithReviewsCount > 0) {
          console.log('📝 Reviews attached to appointments:', {
            totalAppointments: appointmentsWithReviews.length,
            appointmentsWithReviews: appointmentsWithReviewsCount
          })
        }
        
        setAppointments(appointmentsWithReviews)
      }
    } catch (error) {
      console.error('Error refreshing appointments:', error)
    } finally {
      if (!silent) {
        setRefreshingAppointments(false)
      }
    }
  }

  // Initial load + stay in sync when another client (e.g. mobile app) inserts/updates appointments
  useEffect(() => {
    if (!user?.id || !salonReady) return

    setAppointments([])
    refreshAppointments({ silent: true })

    // Campaign demo: no real DB rows for this user — skip realtime
    if (isDemoMode()) {
      return
    }

    let debounceTimer = null
    const channel = supabase
      .channel(`appointments-web-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            refreshAppointments({ silent: true })
          }, 400)
        }
      )
      .subscribe()

    return () => {
      clearTimeout(debounceTimer)
      supabase.removeChannel(channel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, salonId, salonReady])

  // Handle cancel appointment with full validation like dashboard
  const handleCancelAppointment = async (bookingId) => {
    const booking = allBookings.find(b => b.id === bookingId)
    if (!booking) return

    const siblings = findDealSiblingBookings(booking, allBookings)
    const isDealGroup = siblings.length > 1
    const primary = getDealPrimaryBooking(siblings) || booking
    // Use soonest day for cancel-window policy (most restrictive)
    const policyBooking = [...siblings].sort((a, b) => {
      const da = parseAppointmentDateTime(a)?.getTime() || 0
      const db = parseAppointmentDateTime(b)?.getTime() || 0
      return da - db
    })[0] || booking
    
      // Check if cancellation is allowed
      if (!canCancelAppointment(policyBooking)) {
        const offerBlocked = !getOfferFlagsFromBooking(policyBooking).allowCancel
        const appointmentDateTime = parseAppointmentDateTime(policyBooking)
        const formattedDateTime = appointmentDateTime 
          ? appointmentDateTime.toLocaleString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })
          : 'this appointment'
        
        setAlert({
          show: true,
          type: 'warning',
          title: 'Cannot Cancel',
          message: offerBlocked
            ? (isDealGroup
                ? 'Cancellation is turned off for this package offer.'
                : 'Cancellation is turned off for this offer.')
            : isDealGroup
            ? `Cancellation is not available for this package. The earliest day is scheduled for ${formattedDateTime}.`
            : `Cancellation is not available for this appointment. This appointment is scheduled for ${formattedDateTime}.`,
          onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
          onCancel: null
        })
        return
      }
    
    // Get cancellation fee info from the payment-bearing day
    const feeInfo = getCancellationFeeInfo(primary)
    
    // Format appointment details for confirmation message
    const appointmentDateTime = parseAppointmentDateTime(primary)
    const formattedDate = appointmentDateTime 
      ? appointmentDateTime.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : primary.date || 'N/A'
    
    const formattedTime = primary.time?.slot || formatTimeFromDB(primary.start_time || '') || 'N/A'
    
    // Only show late cancellation fee text for card-paid appointments
    const isCardPaid =
      (primary.payment_method === 'card' || primary.paymentMethod === 'card') &&
      (primary.payment_status === 'paid' || primary.paymentStatus === 'paid')

    const feeText = isCardPaid && feeInfo.appliesFee
      ? feeInfo.feeAmount > 0
        ? `\n\nA late cancellation fee of ${feeInfo.feePercentage}% (approx. ${formatPrice(feeInfo.feeAmount, currency)}) applies.`
        : `\n\nA late cancellation fee of ${feeInfo.feePercentage}% applies.`
      : ''

    const message = isDealGroup
      ? `Cancel this ${siblings.length}-day package? All visits will be cancelled.${feeText}\n\nThis cannot be undone.`
      : `Cancel this appointment on ${formattedDate} at ${formattedTime}?${feeText}\n\nThis cannot be undone.`
    
    // Show alert confirmation dialog (consistent with Reschedule button)
    setAlert({
      show: true,
      type: 'confirm',
      title: isDealGroup ? 'Cancel Package' : 'Cancel Appointment',
      message,
      onConfirm: async () => {
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
        await proceedWithCancellation(primary.id, primary, feeInfo, siblings)
      },
      onCancel: () =>
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
    })
    
    return
  }
  
  // Separate function to handle the actual cancellation (extracted for clarity)
  const proceedWithCancellation = async (bookingId, booking, feeInfo, siblingBookings = null) => {

    // Show per-appointment loader while cancellation is in progress
    setCancellingBookingId(bookingId)

    const siblings =
      Array.isArray(siblingBookings) && siblingBookings.length > 0
        ? siblingBookings
        : findDealSiblingBookings(booking, allBookings)
    const isDealGroup = siblings.length > 1
    const groupId = getGroupBookingId(booking)

    // Campaign demo: cancel locally, no refund and no database write
    if (isDemoMode()) {
      try {
        if (isDealGroup && groupId) {
          await cancelAppointmentsByGroupBookingId(groupId)
        } else {
          await updateAppointmentStatus(bookingId, 'cancelled')
        }
        await refreshAppointments({ silent: false })
        setAlert({
          show: true,
          type: 'success',
          title: isDealGroup ? 'Package Cancelled' : 'Appointment Cancelled',
          message: isDealGroup
            ? 'All days of this package were cancelled in this demo session. Nothing was saved.'
            : 'Your appointment was cancelled in this demo session. Nothing was saved.',
          onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
          onCancel: null
        })
      } finally {
        setCancellingBookingId(null)
      }
      return
    }

    try {
      // Check if this is a database appointment
      const dbAppointment = appointments?.find(apt => apt.id === bookingId)
      
      if (dbAppointment) {
        // Check if payment was made by card and needs refund
        const isCardPaid = 
          (booking.payment_method === 'card' || booking.paymentMethod === 'card') &&
          (booking.payment_status === 'paid' || booking.paymentStatus === 'paid')
        const paymentIntentId = booking.payment_intent_id || booking.paymentIntentId
        
        // For card payments, get the actual USD amount charged to Stripe
        // This is critical because total_amount in DB might be in PKR
        let originalAmount = 0
        
        if (isCardPaid && paymentIntentId) {
          try {
            // Method 1: Try to get actual charged amount from appointment_payments table
            const { supabase } = await import('./config/supabase')
            const { data: paymentRecords, error: paymentError } = await supabase
              .from('appointment_payments')
              .select('amount_usd')
              .eq('appointment_id', bookingId)
              .eq('user_id', user?.id || booking.user_id)
              .eq('payment_type', 'booking_charge')
              .eq('direction', 'charge')
              .eq('payment_intent_id', paymentIntentId)
              .order('created_at', { ascending: false })
              .limit(1)
            
            if (!paymentError && paymentRecords && paymentRecords.length > 0 && paymentRecords[0].amount_usd) {
              originalAmount = parseFloat(paymentRecords[0].amount_usd)
              console.log('✅ Found original charged amount from appointment_payments:', originalAmount, 'USD')
            } else {
              // Method 2: Fallback - calculate from services (services are stored in USD)
              console.log('⚠️ No payment record found, calculating from services...')
              const calculatedTotal = calculateBookingTotal(booking)
              
              if (calculatedTotal > 0 && calculatedTotal < 1000) {
                // If calculated total is reasonable (< $1000), use it
                originalAmount = calculatedTotal
                console.log('✅ Using calculated total from services:', originalAmount, 'USD')
              } else {
                // Method 3: Last resort - use total_amount from DB but validate
                const dbTotalAmount = parseFloat(booking.totalAmount || dbAppointment.total_amount || 0)
                // If the amount seems too large (likely PKR), throw error
                if (dbTotalAmount > 1000) {
                  console.error('❌ Cannot determine original USD amount. DB total_amount appears to be in PKR:', dbTotalAmount)
                  throw new Error('Unable to determine original payment amount. Please contact support with payment ID: ' + paymentIntentId)
                }
                originalAmount = dbTotalAmount
                console.warn('⚠️ Using total_amount from DB as fallback:', originalAmount, 'USD')
              }
            }
          } catch (amountError) {
            console.error('❌ Error determining original payment amount:', amountError)
            throw amountError
          }
        } else {
          // For cash payments, use totalAmount from booking
          originalAmount = parseFloat(booking.totalAmount || dbAppointment.total_amount || 0)
        }
        
        let refundResult = null
        let notesToSave = booking.notes || dbAppointment.notes || null
        
        // Process refund if card payment
        if (isCardPaid && paymentIntentId) {
          try {
            const { processRefund } = await import('./services/refundService')
            
            // Calculate refund amount: original payment - cancellation fee
            // feeAmountUSD is already in USD from getCancellationFeeInfo
            const feeAmountUSD = feeInfo.feeAmountUSD ?? (isCardPaid && originalAmount > 0 ? originalAmount * ((feeInfo.feePercentage || 0) / 100) : 0)
            const refundAmount = feeInfo.appliesFee 
              ? Math.max(0, originalAmount - feeAmountUSD) // Refund original minus fee
              : originalAmount // Full refund if no fee
            
            console.log('💰 Processing refund:', {
              paymentIntentId,
              originalAmountUSD: originalAmount,
              cancellationFee: feeInfo.appliesFee ? feeAmountUSD : 0,
              refundAmountUSD: refundAmount,
              feePercentage: feeInfo.feePercentage
            })
            
            // Validate refund amount doesn't exceed original charge
            if (refundAmount > originalAmount) {
              console.error('❌ Refund amount exceeds original charge:', {
                refundAmount,
                originalAmount,
                feeAmountUSD
              })
              throw new Error(`Refund amount ($${refundAmount.toFixed(2)}) cannot exceed original charge ($${originalAmount.toFixed(2)}). Please contact support.`)
            }
            
            refundResult = await processRefund(paymentIntentId, refundAmount > 0 ? refundAmount : null)
            
            // Handle "already refunded" error gracefully - treat as success
            if (!refundResult.success && refundResult.error?.includes('already been refunded')) {
              console.log('ℹ️ Charge already refunded, treating as success')
              refundResult = {
                success: true,
                refundId: 'already_refunded',
                refundAmountUSD: refundAmount,
                status: 'succeeded',
              }
            }
            
            if (refundResult.success) {
              console.log('✅ Refund processed successfully:', refundResult.refundId)
              
              // Record refund in appointment_payments table
              try {
                const paymentRecordResult = await recordAppointmentPayment({
                  appointmentId: bookingId,
                  userId: user?.id || booking.user_id,
                  paymentType: 'refund',
                  direction: 'refund',
                  amountUSD: refundResult.refundAmountUSD,
                  paymentIntentId: paymentIntentId,
                  refundId: refundResult.refundId,
                  meta: {
                    reason: 'cancellation',
                    refund_type: feeInfo.appliesFee ? 'partial' : 'full',
                    cancellation_fee: feeInfo.appliesFee ? feeAmountUSD : 0,
                  },
                })
                if (!paymentRecordResult.success) {
                  console.warn('⚠️ Failed to record refund in appointment_payments:', paymentRecordResult.error)
                }
              } catch (paymentRecordError) {
                console.warn('⚠️ Exception recording refund in appointment_payments:', paymentRecordError)
              }
              
              // Add refund info to notes
              const refundTag = feeInfo.appliesFee
                ? `[REFUND_PROCESSED:${refundResult.refundId}:${refundResult.refundAmountUSD.toFixed(2)}USD:PARTIAL]`
                : `[REFUND_PROCESSED:${refundResult.refundId}:${refundResult.refundAmountUSD.toFixed(2)}USD:FULL]`
              notesToSave = notesToSave ? `${notesToSave}\n${refundTag}` : refundTag
            } else {
              console.error('❌ Refund failed:', refundResult.error)
              // Don't block cancellation if refund fails, but log it
              // Check if it's an "already refunded" error - this is okay
              const isAlreadyRefunded = refundResult.error?.includes('already been refunded') || 
                                        refundResult.error?.includes('already refunded')
              if (isAlreadyRefunded) {
                console.log('ℹ️ Charge was already refunded, continuing with cancellation')
                // Don't add error tag for already-refunded case
              } else {
                const refundErrorTag = `[REFUND_FAILED:${refundResult.error || 'Unknown error'}]`
                notesToSave = notesToSave ? `${notesToSave}\n${refundErrorTag}` : refundErrorTag
              }
            }
          } catch (refundError) {
            console.error('❌ Exception processing refund:', refundError)
            // Check if it's an "already refunded" error
            const isAlreadyRefunded = refundError.message?.includes('already been refunded') || 
                                      refundError.message?.includes('already refunded')
            if (isAlreadyRefunded) {
              console.log('ℹ️ Charge was already refunded (exception), continuing with cancellation')
              // Don't add error tag for already-refunded case
            } else {
              // Don't block cancellation if refund fails
              const refundErrorTag = `[REFUND_ERROR:${refundError.message || 'Exception occurred'}]`
              notesToSave = notesToSave ? `${notesToSave}\n${refundErrorTag}` : refundErrorTag
            }
          }
        }
        
        // Add cancellation fee info to notes if applicable
        if (feeInfo.appliesFee) {
          // Ensure originalAmount is defined (defensive check)
          const safeOriginalAmount = originalAmount ?? parseFloat(booking.totalAmount || dbAppointment?.total_amount || 0)
          // Calculate feeAmountUSD: use feeAmountUSD if available, otherwise calculate from originalAmount
          const feeAmountUSD = feeInfo.feeAmountUSD ?? (isCardPaid && safeOriginalAmount ? safeOriginalAmount * ((feeInfo.feePercentage || 0) / 100) : 0)
          const feePercentage = feeInfo.feePercentage ?? 0
          
          // Record cancellation fee charge (only for card payments, as cash fees are not charged)
          if (isCardPaid && feeAmountUSD > 0) {
            try {
              const paymentRecordResult = await recordAppointmentPayment({
                appointmentId: bookingId,
                userId: user?.id || booking.user_id,
                paymentType: 'cancellation_fee',
                direction: 'charge',
                amountUSD: feeAmountUSD,
                paymentIntentId: paymentIntentId, // Original payment intent
                meta: {
                  fee_percentage: feePercentage,
                  reason: 'late_cancellation',
                  original_amount: safeOriginalAmount,
                  refund_amount: refundResult?.refundAmountUSD || 0,
                },
              })
              if (!paymentRecordResult.success) {
                console.warn('⚠️ Failed to record cancellation fee in appointment_payments:', paymentRecordResult.error)
              }
            } catch (paymentRecordError) {
              console.warn('⚠️ Exception recording cancellation fee in appointment_payments:', paymentRecordError)
            }
          }
          
          const feeTag = isCardPaid
            ? `[LATE_CANCEL_FEE_CARD:${feePercentage}%:${feeAmountUSD.toFixed(2)}USD]`
            : `[LATE_CANCEL_FEE_CASH:${feePercentage}%:${feeAmountUSD.toFixed(2)}USD]`
          notesToSave = notesToSave ? `${notesToSave}\n${feeTag}` : feeTag
        }
        
        // Update in database — whole deal group when multi-day, else single row
        console.log('📅 Cancelling database appointment(s):', {
          bookingId,
          isDealGroup,
          groupId,
          siblingCount: siblings.length,
        })
        try {
          const { supabase } = await import('./config/supabase')
          const paymentStatusUpdate =
            refundResult?.success && isCardPaid ? 'refunded' : null

          let adminCancelReason = null
          if (isDealGroup && groupId) {
            const groupResult = await cancelAppointmentsByGroupBookingId(groupId, {
              primaryAppointmentId: bookingId,
              notes: notesToSave,
              paymentStatus: paymentStatusUpdate,
            })
            if (!groupResult.success) {
              throw new Error(groupResult.error || 'Failed to cancel package')
            }
            // Cancel any sibling that might not share group id yet (safety)
            for (const sib of siblings) {
              if (sib?.id && !groupResult.cancelledIds?.includes(sib.id)) {
                await updateAppointmentStatus(sib.id, 'cancelled')
              }
            }
            console.log('✅ Package deal cancelled in database:', groupResult.cancelledIds)
          } else {
            const updateFields = {
              status: 'cancelled',
              updated_at: new Date().toISOString(),
              ...(paymentStatusUpdate ? { payment_status: paymentStatusUpdate } : {}),
            }
            if (notesToSave) {
              updateFields.notes = notesToSave
            }

            const { data, error } = await supabase
              .from('appointments')
              .update(updateFields)
              .eq('id', bookingId)
              .select()
              .single()

            if (error) {
              throw error
            }
            adminCancelReason = data?.admin_cancellation_reason
            console.log('✅ Appointment cancelled in database')

            try {
              await supabase
                .from('appointment_segments')
                .delete()
                .eq('appointment_id', bookingId)
            } catch (segError) {
              console.warn('⚠️ Error deleting appointment segments:', segError)
            }
          }
          
          // Send notification for cancelled appointment
          try {
            const cancelDateTime = parseAppointmentDateTime(booking)
            const cancelFormattedDate = cancelDateTime
              ? cancelDateTime.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : booking.date || 'N/A'
            const cancelFormattedTime =
              booking.time?.slot || formatTimeFromDB(booking.start_time || '') || 'N/A'
            const servicesList = isDealGroup
              ? siblings
                  .flatMap((d) => (d.services || []).map((s) => s.name))
                  .filter(Boolean)
                  .join(', ') || 'your services'
              : booking.services?.map((s) => s.name).join(', ') || 'your services'
            const professionalName = getBookingProfessionalDisplay(booking) || 'your specialist'
            let notificationMessage = isDealGroup
              ? `Your ${siblings.length}-day package (${servicesList}) has been cancelled.`
              : `Your appointment for ${servicesList} with ${professionalName} on ${cancelFormattedDate} at ${cancelFormattedTime} has been cancelled.`
            
            // Add refund info to notification if applicable
            if (refundResult?.success) {
              if (feeInfo.appliesFee) {
                notificationMessage += ` A refund of ${formatPrice(refundResult.refundAmountUSD, currency)} has been processed (cancellation fee of ${formatPrice(feeInfo.feeAmountUSD, currency)} applied).`
              } else {
                notificationMessage += ` A full refund of ${formatPrice(refundResult.refundAmountUSD, currency)} has been processed.`
              }
            }
            notificationMessage = appendAdminCancellationReason(
              notificationMessage,
              adminCancelReason
            )
            
            await sendNotification(
              user.id,
              isDealGroup ? 'Package Cancelled' : 'Appointment Cancelled',
              notificationMessage,
              NOTIFICATION_TYPES.APPOINTMENT_CANCELLED,
              bookingId
            )
          } catch (notifError) {
            console.warn('⚠️ Failed to send cancellation notification:', notifError)
          }
          
          // Small delay to ensure database update is committed
          await new Promise(resolve => setTimeout(resolve, 100))
          
          // Refresh appointments from database to update UI
          await refreshAppointments()
          
          // Show success message with refund info (using setAlert for consistency)
          let successMessage = isDealGroup
            ? `All ${siblings.length} days of this package were cancelled successfully.`
            : 'Appointment cancelled successfully.'
          if (refundResult?.success) {
            const safeOriginalAmount = originalAmount ?? parseFloat(booking.totalAmount || dbAppointment?.total_amount || 0)
            const feeAmountUSD = feeInfo.feeAmountUSD ?? (isCardPaid && safeOriginalAmount ? safeOriginalAmount * ((feeInfo.feePercentage || 0) / 100) : 0)
            if (feeInfo.appliesFee && feeAmountUSD > 0) {
              successMessage += ` Refund of ${formatPrice(refundResult.refundAmountUSD, currency)} processed (cancellation fee of ${formatPrice(feeAmountUSD, currency)} applied).`
            } else {
              successMessage += ` Full refund of ${formatPrice(refundResult.refundAmountUSD, currency)} processed.`
            }
          } else if (isCardPaid && paymentIntentId && !refundResult?.success) {
            // Check if it's an "already refunded" error - don't show error message for that
            const isAlreadyRefunded = refundResult?.error?.includes('already been refunded') || 
                                      refundResult?.error?.includes('already refunded')
            if (!isAlreadyRefunded) {
              successMessage += ' Note: Refund processing encountered an issue. Please contact support with your payment ID.'
            }
          }
          setAlert({
            show: true,
            type: 'success',
            title: 'Success',
            message: successMessage,
            onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
            onCancel: null
          })
        } catch (updateError) {
          console.error('❌ Failed to cancel appointment:', updateError)
          setAlert({
            show: true,
            type: 'error',
            title: 'Error',
            message: updateError.message || 'Failed to cancel appointment. Please try again.',
            onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
            onCancel: null
          })
        }
      } else {
        // Update local booking
        const updatedBookings = bookings.map(b => 
          b.id === bookingId 
            ? { ...b, status: 'cancelled', updatedAt: new Date().toISOString() }
            : b
        )
        setBookings(updatedBookings)
        localStorage.setItem('bookings', JSON.stringify(updatedBookings))
        setAlert({
          show: true,
          type: 'success',
          title: 'Success',
          message: 'Appointment cancelled successfully.',
          onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
          onCancel: null
        })
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error)
      window.alert('Failed to cancel appointment. Please try again.')
    } finally {
      // Always clear loader state
      setCancellingBookingId(null)
    }
  }

  // Handle reschedule - show confirm first, then open modal
  const handleReschedule = async (booking, closeSidebar) => {
    const {
      isDealGroup,
      siblings,
      deal: packageDeal,
      totalDays: packageTotalDays,
      offerId: packageOfferId,
      groupId: packageGroupId,
    } = resolvePackageDealForReschedule(booking, allBookings)
    const primary = getDealPrimaryBooking(siblings) || booking
    const policyBooking = [...siblings].sort((a, b) => {
      const da = parseAppointmentDateTime(a)?.getTime() || 0
      const db = parseAppointmentDateTime(b)?.getTime() || 0
      return da - db
    })[0] || booking

    // Check if rescheduling is allowed
    if (!canRescheduleAppointment(policyBooking)) {
      const appointmentDateTime = parseAppointmentDateTime(policyBooking)
      const formattedDateTime = appointmentDateTime 
        ? appointmentDateTime.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        : 'this appointment'

      const blockReason = getRescheduleBlockReason(policyBooking)
      let message = ''

      if (blockReason === 'offerBlocked') {
        message = isDealGroup
          ? 'Rescheduling is turned off for this package offer.'
          : 'Rescheduling is turned off for this offer.'
      } else if (blockReason === 'maxReschedules') {
        message = isDealGroup
          ? 'You have already used all allowed reschedules for this package.'
          : 'You have already used all allowed reschedules for this appointment.'
      } else if (blockReason === 'cutoff') {
        message = 'Rescheduling is not available this close to the appointment time.'
      } else {
        message = `Rescheduling is not available for this ${isDealGroup ? 'package' : 'appointment'}. Scheduled for ${formattedDateTime}.`
      }
      
      setAlert({
        show: true,
        type: 'warning',
        title: 'Cannot Reschedule',
        message,
        onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
        onCancel: null
      })
      return
    }
    
    // Build fee text (based on current appointment time) — card payments ONLY
    const feeInfo = getRescheduleFeeInfo(primary)
    let feeText = ''
    const isCardPaid =
      (primary.payment_method === 'card' || primary.paymentMethod === 'card') &&
      (primary.payment_status === 'paid' || primary.paymentStatus === 'paid')

    // For cash customers, we don't show any reschedule fee message.
    if (isCardPaid && feeInfo.appliesFee) {
      const sym = feeInfo.currencySymbol || 'PKR'
      feeText = feeInfo.feeAmount > 0
        ? `\n\nA reschedule fee of ${feeInfo.feePercentage}% (approx. ${sym} ${feeInfo.feeAmount.toFixed(2)}) applies.`
        : `\n\nA reschedule fee of ${feeInfo.feePercentage}% applies.`
    }

    // Format current appointment details for info
    const appointmentDateTime = parseAppointmentDateTime(primary)
    const formattedDate = appointmentDateTime
      ? appointmentDateTime.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : 'this appointment'
    const formattedTime = appointmentDateTime
      ? appointmentDateTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        })
      : ''

    const message = isDealGroup
      ? `Reschedule this ${siblings.length}-day package? You'll choose new dates for all visits.${feeText}`
      : `Reschedule this appointment (${formattedDate}${formattedTime ? ` at ${formattedTime}` : ''})?${feeText}`

    setAlert({
      show: true,
      type: 'confirm',
      title: isDealGroup ? 'Reschedule Package' : 'Reschedule Appointment',
      message,
      onConfirm: async () => {
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })

        // Multi-day package: rebook all days together (not day-wise)
        if (isDealGroup) {
          if (closeSidebar && typeof closeSidebar === 'function') {
            closeSidebar()
          }
          const groupId = packageGroupId || getGroupBookingId(primary)
          const services = siblings.flatMap((day) => {
            const dayNum = day.day_number || 1
            return (day.services || []).map((s) => ({
              ...s,
              day_number: s.day_number || dayNum,
              professional: s.professional || day.specialist || null,
            }))
          })
          const offerId =
            packageOfferId || siblings.find((d) => d.offer_id)?.offer_id || null
          const totalDays = Math.max(
            packageTotalDays || 1,
            ...siblings.map((d) => Number(d.day_number) || 1),
            siblings.length,
          )

          // Load live offer policy/discount so review checkout matches the original deal
          let offerPolicy = buildOfferPolicyFields(primary)
          let discountPercent = Number(primary?.discount_percent) || 0
          if (offerId) {
            try {
              const { supabase } = await import('./config/supabase')
              const { data: offerRow } = await supabase
                .from('hero_offers')
                .select(
                  'id, title, discount_percent, starts_at, ends_at, total_days, allowed_payment_methods, payment_methods, reschedule_allowed, cancel_allowed, reschedule_policy_override, cancel_policy_override, allow_reschedule, allow_cancel, override_reschedule_policy, override_cancel_policy',
                )
                .eq('id', offerId)
                .maybeSingle()
              if (offerRow) {
                discountPercent = Number(offerRow.discount_percent) || discountPercent
                offerPolicy = buildOfferPolicyFields(offerRow)
              }
            } catch (offerLoadErr) {
              console.warn('[PackageReschedule] could not load offer policy:', offerLoadErr)
            }
          }

          const preservedMethod =
            primary.payment_method || primary.paymentMethod || 'card'
          const preservedStatus =
            primary.payment_status || primary.paymentStatus || 'unpaid'
          const feeAppliesNow =
            isCardPaid && feeInfo.appliesFee && Number(feeInfo.feeAmount) > 0

          const packagePayload = {
            services,
            specialist: primary.specialist || siblings[0]?.specialist || null,
            startStep: 1,
            skipOverflowCheck: true,
            rescheduleGroupId: groupId,
            paymentMethod: preservedMethod,
            packageReschedule: {
              active: true,
              paymentMethod: preservedMethod,
              paymentStatus: preservedStatus,
              feeAmount: feeAppliesNow ? Number(feeInfo.feeAmount) || 0 : 0,
              feePercentage: feeAppliesNow ? Number(feeInfo.feePercentage) || 0 : 0,
              feeCharged: false,
              currencySymbol: feeInfo.currencySymbol || currency || 'PKR',
            },
            deal: {
              id: offerId,
              title:
                packageDeal?.title ||
                siblings.find((d) => d.offer_title)?.offer_title ||
                (offerId ? 'Package deal' : 'Multi-day booking'),
              total_days: totalDays,
              isDurationSplit: !offerId,
              isPackageOffer: !!offerId,
              discountPercent,
              serviceIds: services.map((s) => s.id).filter(Boolean),
              ...offerPolicy,
            },
          }

          const startPackageReschedule = (feeAlreadyPaid = false) => {
            setPendingPackageReschedule(null)
            openServiceBookingWithServices({
              ...packagePayload,
              packageReschedule: {
                ...packagePayload.packageReschedule,
                feeCharged: feeAlreadyPaid || !feeAppliesNow,
              },
            })
          }

          // Card-paid packages may need a reschedule fee before picking new days
          if (!isDemoMode() && feeAppliesNow) {
            let feeAlreadyPaid = false
            try {
              const { supabase } = await import('./config/supabase')
              const { data: { session } } = await supabase.auth.getSession()
              if (session?.user) {
                const { data: freshAppointment } = await supabase
                  .from('appointments')
                  .select('notes')
                  .eq('id', primary.id)
                  .eq('user_id', session.user.id)
                  .single()
                const match = freshAppointment?.notes?.match(/\[RESCHEDULE_FEE_PAID:(\d+):([^\]]+)\]/)
                if (match?.[1]) {
                  const hoursSincePaid = (Date.now() - parseInt(match[1], 10)) / (1000 * 60 * 60)
                  if (hoursSincePaid < 24 && hoursSincePaid >= 0) feeAlreadyPaid = true
                }
                if (!feeAlreadyPaid) {
                  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
                  const { data: paymentRecords } = await supabase
                    .from('appointment_payments')
                    .select('created_at')
                    .eq('appointment_id', primary.id)
                    .eq('user_id', session.user.id)
                    .eq('payment_type', 'reschedule_fee')
                    .eq('direction', 'charge')
                    .gte('created_at', twentyFourHoursAgo)
                    .limit(1)
                  if (paymentRecords?.length) feeAlreadyPaid = true
                }
              }
            } catch (feeCheckErr) {
              console.warn('[PackageReschedule] fee check failed:', feeCheckErr)
            }

            if (feeAlreadyPaid) {
              startPackageReschedule(true)
            } else if (typeof openRescheduleFeePayment === 'function') {
              setPendingPackageReschedule({
                ...packagePayload,
                packageReschedule: {
                  ...packagePayload.packageReschedule,
                  feeCharged: true,
                },
              })
              openRescheduleFeePayment(primary, feeInfo, closeSidebar)
            } else {
              startPackageReschedule(false)
            }
            return
          }

          startPackageReschedule(false)
          return
        }

        // Check if reschedule fee was already paid by fetching fresh data from database
        const appointmentId = booking?.id
        let feeAlreadyPaid = false
        
        if (appointmentId && isCardPaid && feeInfo.appliesFee && feeInfo.feeAmount > 0 && !isDemoMode()) {
          // Show loading state
          setAlert({
            show: true,
            type: 'info',
            title: 'Checking...',
            message: 'Checking if reschedule fee was already paid...',
            onConfirm: null,
            onCancel: null
          })
          
          try {
            const { supabase } = await import('./config/supabase')
            const { data: { session } } = await supabase.auth.getSession()
            
            if (session?.user) {
              // Method 1: Check notes for RESCHEDULE_FEE_PAID tag
              const { data: freshAppointment, error: appointmentError } = await supabase
                .from('appointments')
                .select('notes')
                .eq('id', appointmentId)
                .eq('user_id', session.user.id)
                .single()
              
              if (!appointmentError && freshAppointment?.notes) {
                const match = freshAppointment.notes.match(/\[RESCHEDULE_FEE_PAID:(\d+):([^\]]+)\]/)
                if (match && match[1]) {
                  const paidTimestamp = parseInt(match[1], 10)
                  const now = Date.now()
                  const hoursSincePaid = (now - paidTimestamp) / (1000 * 60 * 60)
                  
                  console.log('[RescheduleFeeCheck] Found fee paid tag in notes:', {
                    timestamp: paidTimestamp,
                    hoursAgo: hoursSincePaid,
                    paymentIntentId: match[2],
                    isValid: hoursSincePaid < 24 && hoursSincePaid >= 0,
                  })
                  
                  // Fee is valid if paid within last 24 hours
                  if (hoursSincePaid < 24 && hoursSincePaid >= 0) {
                    feeAlreadyPaid = true
                    console.log('✅ Reschedule fee already paid (from notes tag), proceeding directly to reschedule')
                  } else {
                    console.log('[RescheduleFeeCheck] Fee tag expired (older than 24 hours)')
                  }
                }
              }
              
              // Method 2: Fallback - Check appointment_payments table for recent reschedule fee payment
              if (!feeAlreadyPaid) {
                const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
                
                const { data: paymentRecords, error: paymentError } = await supabase
                  .from('appointment_payments')
                  .select('created_at, payment_intent_id')
                  .eq('appointment_id', appointmentId)
                  .eq('user_id', session.user.id)
                  .eq('payment_type', 'reschedule_fee')
                  .eq('direction', 'charge')
                  .gte('created_at', twentyFourHoursAgo)
                  .order('created_at', { ascending: false })
                  .limit(1)
                
                if (!paymentError && paymentRecords && paymentRecords.length > 0) {
                  const latestPayment = paymentRecords[0]
                  const paymentTime = new Date(latestPayment.created_at).getTime()
                  const now = Date.now()
                  const hoursSincePaid = (now - paymentTime) / (1000 * 60 * 60)
                  
                  console.log('[RescheduleFeeCheck] Found reschedule fee payment in appointment_payments:', {
                    paymentIntentId: latestPayment.payment_intent_id,
                    hoursAgo: hoursSincePaid,
                    createdAt: latestPayment.created_at,
                  })
                  
                  if (hoursSincePaid < 24 && hoursSincePaid >= 0) {
                    feeAlreadyPaid = true
                    console.log('✅ Reschedule fee already paid (from appointment_payments table), proceeding directly to reschedule')
                    
                    // Add the tag to notes for future checks (non-blocking)
                    try {
                      const existingNotes = freshAppointment?.notes || ''
                      const timestamp = Date.now()
                      const feePaidTag = `[RESCHEDULE_FEE_PAID:${timestamp}:${latestPayment.payment_intent_id}]`
                      const updatedNotes = existingNotes 
                        ? `${existingNotes}\n${feePaidTag}`
                        : feePaidTag
                      
                      const { error: updateError } = await supabase
                        .from('appointments')
                        .update({ notes: updatedNotes })
                        .eq('id', appointmentId)
                        .eq('user_id', session.user.id)
                      
                      if (updateError) {
                        throw updateError
                      }
                      console.log('✅ Added RESCHEDULE_FEE_PAID tag to notes for future checks')
                    } catch (tagError) {
                      console.warn('⚠️ Failed to add tag to notes (non-blocking):', tagError)
                    }
                  }
                } else {
                  console.log('[RescheduleFeeCheck] No recent reschedule fee payment found in appointment_payments table')
                }
              }
            }
          } catch (checkError) {
            console.warn('[RescheduleFeeCheck] Error checking fee status:', checkError)
            // If check fails, proceed with normal flow (ask for payment)
          }
        }

        // Hide loading alert
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })

        // If fee was already paid, skip payment and go directly to reschedule
        if (feeAlreadyPaid) {
          if (closeSidebar && typeof closeSidebar === 'function') {
            closeSidebar()
          }
          setSelectedBooking(booking)
          // Initialize reschedule booking data from the appointment
          const effectiveBooking = rescheduleBookingOverride || booking
          setRescheduleBookingData({
            specialist: effectiveBooking.specialist || null,
            services: hydrateRescheduleServices(effectiveBooking.services || [], effectiveBooking),
            date: effectiveBooking.date || '',
            time: effectiveBooking.time || null,
            paymentMethod: effectiveBooking.paymentMethod || effectiveBooking.payment_method || null,
            discount: 0,
            discountCode: ''
          })
          setRescheduleStep(0) // Start at step 0 (Choose)
          setShowRescheduleModal(true)
        } else if (!isDemoMode() && isCardPaid && feeInfo.appliesFee && feeInfo.feeAmount > 0 && typeof openRescheduleFeePayment === 'function') {
          // If card-paid and fee applies, take reschedule fee payment first (handled by parent via callback)
          openRescheduleFeePayment(booking, feeInfo, closeSidebar)
          return
        } else {
          // No fee (or cash) → go directly to reschedule modal
          if (closeSidebar && typeof closeSidebar === 'function') {
            closeSidebar()
          }
          setSelectedBooking(booking)
          // Initialize reschedule booking data from the appointment
          const effectiveBooking = rescheduleBookingOverride || booking
          setRescheduleBookingData({
            specialist: effectiveBooking.specialist || null,
            services: hydrateRescheduleServices(effectiveBooking.services || [], effectiveBooking),
            date: effectiveBooking.date || '',
            time: effectiveBooking.time || null,
            paymentMethod: effectiveBooking.paymentMethod || effectiveBooking.payment_method || null,
            discount: 0,
            discountCode: ''
          })
          setRescheduleStep(0) // Start at step 0 (Choose)
          setShowRescheduleModal(true)
        }
      },
      onCancel: () =>
        setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
    })
  }

  // Handle reschedule submit (date/time change)
  const handleRescheduleSubmit = async (newDate, newTime) => {
    if (!selectedBooking) return

    const dealConstraints = getDealRescheduleDateConstraints(selectedBooking, allBookings)
    if (dealConstraints) {
      const key = String(newDate || '').slice(0, 10)
      if (
        (dealConstraints.minDate && key && key < dealConstraints.minDate) ||
        (dealConstraints.maxDate && key && key > dealConstraints.maxDate) ||
        (dealConstraints.disabledDates || []).includes(key)
      ) {
        setAlert({
          show: true,
          type: 'warning',
          title: 'Invalid package date',
          message:
            'This date breaks the order of your multi-day package. Day 1 must stay before Day 2 (and so on).',
          onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
          onCancel: null,
        })
        return
      }
    }
    
    // Modal state (rescheduleBookingData) holds the current specialist/services while rescheduling.
    // selectedBooking is the original row only — without rescheduleBookingData, a changed professional is ignored.
    const effectiveBooking = rescheduleBookingOverride || rescheduleBookingData || selectedBooking
    
    try {
      // Check if this is a database appointment
      const dbAppointment = appointments?.find(apt => apt.id === selectedBooking.id)
      
      if (dbAppointment) {
        // Reschedule behavior: cancel old appointment and create a new one (tagged rescheduled)
        const timeStr = newTime.slot || newTime || ''
        const [time, period] = timeStr.split(' ')
        const [hours, minutes] = time.split(':')
        let hour24 = parseInt(hours) || 0
        if (period === 'PM' && hour24 !== 12) hour24 += 12
        if (period === 'AM' && hour24 === 12) hour24 = 0
        const startTime = `${hour24.toString().padStart(2, '0')}:${minutes || '00'}:00`

        const effectiveServices = (effectiveBooking.services || []).map((service) => {
          const professional =
            service.professional && service.professional.id && service.professional.id !== 'any'
              ? service.professional
              : (service.professional_id
                  ? specialists.find((s) => String(s.id) === String(service.professional_id)) || null
                  : null)
          return {
            ...service,
            professional: professional || service.professional || null,
            professional_id: professional?.id || service.professional_id || null,
          }
        })

        // Build professionalData similar to createAppointment
        const serviceProfessionals = effectiveServices
          .map(s => s.professional)
          .filter(p => p && p.id && p.id !== 'any')

        const professionalIds = [...new Set(
          effectiveServices
            .map(s => s.professional_id)
            .filter(Boolean)
        )]

        const primaryProfessional =
          (professionalIds.length > 0 &&
            serviceProfessionals.find(p => p.id === professionalIds[0])) ||
          serviceProfessionals[0] ||
          effectiveBooking.specialist ||
          null

        const professionalDataFormatted = serviceProfessionals.length > 0
          ? {
              primary: primaryProfessional || null,
              professionals: serviceProfessionals,
              professionalIds,
              original:
                effectiveBooking.specialist &&
                effectiveBooking.specialist.id &&
                effectiveBooking.specialist.id !== 'any'
                  ? effectiveBooking.specialist
                  : null
            }
          : (effectiveBooking.professionalData || null)

        const durationMinutes = effectiveServices.reduce((sum, s) => {
          if (!s) return sum
          if (typeof s.duration_minutes === 'number') return sum + s.duration_minutes
          const n = parseInt(String(s.duration || '0').replace(/\D/g, ''), 10)
          return sum + (Number.isNaN(n) ? 60 : n)
        }, 0) || (dbAppointment.duration_minutes || 60)

        const totalAmount =
          (typeof effectiveBooking.totalAmount === 'number' ? effectiveBooking.totalAmount : null) ||
          (typeof dbAppointment.total_amount === 'number' ? dbAppointment.total_amount : null) ||
          effectiveServices.reduce((sum, s) => sum + (parseFloat(s.price || 0) || 0), 0)

        const rescheduleFromTag = `[RESCHEDULED_FROM:${selectedBooking.id}]`
        const currentRescheduleCount = getRescheduleCountFromNotes(selectedBooking)
        const nextRescheduleCount = currentRescheduleCount + 1
        const rescheduleCountTag = `[RESCHEDULE_COUNT:${nextRescheduleCount}]`
        const combinedNotes = `${rescheduleFromTag}\n${rescheduleCountTag}`

        const createResult = await createAppointment({
          userId: user?.id || null,
          profileId: user?.id || null,
          professionalId: (primaryProfessional && primaryProfessional.id) ? primaryProfessional.id : dbAppointment.professional_id,
          salonId: dbAppointment.salon_id || null,
          appointmentDate: newDate,
          startTime: startTime,
          durationMinutes: durationMinutes,
          services: effectiveServices,
          professionalData: professionalDataFormatted,
          totalAmount: totalAmount,
          paymentMethod: dbAppointment.payment_method || 'cash',
          paymentStatus: dbAppointment.payment_status || 'unpaid',
          status: 'confirmed',
          notes: combinedNotes,
          paymentIntentId: dbAppointment.payment_intent_id || null, // Copy payment intent ID from original appointment
          excludeAppointmentId: selectedBooking.id
        })

        if (!createResult.success) {
          console.warn('⚠️ Failed to create rescheduled appointment:', createResult.error)
          setAlert({
            show: true,
            type: 'error',
            title: 'Error',
            message: createResult.error || 'Failed to reschedule appointment. Please try again.',
            onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
            onCancel: null
          })
          return
        }

        const newAppointmentId = createResult.data?.id

        // Cancel old appointment (and entire package group if this day belonged to a deal)
        const oldGroupId = getGroupBookingId(selectedBooking)
        if (oldGroupId) {
          const groupCancel = await cancelAppointmentsByGroupBookingId(oldGroupId)
          if (!groupCancel.success) {
            console.warn('⚠️ Failed to cancel old package during reschedule:', groupCancel.error)
          }
        } else {
          const cancelResult = await updateAppointmentStatus(selectedBooking.id, 'cancelled')
          if (!cancelResult.success) {
            console.warn('⚠️ Failed to cancel old appointment during reschedule:', cancelResult.error)
          }
          try {
            if (!isDemoMode()) {
              await supabase
                .from('appointment_segments')
                .delete()
                .eq('appointment_id', selectedBooking.id)
            }
          } catch (segDeleteErr) {
            console.warn('⚠️ Failed to delete old appointment segments during reschedule:', segDeleteErr)
          }
        }

        console.log('✅ Appointment rescheduled by cancel+create:', { oldId: selectedBooking.id, newId: newAppointmentId })
        
        // Send notification
        try {
          const servicesList = effectiveBooking.services?.map(s => s.name).join(', ') || 'your services'
          const professionalName = getBookingProfessionalDisplay(effectiveBooking) || 'your specialist'
          const timeSlot = newTime.slot || newTime || 'the selected time'
          await sendNotification(
            user.id,
            'Appointment Rescheduled',
            `Your appointment for ${servicesList} with ${professionalName} has been rescheduled to ${newDate} at ${timeSlot}.`,
            NOTIFICATION_TYPES.APPOINTMENT_RESCHEDULED,
            newAppointmentId || selectedBooking.id
          )
        } catch (notifError) {
          console.warn('⚠️ Failed to send reschedule notification:', notifError)
        }
        
        // Refresh appointments to show updated appointment
        await refreshAppointments()
        
        setShowRescheduleModal(false)
        setSelectedBooking(null)
        setRescheduleBookingOverride(null)
        setRescheduleProfessionalIds([])
        setRescheduleStep(1)
        setAlert({ 
          show: true, 
          type: 'success', 
          title: 'Success', 
          message: 'Appointment rescheduled successfully!',
          onConfirm: () => {
            setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
            // Refresh appointments again to ensure UI is updated
            refreshAppointments()
          },
          onCancel: null
        })
      } else {
        // Local reschedule behavior: cancel old and create new (tagged rescheduled)
        const timestamp = new Date().toISOString()
        const oldId = selectedBooking.id
        const newId = `rescheduled-${Date.now()}`

        const cancelledBookings = bookings.map(booking =>
          booking.id === oldId
            ? { ...booking, status: 'cancelled', updatedAt: timestamp }
            : booking
        )

        const newBooking = {
          ...(selectedBooking || {}),
          ...(rescheduleBookingOverride || {}),
          ...(rescheduleBookingData || {}),
          id: newId,
          date: newDate,
          time: newTime,
          status: 'confirmed',
          createdAt: timestamp,
          updatedAt: timestamp,
          notes: `[RESCHEDULED_FROM:${oldId}]`
        }

        const updatedBookings = [...cancelledBookings, newBooking]
        setBookings(updatedBookings)
        localStorage.setItem('bookings', JSON.stringify(updatedBookings))
        
        setShowRescheduleModal(false)
        setSelectedBooking(null)
        setRescheduleBookingOverride(null)
        setRescheduleProfessionalIds([])
        setRescheduleStep(1)
        setAlert({ 
          show: true, 
          type: 'success', 
          title: 'Success', 
          message: 'Appointment rescheduled successfully!',
          onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
          onCancel: null
        })
      }
    } catch (error) {
      console.error('❌ Error rescheduling appointment:', error)
      setAlert({
        show: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to reschedule appointment. Please try again.',
        onConfirm: () =>
          setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
        onCancel: null
      })
    }
  }

  // Handle change professional submit
  const handleChangeProfessionalSubmit = (updatedBooking, professionalIds) => {
    setRescheduleBookingOverride(updatedBooking)
    setRescheduleProfessionalIds(professionalIds)
    // Update reschedule booking data with new professional info
    setRescheduleBookingData(prev => ({
      ...prev,
      specialist: updatedBooking.specialist || prev.specialist,
      services: updatedBooking.services || prev.services
    }))
    setRescheduleStep(1) // Go to date/time step
  }

  // Handle view details - just for viewing
  const handleViewDetails = (booking) => {
    // This is handled by the expanded details section
  }

  // Handle remove booking (for local bookings only)
  const handleRemoveBooking = (bookingId) => {
    const updatedBookings = bookings.filter(b => b.id !== bookingId)
    setBookings(updatedBookings)
    localStorage.setItem('bookings', JSON.stringify(updatedBookings))
  }

  // Handle clear all cancelled
  const handleClearAllCancelled = () => {
    const cancelledIds = cancelledBookings.map(b => b.id)
    const updatedBookings = bookings.filter(b => !cancelledIds.includes(b.id))
    setBookings(updatedBookings)
    localStorage.setItem('bookings', JSON.stringify(updatedBookings))
  }

  // Handle book again - auto-select services and professionals
  const handleBookAgain = (booking) => {
    if (isOfferDealBooking(booking)) {
      setAlert({
        show: true,
        type: 'warning',
        title: 'Unavailable',
        message: 'Offer packages cannot be booked again from cancelled or completed bookings.',
        onConfirm: () => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }),
        onCancel: null,
      })
      return
    }

    // Extract services and professionals from the completed booking
    if (!booking || !booking.services || booking.services.length === 0) {
      alert('This appointment has no services to book again.')
      return
    }

    // Prepare services with their assigned professionals
    const servicesWithProfessionals = booking.services.map(service => {
      // Preserve professional info from the service
      const professional = service.professional || 
        (service.professional_id ? specialists.find(s => String(s.id) === String(service.professional_id)) : null)
      
      // Ensure service has all required fields
      return {
        id: service.id,
        name: service.name || 'Unknown Service',
        price: service.price || 0,
        duration: service.duration || '60 min',
        professional: professional ? {
          id: professional.id,
          name: professional.name,
          specialty: professional.role || professional.specialty
        } : null,
        professional_id: service.professional_id || (professional ? professional.id : null),
        category_id: service.category_id || null,
        category_name: service.category_name || null
      }
    })

    // Determine the top-level specialist
    // If all services have the same professional, use that; otherwise use "any"
    const professionalIds = [...new Set(
      servicesWithProfessionals
        .map(s => s.professional_id)
        .filter(Boolean)
    )]

    let selectedSpecialist = null
    if (professionalIds.length === 1) {
      selectedSpecialist = specialists.find(s => String(s.id) === String(professionalIds[0])) || 
        { id: professionalIds[0], name: 'Any Specialist' }
    } else if (professionalIds.length > 1) {
      selectedSpecialist = { id: 'any', name: 'Any Specialist' }
    } else {
      // Fallback to booking's specialist if available
      selectedSpecialist = booking.specialist || { id: 'any', name: 'Any Specialist' }
    }

    // Store payload so App can pre-fill booking on home page
    try {
      localStorage.setItem(
        'bookAgainPayload',
        JSON.stringify({
          services: servicesWithProfessionals,
          specialist: selectedSpecialist
        })
      )
    } catch (error) {
      console.error('Failed to store book-again payload', error)
    }

    // Trigger navigation to home / booking section
    if (typeof scrollToServices === 'function') {
      scrollToServices()
      // Navigate to home page to trigger the useEffect
      window.location.href = '/'
    } else {
      window.location.href = '/'
    }
  }

  const sidebarProps = {
    // Favorites props
    favoriteTab,
    setFavoriteTab,
    favoriteServices,
    favoriteSpecialists,
    favoriteServicesWithAssociations,
    favoriteProfessionalsWithAssociations,
    favoriteAppointments,
    categories,
    currency,
    toggleFavorite,
    onBookNow: scrollToServices,
    setSelectedFavoriteSpecialist,
    setShowFavoriteModal,
    setBookingData,
    setShowServiceBooking,
    setServiceBookingStep,
    // Remove favorite appointment handler
    onRemoveFavoriteAppointment: async (appointmentFavoriteId) => {
      if (!user?.id) return
      
      try {
        const { removeAppointmentFromFavorites } = await import('./services/favoritesService')
        const result = await removeAppointmentFromFavorites(user.id, appointmentFavoriteId)
        
        if (result.success) {
          // Remove from state
          setFavoriteAppointments(prev => prev.filter(fav => fav.id !== appointmentFavoriteId))
          
          // Send notification
          try {
            await sendNotification(
              user.id,
              'Removed from Favorites',
              'You removed an appointment from your favorites.',
              NOTIFICATION_TYPES.FAVORITE_ADDED,
              appointmentFavoriteId
            )
          } catch (notifError) {
            console.warn('⚠️ Failed to send favorite notification:', notifError)
          }
          
          setAlert({
            show: true,
            type: 'success',
            title: 'Success',
            message: 'Appointment removed from favorites.'
          })
        } else {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('Error removing favorite appointment:', error)
        setAlert({
          show: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to remove appointment from favorites. Please try again.'
        })
      }
    },
    // Bookings props
    bookingFilter,
    setBookingFilter,
    upcomingBookings,
    completedBookings,
    cancelledBookings,
    missedBookings,
    allBookings,
    appointments: appointments || [],
    refreshingAppointments,
    refreshAppointments,
    getFilteredBookings,
    calculateBookingTotal,
    calculateBookingDuration,
    formatDateForDisplay,
    formatTimeForDisplay,
    getPaymentStatus,
    isAppointmentExpired,
    isAppointmentAtLeastOneHourAway,
    canCancelAppointment,
    canRescheduleAppointment,
    getRescheduleBlockReason,
    getBookingProfessionalDisplay,
    getBookingProfessionalsForReview,
    getDealProfessionalsForReview,
    getDealReviewKey,
    parseServiceDuration,
    calculateServiceTimeRange,
    handleViewDetails,
    handleCancelAppointment,
    handleRemoveBooking,
    handleClearAllCancelled,
    handleReschedule,
    handleBookAgain,
    toggleBookingCollapse: (bookingId) => {
      setCollapsedBookings(prev => ({
        ...prev,
        [bookingId]: !prev[bookingId]
      }))
    },
    collapsedBookings,
    toggleReview: (bookingId, dealDays = null) => {
      setShowReviewForBooking(prev => {
        const opening = !prev[bookingId]
        if (opening) {
          let professionals = []
          if (String(bookingId).startsWith('deal:') && Array.isArray(dealDays)) {
            professionals = getDealProfessionalsForReview(dealDays)
          } else {
            const currentAllBookings = [
              ...(appointments || []).map(convertAppointmentToBooking),
              ...(bookings || []).filter(b => !appointments?.some(apt => apt.id === b.id)),
            ]
            const booking = currentAllBookings.find(b => b.id === bookingId)
            professionals = getBookingProfessionalsForReview(booking)
          }

          if (professionals.length > 0) {
            setActiveReviewTab(tabPrev => ({
              ...tabPrev,
              [bookingId]: tabPrev[bookingId] || professionals[0].id,
            }))
            setReviewData(dataPrev => {
              const existing = dataPrev[bookingId] || {}
              const nextForBooking = { ...existing }
              professionals.forEach((p) => {
                if (!nextForBooking[p.id]) {
                  nextForBooking[p.id] = { rating: 0, comment: '' }
                }
              })
              return { ...dataPrev, [bookingId]: nextForBooking }
            })
          }

          // Keep deal day professionals mapping for submit
          if (String(bookingId).startsWith('deal:') && Array.isArray(dealDays)) {
            setPackageReviewDays(prev => ({ ...prev, [bookingId]: dealDays }))
          }
        }
        return {
          ...prev,
          [bookingId]: opening,
        }
      })
    },
    activeReviewTab,
    setActiveReviewProfessional: (bookingId, professionalId) => {
      setActiveReviewTab(prev => ({
        ...prev,
        [bookingId]: professionalId,
      }))
    },
    handleReviewRating: (bookingId, rating, professionalId) => {
      if (!professionalId) return
      setReviewData(prev => ({
        ...prev,
        [bookingId]: {
          ...(prev[bookingId] || {}),
          [professionalId]: {
            ...(prev[bookingId]?.[professionalId] || { rating: 0, comment: '' }),
            rating,
          },
        },
      }))
    },
    handleReviewComment: (bookingId, comment, professionalId) => {
      if (!professionalId) return
      setReviewData(prev => ({
        ...prev,
        [bookingId]: {
          ...(prev[bookingId] || {}),
          [professionalId]: {
            ...(prev[bookingId]?.[professionalId] || { rating: 0, comment: '' }),
            comment,
          },
        },
      }))
    },
    submittingReviewBookingId,
    cancellingBookingId,
    // Notifications props
    notificationFeed,
    markAllNotificationsRead,
    unreadNotificationCount,
    onDeclineReassignment: handleDeclineReassignment,
    onKeepAppointment: handleKeepAppointment,
    onNotificationClick: handleNotificationClick,
    processingNotificationId,
    handleSubmitReview: async (bookingId) => {
      // Prevent multiple submissions
      if (submittingReviewBookingId === bookingId) {
        return
      }

      const isDealReview = String(bookingId).startsWith('deal:')
      const currentAllBookings = [
        ...(appointments || []).map(convertAppointmentToBooking),
        ...(bookings || []).filter(b => {
          return !appointments?.some(apt => apt.id === b.id)
        })
      ]

      let professionals = []
      let dayBookings = []

      if (isDealReview) {
        dayBookings = packageReviewDays[bookingId] || []
        if (dayBookings.length === 0) {
          const groupId = String(bookingId).replace(/^deal:/, '')
          dayBookings = currentAllBookings.filter(
            (b) => String(b.group_booking_id || '') === String(groupId)
          )
        }
        const fullGroup =
          dayBookings[0]
            ? findDealSiblingBookings(dayBookings[0], currentAllBookings, { includeCancelled: true })
            : dayBookings
        if (!areAllPackageVisitsCompleted(fullGroup)) {
          setAlert({
            show: true,
            type: 'warning',
            title: 'Reviews unavailable',
            message: 'You can leave a review after all package visits are completed.',
          })
          return
        }
        dayBookings = fullGroup
        professionals = getDealProfessionalsForReview(dayBookings)
      } else {
        const booking = currentAllBookings.find(b => b.id === bookingId)
        if (!booking) {
          setAlert({ show: true, type: 'error', title: 'Error', message: 'Appointment not found' })
          return
        }
        dayBookings = [booking]
        professionals = getBookingProfessionalsForReview(booking)
      }

      if (professionals.length === 0) {
        setAlert({ show: true, type: 'error', title: 'Error', message: 'Professional information not found' })
        return
      }

      const appointmentReviews = reviewData[bookingId] || {}
      const incomplete = professionals.filter((p) => {
        const entry = appointmentReviews[p.id]
        const hasRating = entry && entry.rating > 0
        const hasText = entry && String(entry.comment || '').trim().length > 0
        return !hasRating || !hasText
      })

      if (incomplete.length > 0) {
        setAlert({
          show: true,
          type: 'warning',
          title: 'Complete All Reviews',
          message:
            professionals.length > 1
              ? 'Please rate and write a short review for each professional before submitting.'
              : 'Please select a rating and write a short review.',
        })
        return
      }

      setSubmittingReviewBookingId(bookingId)

      try {
        const reviewerName = profile
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || user?.email?.split('@')[0] || 'Anonymous'
          : user?.user_metadata?.first_name && user?.user_metadata?.last_name
          ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
          : user?.email?.split('@')[0] || 'Anonymous'

        const reviewerAvatar = profile?.profile_image_url || user?.user_metadata?.avatar_url || user?.user_metadata?.picture || null

        const createdReviews = []
        for (const professional of professionals) {
          const entry = appointmentReviews[professional.id]
          const linkedDay =
            dayBookings.find((d) =>
              getBookingProfessionalsForReview(d).some((p) => String(p.id) === String(professional.id))
            ) || dayBookings[0]

          const appointmentId = linkedDay?.id
          if (!appointmentId) {
            setAlert({
              show: true,
              type: 'error',
              title: 'Error',
              message: `Could not link review for ${professional.name} to an appointment day.`,
            })
            return
          }

          const { hasReviewed } = await hasUserReviewedProfessionalForAppointment(
            user.id,
            appointmentId,
            professional.id
          )
          if (hasReviewed) {
            createdReviews.push({
              professional_id: professional.id,
              rating: entry.rating,
              comment: entry.comment || '',
              date: new Date().toISOString(),
              appointment_id: appointmentId,
            })
            continue
          }

          const reviewPayload = {
            user_id: user.id,
            professional_id: professional.id,
            appointment_id: appointmentId,
            rating: entry.rating,
            review_text: entry.comment || null,
            reviewer_name: reviewerName,
            reviewer_avatar: reviewerAvatar,
            is_verified: true,
            salon_id: linkedDay.salon_id || linkedDay.salonId || salonId || null,
          }

          const result = await createReview(reviewPayload)
          if (result.error) {
            console.error('❌ Error creating review:', result.error)
            setAlert({
              show: true,
              type: 'error',
              title: 'Error',
              message: result.error.message || `Failed to submit review for ${professional.name}. Please try again.`,
            })
            return
          }

          createdReviews.push({
            professional_id: professional.id,
            rating: entry.rating,
            comment: entry.comment || '',
            date: new Date().toISOString(),
            appointment_id: appointmentId,
          })

          try {
            await recomputeAndPersistProfessionalRating(professional.id)
          } catch (ratingError) {
            console.error('❌ Error recomputing professional rating:', ratingError)
          }
        }

        try {
          const names = professionals.map((p) => p.name).join(', ')
          await sendNotification(
            user.id,
            'Review Submitted',
            `Thank you for your review! Your feedback for ${names} has been submitted.`,
            NOTIFICATION_TYPES.PROFILE_UPDATED,
            dayBookings[0]?.id || bookingId
          )
        } catch (notifError) {
          console.warn('⚠️ Failed to send review notification:', notifError)
        }

        // Attach reviews onto affected day bookings/appointments
        const reviewsByAppointment = createdReviews.reduce((acc, review) => {
          const aid = review.appointment_id
          if (!acc[aid]) acc[aid] = []
          acc[aid].push(review)
          return acc
        }, {})

        const updatedBookings = bookings.map((b) => {
          const dayReviews = reviewsByAppointment[b.id]
          if (!dayReviews) return b
          return {
            ...b,
            reviews: [...(Array.isArray(b.reviews) ? b.reviews : []), ...dayReviews],
            review: {
              rating: dayReviews[0].rating,
              comment: dayReviews[0].comment,
              date: dayReviews[0].date,
            },
            updatedAt: new Date().toISOString(),
          }
        })
        setBookings(updatedBookings)
        localStorage.setItem('bookings', JSON.stringify(updatedBookings))

        if (appointments && setAppointments) {
          const updatedAppointments = appointments.map((apt) => {
            const dayReviews = reviewsByAppointment[apt.id]
            if (!dayReviews) return apt
            return {
              ...apt,
              reviews: [...(Array.isArray(apt.reviews) ? apt.reviews : []), ...dayReviews],
              review: {
                rating: dayReviews[0].rating,
                comment: dayReviews[0].comment,
                date: dayReviews[0].date,
              },
            }
          })
          setAppointments(updatedAppointments)
        }

        await refreshAppointments()

        setShowReviewForBooking(prev => ({
          ...prev,
          [bookingId]: false
        }))

        setReviewData(prev => {
          const next = { ...prev }
          delete next[bookingId]
          return next
        })
        setActiveReviewTab(prev => {
          const next = { ...prev }
          delete next[bookingId]
          return next
        })
        setPackageReviewDays(prev => {
          const next = { ...prev }
          delete next[bookingId]
          return next
        })

        setAlert({
          show: true,
          type: 'success',
          title: 'Success',
          message:
            professionals.length > 1
              ? `Your ${professionals.length} reviews have been submitted successfully!`
              : 'Review submitted successfully!',
        })
      } catch (error) {
        console.error('❌ Exception submitting review:', error)
        setAlert({
          show: true,
          type: 'error',
          title: 'Error',
          message: error.message || 'An unexpected error occurred. Please try again.'
        })
      } finally {
        setSubmittingReviewBookingId(null)
      }
    },
    showReviewForBooking,
    reviewData
  }

  return (
    <>
      <Navigation 
        user={user}
        appointments={allBookings}
        favorites={favorites}
        favoriteAppointments={favoriteAppointments}
        sidebarProps={sidebarProps}
         unreadNotificationCount={unreadNotificationCount}
        onAuthClick={() => {
          setAuthMode('login')
          setShowAuth(true)
        }}
        onAccountClick={() => {
          setAuthMode('login')
          setShowAuth(true)
        }}
      />
      
      {/* Reschedule Modal - Using BookingModal with rescheduleMode */}
      {showRescheduleModal && selectedBooking && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="text-white">Loading...</div></div>}>
          <BookingModal
            booking={rescheduleBookingData}
            setBooking={setRescheduleBookingData}
            step={rescheduleStep}
            setStep={setRescheduleStep}
            specialists={specialists}
            categories={categories}
            bookings={allBookings}
            onClose={() => {
              setShowRescheduleModal(false)
              setRescheduleStep(0)
              setRescheduleBookingOverride(null)
              setRescheduleProfessionalIds([])
            }}
            onConfirm={() => {}} // Not used in reschedule mode
            onReschedule={handleRescheduleSubmit}
            rescheduleMode={true}
            originalAppointment={selectedBooking}
          />
        </Suspense>
      )}
      
      {/* Alert Modal */}
      {alert.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{alert.title}</h3>
            <p className="text-gray-700 mb-6 whitespace-pre-line">{alert.message}</p>
            <div className="flex gap-3 justify-end">
              {alert.type === 'confirm' && (
                <button
                  onClick={alert.onCancel || (() => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }))}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={alert.onConfirm || (() => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null }))}
                className={`px-4 py-2 rounded-lg transition font-medium ${
                  alert.type === 'error' 
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : alert.type === 'warning'
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                    : alert.type === 'success'
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-primary text-white hover:bg-primary/90'
                }`}
              >
                {alert.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function App() {
  const { user, profile, loading: authLoading, isWebCustomer } = useAuth()
  const { selectedSalon, salonId, salonReady } = useSalon()

  // Dynamically update browser tab title based on selected salon branch name
  useEffect(() => {
    if (selectedSalon?.name) {
      document.title = `${selectedSalon.name} - Book Your Perfect Experience`
    } else {
      document.title = 'Timeless Trends Salon - Book Your Perfect Experience'
    }
  }, [selectedSalon])
  
  // OAuth is handled automatically by Supabase with detectSessionInUrl: true
  // AuthContext will detect the session via getSession() on app startup
  
  // Explicitly handle OAuth callback on mount
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const pathname = window.location.pathname
      const isResetPasswordRoute =
        pathname === '/reset-password' || pathname === '/resetpassword'
      const isAuthCallbackRoute = pathname === '/auth/callback'

      // Let ResetPassword and AuthCallback handle their own routes
      if (isResetPasswordRoute || isAuthCallbackRoute) return

      const hash = window.location.hash

      // Recovery links that land on the home page should go to the reset form
      if (hash.includes('type=recovery')) {
        window.location.replace(`/reset-password${window.location.search}${hash}`)
        return
      }

      // PKCE password-reset codes (not OAuth — OAuth uses /auth/callback)
      const code = new URL(window.location.href).searchParams.get('code')
      if (code) {
        window.location.replace(`/reset-password${window.location.search}${hash}`)
        return
      }

      // Check if URL contains OAuth tokens
      const isOAuthCallback = hash && (
        hash.includes('access_token=') || 
        hash.includes('error=')
      )
      
      if (isOAuthCallback) {
        console.log('🔐 OAuth callback detected in URL, processing...')
        
        // Give Supabase a moment to process the hash
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Check if session was created
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.user) {
          console.log('✅ OAuth session successfully created:', session.user.email)
          
          // Clean up the hash from URL after processing
          window.history.replaceState(null, '', window.location.pathname)
        } else {
          console.warn('⚠️ OAuth callback detected but no session found')
        }
      }
    }
    
    handleOAuthCallback()
  }, []) // Only run on mount
  
  // Debug: Log auth state changes
  useEffect(() => {
    console.log('🔍 App.jsx - Auth state changed:', {
      authLoading,
      hasUser: !!user,
      userEmail: user?.email || 'none',
      isWebCustomer
    })
  }, [authLoading, user, isWebCustomer])
  const [showAuth, setShowAuth] = useState(false)
  const [authMode, setAuthMode] = useState('login')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [authSuccessMessage, setAuthSuccessMessage] = useState('')

  useEffect(() => {
    if (sessionStorage.getItem('open_login_after_reset') === 'true') {
      sessionStorage.removeItem('open_login_after_reset')
      const message = sessionStorage.getItem('auth_success_message')
      if (message) {
        sessionStorage.removeItem('auth_success_message')
        setAuthSuccessMessage(message)
        setTimeout(() => setAuthSuccessMessage(''), 6000)
      }
      setAuthMode('login')
      setShowAuth(true)
    }
  }, [])

  // Open login modal in-place (e.g. Book on deals while signed out) — stay on current page
  useEffect(() => {
    const onRequireLogin = () => {
      setAuthMode('login')
      setShowAuth(true)
      setAuthSuccessMessage('')
    }
    window.addEventListener('requireLogin', onRequireLogin)
    return () => window.removeEventListener('requireLogin', onRequireLogin)
  }, [])
  const [showTeamBooking, setShowTeamBooking] = useState(false)
  const [showServiceBooking, setShowServiceBooking] = useState(false)
  const [showCategoryPage, setShowCategoryPage] = useState(false)
  const [showSpecialistProfile, setShowSpecialistProfile] = useState(false)
  const [alert, setAlert] = useState({ show: false, type: 'info', title: '', message: '' })
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedSpecialist, setSelectedSpecialist] = useState(null)
  const [favorites, setFavorites] = useState({ specialists: [], services: [] })
  // Favorite associations state
  const [favoriteServicesWithAssociations, setFavoriteServicesWithAssociations] = useState([])
  const [favoriteProfessionalsWithAssociations, setFavoriteProfessionalsWithAssociations] = useState([])
  const [favoriteAppointments, setFavoriteAppointments] = useState([])
  // Bumps to ignore stale association fetches after unfavorite
  const favoritesAssocGenRef = useRef(0)
  // Favorite modals
  const [showFavoriteServiceModal, setShowFavoriteServiceModal] = useState(false)
  const [showFavoriteProfessionalModal, setShowFavoriteProfessionalModal] = useState(false)
  const [pendingFavoriteService, setPendingFavoriteService] = useState(null)
  const [pendingFavoriteProfessional, setPendingFavoriteProfessional] = useState(null)
  const [bookings, setBookings] = useState([]) // Local bookings (for conflict checking)
  const [appointments, setAppointments] = useState([]) // Database appointments
  const allBookingsForConflicts = React.useMemo(() => {
    const merged = [
      ...(appointments || []).map((apt) => convertAppointmentToBooking(apt)).filter(Boolean),
      ...(bookings || []).filter((b) => !appointments?.some((apt) => String(apt.id) === String(b.id)))
    ]
    if (!salonId) return merged
    return merged.filter((b) => (b.salon_id || b.salonId) === salonId)
  }, [appointments, bookings, salonId])
  const [bookingData, setBookingData] = useState({
    specialist: null,
    services: [],
    date: '',
    time: null,
    paymentMethod: 'card',
    discountCode: '',
    discount: 0,
    deal: null,
    schedulingMode: 'continuous',
    serviceTimeSelections: {},
  })
  /** Package reschedule: cancel this group after new multi-day booking succeeds */
  const [rescheduleGroupBookingId, setRescheduleGroupBookingId] = useState(null)
  const [pendingPackageReschedule, setPendingPackageReschedule] = useState(null)
  const pendingPackageRescheduleRef = useRef(null)
  useEffect(() => {
    pendingPackageRescheduleRef.current = pendingPackageReschedule
  }, [pendingPackageReschedule])
  // Multi-category booking structure: [{category: {id, name}, services: [{service, professional}], ...}]
  const [multiCategoryBooking, setMultiCategoryBooking] = useState([])
  const [showMultiCategoryBooking, setShowMultiCategoryBooking] = useState(false)
  const [teamBookingStep, setTeamBookingStep] = useState(1)
  const [serviceBookingStep, setServiceBookingStep] = useState(1)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentMode, setPaymentMode] = useState('booking') // 'booking' | 'reschedule_fee'
  const [rescheduleFeeContext, setRescheduleFeeContext] = useState(null)
  const [rescheduleAfterFeeBooking, setRescheduleAfterFeeBooking] = useState(null)
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null)
  const [taxRate, setTaxRate] = useState(0) // decimal, e.g. 0.1 = 10%
  const [cancellationPolicy, setCancellationPolicy] = useState(null)
  const [reschedulePolicy, setReschedulePolicy] = useState(null)
  const pendingBookingRestoredRef = useRef(false)

  // Keep booking draft in localStorage while a booking modal is open so Google/Facebook
  // OAuth (full page redirect) can restore services/professionals after login.
  useEffect(() => {
    if (!showServiceBooking && !showTeamBooking) return
    if (!bookingData?.services?.length) return
    savePendingBookingDraft({
      bookingData,
      serviceBookingStep,
      teamBookingStep,
      showServiceBooking,
      showTeamBooking,
    })
  }, [
    bookingData,
    serviceBookingStep,
    teamBookingStep,
    showServiceBooking,
    showTeamBooking,
  ])

  // Final flush right before OAuth redirect leaves the page
  useEffect(() => {
    const flush = () => {
      if (!showServiceBooking && !showTeamBooking) return
      if (!bookingData?.services?.length) return
      savePendingBookingDraft({
        bookingData,
        serviceBookingStep,
        teamBookingStep,
        showServiceBooking,
        showTeamBooking,
      })
    }
    window.addEventListener('flush-pending-booking-draft', flush)
    return () => window.removeEventListener('flush-pending-booking-draft', flush)
  }, [
    bookingData,
    serviceBookingStep,
    teamBookingStep,
    showServiceBooking,
    showTeamBooking,
  ])
  
  // Helper functions for cancellation policy (must be after cancellationPolicy state declaration)
  const canCancelAppointment = (booking) => {
    if (!booking) return false
    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return false
    }
    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)
    
    // If we have an admin-defined cancellation policy, use that
    if (cancellationPolicy) {
      const cutoffEnabled =
        cancellationPolicy.cutoffPolicyEnabled &&
        typeof cancellationPolicy.cutoffHoursBefore === 'number'

      // Inside cutoff window -> cannot cancel at all
      if (cutoffEnabled && diffMinutes <= cancellationPolicy.cutoffHoursBefore * 60) {
        return false
      }

      // Otherwise allow cancel any time > 0 minutes before start
      return diffMinutes > 0
    }

    // Allow cancel if appointment is in the future
    return diffMinutes > 0
  }

  const getRescheduleCountFromNotes = (booking) => {
    const notes = booking?.notes || ''
    const match = notes.match(/\[RESCHEDULE_COUNT:(\d+)\]/)
    if (match && match[1]) {
      const n = parseInt(match[1], 10)
      return Number.isNaN(n) ? 0 : n
    }
    return 0
  }

  // getRescheduleBlockReason is now defined above inside NavigationWrapper scope

  const canRescheduleAppointment = (booking) => {
    const reason = getRescheduleBlockReason(booking)
    return reason === 'none'
  }

  const getCancellationFeeInfo = (booking) => {
    if (!booking || !cancellationPolicy) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)
    const diffHours = diffMinutes / 60

    // Fee window logic: if within last X hours before appointment and fee policy enabled
    const feeEnabled =
      cancellationPolicy.feePolicyEnabled &&
      typeof cancellationPolicy.feeWindowHours === 'number' &&
      typeof cancellationPolicy.feePercentage === 'number'

    if (!feeEnabled || diffHours <= 0) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    // If outside fee window (too early), no fee
    if (diffHours > cancellationPolicy.feeWindowHours) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    // Calculate total amount from booking
    const totalUSD = calculateBookingTotal(booking)
    const feePercentage = cancellationPolicy.feePercentage
    const feeAmountUSD = totalUSD * (feePercentage / 100)

    return {
      appliesFee: feeAmountUSD > 0,
      feePercentage,
      feeAmountUSD,
    }
  }

  const getRescheduleFeeInfo = (booking) => {
    if (!booking || !reschedulePolicy) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    const appointmentDateTime = parseAppointmentDateTime(booking)
    if (!appointmentDateTime || Number.isNaN(appointmentDateTime.getTime())) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    const now = new Date()
    const diffMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60)
    const diffHours = diffMinutes / 60

    const feeEnabled =
      reschedulePolicy.feePolicyEnabled &&
      typeof reschedulePolicy.feeWindowHours === 'number' &&
      typeof reschedulePolicy.feePercentage === 'number'

    if (!feeEnabled || diffHours <= 0) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    if (diffHours > reschedulePolicy.feeWindowHours) {
      return { appliesFee: false, feePercentage: 0, feeAmountUSD: 0 }
    }

    const totalUSD = calculateBookingTotal(booking)
    const feePercentage = reschedulePolicy.feePercentage
    const feeAmountUSD = totalUSD * (feePercentage / 100)

    return {
      appliesFee: feeAmountUSD > 0,
      feePercentage,
      feeAmountUSD,
    }
  }
  
  // Real data from Supabase
  const [categories, setCategories] = useState([])
  const [services, setServices] = useState([])
  const [specialists, setSpecialists] = useState([])
  const [salon, setSalon] = useState(null)
  const [reviews, setReviews] = useState([])
  const [dataLoading, setDataLoading] = useState(true)

  // After OAuth redirect, restore the in-progress booking once catalog data is ready.
  // Skip if a booking modal is already open (email/password login keeps React state).
  useEffect(() => {
    if (pendingBookingRestoredRef.current) return
    if (authLoading) return
    if (!user && !isDemoMode()) return
    if (!categories || categories.length === 0) return

    if (showServiceBooking || showTeamBooking) {
      clearPendingBookingDraft()
      return
    }

    const draft = loadPendingBookingDraft()
    if (!draft?.bookingData?.services?.length) return

    pendingBookingRestoredRef.current = true

    const restoredServices = (draft.bookingData.services || []).map((service) => {
      const professional = service.professional || service.selectedProfessional || null
      return {
        ...service,
        professional,
        selectedProfessional: service.selectedProfessional || professional,
        professional_id:
          service.professional_id ||
          (professional && professional.id !== 'any' ? professional.id : null),
      }
    })

    setBookingData((prev) => ({
      ...prev,
      ...draft.bookingData,
      services: restoredServices,
      daySelections: draft.bookingData.daySelections || prev.daySelections || [],
    }))
    if (draft.showTeamBooking) {
      setShowTeamBooking(true)
      setShowServiceBooking(false)
      setTeamBookingStep(draft.teamBookingStep || 2)
    } else {
      setShowServiceBooking(true)
      setShowTeamBooking(false)
      setServiceBookingStep(draft.serviceBookingStep || 2)
    }

    // Restore Services-page selection checks (professional borders / Selected buttons)
    try {
      const summary =
        (Array.isArray(draft.servicesBookingSummary) && draft.servicesBookingSummary.length > 0
          ? draft.servicesBookingSummary
          : restoredServices.map((service) => ({
              service: {
                id: service.id,
                name: service.name,
                price: service.price,
                duration: service.duration,
                description: service.description,
                category_id: service.category_id,
                category: service.category || null,
              },
              professional: service.professional || service.selectedProfessional || null,
              category: service.category
                ? { id: service.category.id, name: service.category.name }
                : null,
            })).filter((item) => item.service?.id && item.professional?.id))

      if (summary.length > 0) {
        localStorage.setItem('servicesBookingSummary', JSON.stringify(summary))
        window.dispatchEvent(new CustomEvent('restoreServicesBookingSummary', { detail: summary }))
      }
    } catch (e) {
      console.warn('Failed to restore services booking summary UI', e)
    }

    clearPendingBookingDraft()
    setAuthSuccessMessage('Welcome back! Your booking selections were restored.')
    setTimeout(() => setAuthSuccessMessage(''), 4000)
  }, [authLoading, user, categories, showServiceBooking, showTeamBooking])
  
  // Load favorite associations when favorites change (must be after services, categories, specialists are declared)
  useEffect(() => {
    const gen = ++favoritesAssocGenRef.current
    const loadFavoriteAssociations = async () => {
      if (!user?.id && !isDemoMode()) return
      const favUserId = user?.id || DEMO_USER_ID
      
      try {
        const {
          getFavoriteServicesDetailed,
          getFavoriteProfessionalsDetailed,
          getFavoriteAppointments,
          enrichFavoriteAppointmentsWithActiveStatus,
        } = await import('./services/favoritesService')
        const [servicesResult, professionalsResult, appointmentsResult] = await Promise.all([
          getFavoriteServicesDetailed(favUserId),
          getFavoriteProfessionalsDetailed(favUserId),
          getFavoriteAppointments(favUserId)
        ])

        if (gen !== favoritesAssocGenRef.current) return
        
        setFavoriteServicesWithAssociations(servicesResult.data || [])
        setFavoriteProfessionalsWithAssociations(professionalsResult.data || [])

        if (appointmentsResult.data) {
          const enriched = await enrichFavoriteAppointmentsWithActiveStatus(
            appointmentsResult.data || []
          )
          if (gen !== favoritesAssocGenRef.current) return
          setFavoriteAppointments(enriched)
        } else {
          setFavoriteAppointments([])
        }
      } catch (error) {
        console.error('Error loading favorite associations:', error)
      }
    }
    
    loadFavoriteAssociations()
  }, [user?.id, favorites?.services, favorites?.specialists, services, categories, specialists, salonId])

  // Handle "Book Again" payload stored in localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem('bookAgainPayload')
      if (!raw) return

      const payload = JSON.parse(raw)
      if (!payload || !Array.isArray(payload.services) || payload.services.length === 0) {
        localStorage.removeItem('bookAgainPayload')
        return
      }

      setBookingData(prev => ({
        ...prev,
        services: payload.services,
        specialist: payload.specialist || null,
        date: '',
        time: null,
        schedulingMode: 'continuous',
        serviceTimeSelections: {},
      }))

      // Open service booking directly at Date & Time step
      setShowServiceBooking(true)
      setServiceBookingStep(2)

      // Clear payload so it doesn't re-trigger
      localStorage.removeItem('bookAgainPayload')
    } catch (error) {
      console.error('Failed to apply book-again payload', error)
      localStorage.removeItem('bookAgainPayload')
    }
  }, [])

  // Offer booking is opened from HomePage via location.state.bookOffer
  // (App sits outside the Router, so it does not re-render on SPA navigation).

  // Function to refresh professionals only (for after rating updates)
  const refreshProfessionals = React.useCallback(async () => {
    try {
      console.log('🔄 Refreshing professionals...')
      clearProfessionalsCache()
      const professionalsResult = await getProfessionals()
      
      if (professionalsResult.error) {
        console.error('❌ Error refreshing professionals:', professionalsResult.error)
      } else {
        // Log raw data from database
        console.log('📋 Raw professionals data from DB:', professionalsResult.data?.map(p => ({
          id: p.id,
          name: p.name,
          rating: p.rating,
          ratingType: typeof p.rating
        })))
        
        const transformedProfessionals = transformProfessionals(professionalsResult.data || [])
        
        // Log transformed data
        console.log('📋 Transformed professionals:', transformedProfessionals.map(p => ({
          id: p.id,
          name: p.name,
          rating: p.rating
        })))
        
        setSpecialists(transformedProfessionals)
        console.log('✅ Refreshed', transformedProfessionals.length, 'professionals')
      }
    } catch (error) {
      console.error('❌ Error in refreshProfessionals:', error)
    }
  }, [])

  // Shared function to load all data
  const loadAllData = React.useCallback(async ({ showOverlay = false } = {}) => {
      try {
        if (showOverlay) {
          setDataLoading(true)
        }
      console.log('📦 Loading all data from Supabase...')
        
        // Fetch all data in parallel
        const [categoriesResult, servicesResult, professionalsResult, salonResult, reviewsResult] = await Promise.all([
          getCategories(),
          getServices(),
          getProfessionals(),
          getSalon(),
          getReviews(6, 0) // Load first 6 reviews
        ])

        if (categoriesResult.error) {
        console.error('❌ Error loading categories:', categoriesResult.error)
        } else if (servicesResult.error) {
        console.error('❌ Error loading services:', servicesResult.error)
        } else {
          // Transform data to match component format
          const transformedCategories = transformCategoriesWithServices(
            categoriesResult.data || [],
            servicesResult.data || []
          )
          setCategories(transformedCategories)
        setServices(servicesResult.data || [])
        console.log('✅ Loaded', transformedCategories.length, 'categories and', servicesResult.data?.length || 0, 'services')
        }
        
        if (professionalsResult.error) {
        console.error('❌ Error loading professionals:', professionalsResult.error)
        } else {
          const transformedProfessionals = transformProfessionals(professionalsResult.data || [])
          setSpecialists(transformedProfessionals)
        console.log('✅ Loaded', transformedProfessionals.length, 'professionals')
        }
        
        if (salonResult.error) {
        console.error('❌ Error loading salon:', salonResult.error)
      } else if (!salonResult.data) {
        console.warn('⚠️ No salon data returned from database')
        } else {
          const transformedSalon = transformSalon(salonResult.data)
        if (transformedSalon) {
          setSalon(transformedSalon)
          console.log('✅ Loaded salon:', transformedSalon.name)
          // Log amenities for debugging
          if (transformedSalon.additionalInfo?.amenities) {
            console.log('📋 Salon amenities:', transformedSalon.additionalInfo.amenities)
          }
        }
        }
        
        if (reviewsResult.error) {
        console.error('❌ Error loading reviews:', reviewsResult.error)
        } else {
          const transformedReviews = transformReviews(reviewsResult.data || [])
          setReviews(transformedReviews)
        console.log('✅ Loaded', transformedReviews.length, 'reviews')
        }
      } catch (error) {
      console.error('❌ Exception while loading data:', error)
      } finally {
        setDataLoading(false)
      }
  }, [salonId])

  // Track if data has been loaded to prevent multiple loads
  const dataLoadedRef = useRef(false)
  const loadTimeoutRef = useRef(null)
  const lastUserEmailRef = useRef(null)

  // Load data initially (after auth loading completes) or when user logs in
  useEffect(() => {
    const currentUserEmail = user?.email || null
    
    console.log('🔍 Data load effect triggered:', {
      authLoading,
      user: currentUserEmail || 'none',
      isWebCustomer,
      dataLoaded: dataLoadedRef.current,
      lastUserEmail: lastUserEmailRef.current
    })

    // Clear any pending timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current)
      loadTimeoutRef.current = null
    }

    if (!salonReady) return

    // Strategy 1: If auth is done loading, load catalog once (or again for a different user)
    if (!authLoading ) {
      if (!dataLoadedRef.current || lastUserEmailRef.current !== currentUserEmail) {
        console.log('🔐 Auth complete + User logged in - Loading data immediately...')
        const isFirstLoad = !dataLoadedRef.current
        dataLoadedRef.current = true
        lastUserEmailRef.current = currentUserEmail
        loadAllData({ showOverlay: isFirstLoad })
        return
      }
    }

    // Strategy 2: If auth is done but no user yet, load public data (only once)
    if (!authLoading && !user && !dataLoadedRef.current) {
      console.log('🔐 Auth complete but no user - Loading public data...')
      dataLoadedRef.current = true
      lastUserEmailRef.current = null
      loadAllData({ showOverlay: true })
      return
    }

    // Strategy 3: Fallback - if authLoading is stuck, load after timeout
    if (authLoading && !dataLoadedRef.current) {
      console.warn('⏱️ Auth is still loading; setting fallback timer to load data anyway...')
      loadTimeoutRef.current = setTimeout(() => {
        if (dataLoadedRef.current) return
        console.warn('⏱️ Fallback fired: forcing data load even though authLoading is still true', {
          user: currentUserEmail || 'none',
          isWebCustomer
        })
        dataLoadedRef.current = true
        lastUserEmailRef.current = currentUserEmail
        loadAllData({ showOverlay: true })
      }, 2500)
    }

  }, [authLoading, user?.id, user?.email, isWebCustomer, loadAllData, salonReady, salonId])

  const lastLoadedSalonIdRef = useRef(null)
  useEffect(() => {
    if (!salonReady || !salonId) return
    if (lastLoadedSalonIdRef.current === salonId) return
    const hadSalon = lastLoadedSalonIdRef.current != null
    lastLoadedSalonIdRef.current = salonId
    if (hadSalon) {
      console.log('📍 Salon location changed - reloading catalog')
      setBookings([])
      setAppointments([])
      loadAllData({ showOverlay: true })
    }
  }, [salonReady, salonId, loadAllData])

  // Refresh salon data when page becomes visible (to reflect admin changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh salon data when page becomes visible
        // This ensures icon changes from admin are reflected
        getSalon().then(salonResult => {
          if (!salonResult.error && salonResult.data) {
            const transformedSalon = transformSalon(salonResult.data)
            if (transformedSalon) {
              setSalon(transformedSalon)
              console.log('🔄 Refreshed salon data on visibility change')
            }
          }
        }).catch(err => {
          console.error('Error refreshing salon on visibility change:', err)
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current)
      }
    }
  }, [])

  // Only reset data loaded flag on actual logout (not temporary state changes)
  useEffect(() => {
    const currentUserEmail = user?.email || null
    
    // If user was logged in and now they're not (real logout)
    if (lastUserEmailRef.current !== null && currentUserEmail === null) {
      console.log('👋 User logged out - resetting data loaded flag')
      dataLoadedRef.current = false
      lastUserEmailRef.current = null
    }
  }, [user?.email])


  // Load favorites from database when user logs in
  useEffect(() => {
    const loadFavorites = async () => {
      if (user?.id && isWebCustomer && !authLoading) {
        try {
          const { getAllFavorites } = await import('./services/favoritesService')
          const result = await getAllFavorites(user.id)
          
          if (!result.error) {
            // Normalize DB results into ID arrays for local state.
            // Associations (service↔professional / professional↔services) are handled separately
            // via `favoriteServicesWithAssociations` / `favoriteProfessionalsWithAssociations`.
            const serviceIds = (result.services || [])
              .map((s) => s?.service_id)
              .filter(Boolean)

            const professionalIds = (result.professionals || [])
              .map((p) => p?.professional_id)
              .filter(Boolean)

            setFavorites({
              specialists: professionalIds,
              services: serviceIds
            })
            console.log('✅ Loaded favorites from database:', {
              services: serviceIds.length,
              professionals: professionalIds.length
            })
          } else {
            console.error('❌ Error loading favorites:', result.error)
            // Favorites are stored in database only - no localStorage fallback
            setFavorites({ specialists: [], services: [] })
          }
        } catch (error) {
          console.error('❌ Exception loading favorites:', error)
          // Favorites are stored in database only - no localStorage fallback
          setFavorites({ specialists: [], services: [] })
        }
      } else if (!user) {
        // When not logged in, no favorites to load (all stored in database)
        setFavorites({ specialists: [], services: [] })
      }
    }

    loadFavorites()
  }, [user?.id, isWebCustomer, authLoading, salonId])

  // Appointments initial load + cross-device sync live in NavigationWrapper (refreshAppointments + realtime)
  // to avoid duplicate fetches and list flicker when [user] reference changes on token refresh.

  // Load tax policy (admin-controlled) once on app load
  useEffect(() => {
    const loadTaxPolicy = async () => {
      try {
        const policy = await getTaxPolicy()
        if (policy?.enabled && typeof policy.percentage === 'number') {
          setTaxRate(policy.percentage / 100)
        } else {
          setTaxRate(0)
        }
      } catch (error) {
        console.error('Error loading tax policy:', error)
        setTaxRate(0)
      }
    }

    loadTaxPolicy()
  }, [salonId])

  // Load cancellation and reschedule policy (admin-controlled) once on app load
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const [cancelPolicy, reschedPolicy] = await Promise.all([
          getCancellationPolicy(),
          getReschedulePolicy()
        ])
        setCancellationPolicy(cancelPolicy)
        setReschedulePolicy(reschedPolicy)
      } catch (error) {
        console.error('Error loading cancellation / reschedule policy:', error)
        setCancellationPolicy(null)
        setReschedulePolicy(null)
      }
    }

    loadPolicies()
  }, [salonId])

  // Note: Favorites are now stored in database only, not localStorage
  // Bookings are also stored in database (appointments table)

  // Handle auth success (login or register)
  const handleAuth = (result) => {
    if (result.success) {
      setShowAuth(false)
      if (result.message) {
        setAuthSuccessMessage(result.message)
      } else if (authMode === 'login') {
        setAuthSuccessMessage('Welcome back! You can now complete your booking.')
      } else {
        setAuthSuccessMessage('Account created successfully! You can now book appointments.')
      }
      setTimeout(() => setAuthSuccessMessage(''), 3000)
      
      // Do NOT clear bookingData on login. This allows guest users who started a booking
      // to keep their selected services and specialist after signing in.

      // Resume package booking after login without leaving the deals page.
      try {
        const raw = sessionStorage.getItem('pendingBookOffer')
        if (raw) {
          const returnPath = sessionStorage.getItem('dealBookingReturnPath')
          const returnPathname = returnPath ? returnPath.split('?')[0] : null
          if (returnPathname && returnPathname.startsWith('/offers') && window.location.pathname !== returnPathname) {
            window.dispatchEvent(new CustomEvent('ensureDealBookingPath'))
            return
          }
          const offer = JSON.parse(raw)
          if (offer) {
            if (categories?.length) {
              sessionStorage.removeItem('pendingBookOffer')
              setTimeout(() => handleHeroOfferRef.current?.(offer), 50)
            }
            // If catalog is still loading, pendingBookOffer is picked up by the categories effect.
          }
        }
      } catch (_) {
        sessionStorage.removeItem('pendingBookOffer')
      }
    }
  }

  const toggleFavorite = async (type, id) => {
    if (!user?.id && !isDemoMode()) {
      setAuthMode('login')
      setShowAuth(true)
      setAuthSuccessMessage('')
      return
    }

    const favUserId = user?.id || DEMO_USER_ID
    const idStr = String(id)

    const prevList = Array.isArray(favorites?.[type]) ? favorites[type] : []
    const isFavorite = prevList.some((favId) => String(favId) === idStr)
    const prevServicesAssociations = favoriteServicesWithAssociations
    const prevProfessionalsAssociations = favoriteProfessionalsWithAssociations

    // If removing favorite, do it directly
    if (isFavorite) {
      // Invalidate in-flight association reloads so they can't put the item back
      favoritesAssocGenRef.current += 1

      // Optimistically update UI (favorites list + Favorites tab associations)
      setFavorites(prev => ({
        ...prev,
        [type]: (prev[type] || []).filter((favId) => String(favId) !== idStr)
      }))
      if (type === 'services') {
        setFavoriteServicesWithAssociations(prev =>
          (prev || []).filter((item) => String(item.id) !== idStr)
        )
      } else if (type === 'specialists') {
        setFavoriteProfessionalsWithAssociations(prev =>
          (prev || []).filter((item) => String(item.id) !== idStr)
        )
      }

      // Update database (or demo store)
      try {
        const {
          removeServiceFromFavorites,
          removeProfessionalFromFavorites
        } = await import('./services/favoritesService')

        let result
        if (type === 'services') {
          result = await removeServiceFromFavorites(id, favUserId)
        } else if (type === 'specialists') {
          result = await removeProfessionalFromFavorites(id, favUserId)
        }

        if (!result.success) {
          // Revert optimistic update on error
          favoritesAssocGenRef.current += 1
          setFavorites(prev => ({
            ...prev,
            [type]: prevList
          }))
          if (type === 'services') {
            setFavoriteServicesWithAssociations(prevServicesAssociations)
          } else if (type === 'specialists') {
            setFavoriteProfessionalsWithAssociations(prevProfessionalsAssociations)
          }
          console.error('Failed to remove favorite:', result.error)
        } else {
          // Send notification
          try {
            let itemName = ''
            if (type === 'services') {
              const service = services.find(s => String(s.id) === idStr) || 
                             categories.flatMap(cat => cat.services || []).find(s => String(s.id) === idStr)
              itemName = service?.name || 'service'
            } else if (type === 'specialists') {
              const specialist = specialists.find(s => String(s.id) === idStr)
              itemName = specialist?.name || 'professional'
            }

            if (user?.id) {
            await sendNotification(
              user.id,
              'Removed from Favorites',
              `You removed ${itemName} from your favorites.`,
              NOTIFICATION_TYPES.FAVORITE_ADDED,
              id
            )
            }
          } catch (notifError) {
            console.warn('⚠️ Failed to send favorite notification:', notifError)
          }
        }
      } catch (error) {
        // Revert optimistic update on error
        favoritesAssocGenRef.current += 1
        setFavorites(prev => ({
          ...prev,
          [type]: prevList
        }))
        if (type === 'services') {
          setFavoriteServicesWithAssociations(prevServicesAssociations)
        } else if (type === 'specialists') {
          setFavoriteProfessionalsWithAssociations(prevProfessionalsAssociations)
        }
        console.error('Exception removing favorite:', error)
      }
    } else {
      // If adding favorite, show modal to select associations
      if (type === 'services') {
        const service = services.find(s => String(s.id) === idStr) || 
                       categories.flatMap(cat => cat.services || []).find(s => String(s.id) === idStr)
        if (service) {
          setPendingFavoriteService(service)
          setShowFavoriteServiceModal(true)
        }
      } else if (type === 'specialists') {
        const specialist = specialists.find(s => String(s.id) === idStr)
        if (specialist) {
          setPendingFavoriteProfessional(specialist)
          setShowFavoriteProfessionalModal(true)
        }
      }
    }
  }

  // Handle service favorite confirmation (with professional)
  const handleFavoriteServiceConfirm = async (professional) => {
    if (!pendingFavoriteService || (!user?.id && !isDemoMode())) return

    const favUserId = user?.id || DEMO_USER_ID

    try {
      const { addServiceToFavorites } = await import('./services/favoritesService')
      const result = await addServiceToFavorites(pendingFavoriteService.id, favUserId, professional.id)

      if (result.success) {
        // Update favorites state
        setFavorites(prev => ({
          ...prev,
          services: [...(prev.services || []), pendingFavoriteService.id]
        }))

        // Refresh favorite associations to show the new favorite with its professional
        try {
          const { getFavoriteServicesDetailed } = await import('./services/favoritesService')
          const servicesResult = await getFavoriteServicesDetailed(favUserId)
          setFavoriteServicesWithAssociations(servicesResult.data || [])
        } catch (refreshError) {
          console.error('❌ Error refreshing favorite associations:', refreshError)
        }

        // Send notification
        try {
          if (user?.id) {
          await sendNotification(
            user.id,
            'Added to Favorites',
            `You added ${pendingFavoriteService.name} with ${professional.name} to your favorites.`,
            NOTIFICATION_TYPES.FAVORITE_ADDED,
            pendingFavoriteService.id
          )
          }
        } catch (notifError) {
          console.warn('⚠️ Failed to send favorite notification:', notifError)
        }
      } else {
        console.error('Failed to add service to favorites:', result.error)
        setAlert({
          show: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to add service to favorites. Please try again.'
        })
      }
    } catch (error) {
      console.error('Exception adding service to favorites:', error)
      setAlert({
        show: true,
        type: 'error',
        title: 'Error',
        message: 'An error occurred. Please try again.'
      })
    } finally {
      setPendingFavoriteService(null)
      setShowFavoriteServiceModal(false)
    }
  }

  // Handle professional favorite confirmation (with services)
  const handleFavoriteProfessionalConfirm = async (selectedServices) => {
    if (!pendingFavoriteProfessional || (!user?.id && !isDemoMode()) || !selectedServices || selectedServices.length === 0) return

    const favUserId = user?.id || DEMO_USER_ID

    try {
      const serviceIds = selectedServices.map(s => s.id)
      const { addProfessionalToFavorites } = await import('./services/favoritesService')
      const result = await addProfessionalToFavorites(pendingFavoriteProfessional.id, favUserId, serviceIds)

      if (result.success) {
        // Update favorites state
        setFavorites(prev => ({
          ...prev,
          specialists: [...(prev.specialists || []), pendingFavoriteProfessional.id]
        }))

        // Refresh favorite associations to show the new favorite with its services
        try {
          const { getFavoriteProfessionalsDetailed } = await import('./services/favoritesService')
          const professionalsResult = await getFavoriteProfessionalsDetailed(favUserId)
          setFavoriteProfessionalsWithAssociations(professionalsResult.data || [])
        } catch (refreshError) {
          console.error('❌ Error refreshing favorite associations:', refreshError)
        }

        // Send notification
        try {
          await sendNotification(
            user.id,
            'Added to Favorites',
            `You added ${pendingFavoriteProfessional.name} with ${selectedServices.length} service${selectedServices.length !== 1 ? 's' : ''} to your favorites.`,
            NOTIFICATION_TYPES.FAVORITE_ADDED,
            pendingFavoriteProfessional.id
          )
        } catch (notifError) {
          console.warn('⚠️ Failed to send favorite notification:', notifError)
        }
      } else {
        console.error('Failed to add professional to favorites:', result.error)
        setAlert({
          show: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to add professional to favorites. Please try again.'
        })
      }
    } catch (error) {
      console.error('Exception adding professional to favorites:', error)
      setAlert({
        show: true,
        type: 'error',
        title: 'Error',
        message: 'An error occurred. Please try again.'
      })
    } finally {
      setPendingFavoriteProfessional(null)
      setShowFavoriteProfessionalModal(false)
    }
  }

  // Helper function to parse time slot string to minutes
  const parseTimeSlotToMinutes = (timeSlotString) => {
    if (!timeSlotString) return null
    
    // Handle formats like "10:00 AM - 11:00 AM" or "10:00 AM"
    const parts = timeSlotString.split(' - ')
    const startTime = parts[0].trim()
    
    // Parse start time
    const [time, period] = startTime.split(' ')
    const [hours, minutes] = time.split(':')
    let hour24 = parseInt(hours)
    if (period === 'PM' && hour24 !== 12) hour24 += 12
    if (period === 'AM' && hour24 === 12) hour24 = 0
    
    return {
      start: hour24 * 60 + (parseInt(minutes) || 0),
      end: parts[1] ? (() => {
        const [endTime, endPeriod] = parts[1].trim().split(' ')
        const [endHours, endMinutes] = endTime.split(':')
        let endHour24 = parseInt(endHours)
        if (endPeriod === 'PM' && endHour24 !== 12) endHour24 += 12
        if (endPeriod === 'AM' && endHour24 === 12) endHour24 = 0
        return endHour24 * 60 + (parseInt(endMinutes) || 0)
      })() : null
    }
  }

  // Helper function to check if two time ranges overlap
  const timeRangesOverlap = (startA, endA, startB, endB) => {
    if (startA === null || startB === null) return false
    // If end times are not provided, assume 1 hour duration
    const actualEndA = endA !== null ? endA : startA + 60
    const actualEndB = endB !== null ? endB : startB + 60
    
    // Check if ranges overlap: startA < endB && startB < endA
    return startA < actualEndB && startB < actualEndA
  }

  const checkBookingConflict = (date, timeSlot, durationMinutes = null) => {
    if (!date || !timeSlot) return false
    
    // Parse the new booking time slot
    const newTimeRange = parseTimeSlotToMinutes(timeSlot)
    if (!newTimeRange || newTimeRange.start === null) return false
    
    // Calculate end time if duration is provided
    const newEnd = durationMinutes ? newTimeRange.start + durationMinutes : newTimeRange.end
    
    return allBookingsForConflicts.some(booking => {
      // Only check upcoming/pending/confirmed bookings
      if (booking.status && !['upcoming', 'pending', 'confirmed'].includes(booking.status)) {
        return false
      }
      
      // Check if same date
      const existingDate = String(booking.date || booking.appointment_date || '').slice(0, 10)
      if (existingDate !== String(date).slice(0, 10)) return false
      
      // Check if time slots overlap
      const existingSlot = booking.time?.slot || booking.start_time
      if (existingSlot) {
        const existingTimeRange = parseTimeSlotToMinutes(existingSlot)
        if (existingTimeRange && existingTimeRange.start !== null) {
          // Calculate existing booking end time
          // Try to get duration from booking or calculate from slot
          let existingEnd = existingTimeRange.end
          if (!existingEnd && booking.duration) {
            const duration = parseInt((booking.duration || '0 min').replace(/\D/g, '')) || 60
            existingEnd = existingTimeRange.start + duration
          } else if (!existingEnd) {
            existingEnd = existingTimeRange.start + 60 // Default 1 hour
          }
          
          // Check for overlap
          if (timeRangesOverlap(newTimeRange.start, newEnd, existingTimeRange.start, existingEnd)) {
        return true
          }
        }
      }
      
      return false
    })
  }

  const confirmBooking = async () => {
    // Campaign demo: allow booking without login — data stays in sessionStorage only
    if (!user && isDemoMode()) {
      console.log('🎭 DEMO MODE — booking without login (local only)')
    } else if (!user) {
      // CHECK IF USER IS LOGGED IN FIRST
      console.log('⚠️ User not logged in - showing login modal')
      setAlert({
        show: true,
        type: 'info',
        title: 'Login Required',
        message: 'Please login or create an account to book an appointment.'
      })
      // Close booking modals
      setShowTeamBooking(false)
      setShowServiceBooking(false)
      // Show login modal
      setTimeout(() => {
        setAuthMode('login')
        setShowAuth(true)
      }, 500)
      return
    }

    console.log(isDemoMode() ? '🎭 DEMO MODE — proceeding with local booking' : '✅ User logged in - proceeding with booking')

    // Calculate total amount and duration first (needed for conflict check)
    const subtotal = bookingData.services.reduce((sum, service) => sum + parseFloat(service.price || 0), 0)
    const discount =
      bookingData.deal && !bookingData.deal.isDurationSplit
        ? computeDealDiscount(bookingData.services, bookingData.deal)
        : (bookingData.deal?.isDurationSplit ? 0 : (bookingData.discount || 0))
    const afterDiscount = Math.max(subtotal - discount, 0)
    const tax = afterDiscount * taxRate
    const totalAmount = afterDiscount + tax
    
    const totalDuration = bookingData.services.reduce(
      (sum, service) => sum + parseServiceDurationMinutes(service),
      0
    )

    // Check for conflicts with overlapping time slots (single-day continuous bookings only)
    const isPerServiceConfirmEarly =
      bookingData.schedulingMode === SCHEDULING_MODE_PER_SERVICE &&
      canUsePerServiceScheduling(bookingData.deal, bookingData.services)
    if (
      !isPerServiceConfirmEarly &&
      !(bookingData.deal?.total_days > 1) &&
      checkBookingConflict(bookingData.date, bookingData.time?.slot, totalDuration)
    ) {
      const dateObj = new Date(bookingData.date)
      const formattedDate = dateObj.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
      
      // Find the conflicting booking to show its details
      const conflictingBooking = bookings.find(booking => {
        if (booking.status && !['upcoming', 'pending', 'confirmed'].includes(booking.status)) {
          return false
        }
        if (booking.date !== bookingData.date) return false
        if (!booking.time?.slot) return false
        
        const existingRange = parseTimeSlotToMinutes(booking.time.slot)
        const newRange = parseTimeSlotToMinutes(bookingData.time?.slot)
        if (!existingRange || !newRange || existingRange.start === null || newRange.start === null) return false
        
        let existingEnd = existingRange.end
        if (!existingEnd && booking.duration) {
          const duration = parseInt((booking.duration || '0 min').replace(/\D/g, '')) || 60
          existingEnd = existingRange.start + duration
        } else if (!existingEnd) {
          existingEnd = existingRange.start + 60
        }
        
        const newEnd = totalDuration ? newRange.start + totalDuration : newRange.end
        
        return timeRangesOverlap(newRange.start, newEnd, existingRange.start, existingEnd)
      })
      
      const conflictTime = conflictingBooking?.time?.slot || bookingData.time?.slot
      setAlert({
        show: true,
        type: 'warning',
        title: 'Booking Conflict',
        message: `You already have an appointment on ${formattedDate} at ${conflictTime}. Please choose a different date or time.`
      })
      return
    }

    // Convert time slot to HH:MM:SS format
    const isMultiDay = bookingData.deal?.total_days > 1
    const isPerServiceConfirm =
      bookingData.schedulingMode === SCHEDULING_MODE_PER_SERVICE &&
      canUsePerServiceScheduling(bookingData.deal, bookingData.services)

    if (isPerServiceConfirm) {
      bookingData.services = applyScheduleToServices(
        bookingData.services,
        bookingData.serviceTimeSelections || {},
        bookingData.date
      ).map((service, index) => {
        const key = serviceScheduleKey(service, index)
        const sel = bookingData.serviceTimeSelections?.[key]
        return {
          ...service,
          scheduled_date: sel?.date || service.scheduled_date || bookingData.date || null,
        }
      })
    }

    const timeSlot = isMultiDay
      ? (bookingData.daySelections?.[0]?.time?.slotStart || bookingData.daySelections?.[0]?.time?.slot || bookingData.daySelections?.[0]?.time)
      : (bookingData.time?.slot || bookingData.time)
    let time24 = convertTo24Hour(timeSlot)
    if (isPerServiceConfirm) {
      const earliest = earliestSelectionStart(bookingData.serviceTimeSelections || {})
      if (earliest) {
        time24 = earliest.length === 5 ? `${earliest}:00` : earliest
      }
    }

    // Declare variables for professional assignment
    let selectedProfessionalId = null
    let selectedProfessionalData = null

    // Check if services already have professionals assigned (multi-professional booking)
    const servicesWithProfessionals = bookingData.services.filter(service => {
      const pid =
        service.professional?.id ||
        service.selectedProfessional?.id ||
        service.assignedProfessional?.id ||
        service.professional_id
      return pid && pid !== 'any'
    })
    
    console.log('🔍 Booking confirmation check:', {
      servicesCount: bookingData.services.length,
      servicesWithProfessionals: servicesWithProfessionals.length,
      specialistId: bookingData.specialist?.id,
      specialistName: bookingData.specialist?.name
    })
    
    // If services already have professionals assigned, use the first one as primary
    // (This is for multi-professional bookings where each service has its own professional)
    if (servicesWithProfessionals.length > 0) {
      const firstServiceProfessional =
        servicesWithProfessionals[0].professional ||
        servicesWithProfessionals[0].selectedProfessional ||
        servicesWithProfessionals[0].assignedProfessional
      selectedProfessionalId = firstServiceProfessional.id
      selectedProfessionalData = {
        id: firstServiceProfessional.id,
        name: firstServiceProfessional.name,
        specialty: firstServiceProfessional.role || firstServiceProfessional.specialty
      }
      console.log('✅ Multi-professional booking detected. Using first professional as primary:', selectedProfessionalData.name)

      // Normalize linked professional fields onto each service for createAppointment
      bookingData.services.forEach((service) => {
        const pro =
          service.professional ||
          service.selectedProfessional ||
          service.assignedProfessional ||
          null
        if (pro?.id && pro.id !== 'any') {
          service.professional = pro
          service.selectedProfessional = pro
          service.professional_id = pro.id
        }
      })
    } else if (bookingData.specialist?.id && bookingData.specialist.id !== 'any') {
      // Single specific professional selected
      selectedProfessionalId = bookingData.specialist.id
      selectedProfessionalData = {
        id: bookingData.specialist.id,
        name: bookingData.specialist.name,
        specialty: bookingData.specialist.specialty || bookingData.specialist.role
      }
      console.log('✅ Single professional selected:', selectedProfessionalData.name)
    } else {
      // "Any specialist" selected - need to find best available
      selectedProfessionalId = null
      selectedProfessionalData = null
      console.log('⚠️ "Any Specialist" selected - will find best available')
    }

    // Auto-assign when every service still needs a real professional ("Any" flow)
    let perServiceProfessionals = []
    let usedProfessionalIds = new Set()
    const needsAutoAssign =
      (bookingData.services || []).some(
        (s) => !s.professional?.id || s.professional?.id === 'any'
      ) &&
      (bookingData.specialist?.id === 'any' ||
        servicesWithProfessionals.length === 0 ||
        (bookingData.services || []).some((s) => s.professional?.id === 'any'))

    if (needsAutoAssign) {
      const parseTimeToMinutesLocal = (timeString) => {
        if (!timeString) return null
        const parts = String(timeString).split(':')
        const hh = parseInt(parts[0], 10)
        const mm = parseInt(parts[1] || '0', 10)
        if (Number.isNaN(hh) || Number.isNaN(mm)) return null
        return hh * 60 + mm
      }

      const minutesToTimeStringLocal = (minutes) => {
        const h = Math.floor(minutes / 60) % 24
        const m = minutes % 60
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
      }

      const toTime24 = (timeVal) => {
        let timeSlot = timeVal
        if (timeSlot && typeof timeSlot === 'object') {
          timeSlot = timeSlot.time || timeSlot.slotStart || timeSlot.slot || timeSlot.start
        }
        if (!timeSlot) return null
        const converted = convertTo24Hour(timeSlot)
        if (converted) return converted.length === 5 ? `${converted}:00` : converted
        return null
      }

      // Group by day so multi-day offers reset the clock each day
      const servicesByDay = new Map()
      ;(bookingData.services || []).forEach((service, index) => {
        const dayNum = Number(service.day_number) || 1
        if (!servicesByDay.has(dayNum)) servicesByDay.set(dayNum, [])
        servicesByDay.get(dayNum).push({ service, index })
      })

      for (const [dayNum, items] of servicesByDay.entries()) {
        // Reset per day — a pro busy on Day 1 may be the only free option on Day 2
        usedProfessionalIds = new Set()
        const dayIdx = dayNum - 1
        const daySel = isMultiDay ? bookingData.daySelections?.[dayIdx] : null
        const availabilityDate = isMultiDay
          ? (daySel?.date || bookingData.date)
          : bookingData.date
        const dayStartTime24 =
          toTime24(daySel?.time) ||
          toTime24(bookingData.time) ||
          time24
        let currentStartMinutes = parseTimeToMinutesLocal(dayStartTime24)
        if (currentStartMinutes == null) {
          setAlert({
            show: true,
            type: 'error',
            title: 'Invalid Time',
            message: 'Could not determine the start time for this booking. Please go back and select a time.',
          })
          return
        }

        for (const { service, index } of items) {
          if (service.professional?.id && service.professional.id !== 'any') {
            perServiceProfessionals[index] = service.professional
            usedProfessionalIds.add(String(service.professional.id))
            const svcDurationKeep =
              service.duration_minutes ||
              (service.duration ? parseInt((service.duration || '0 min').replace(/\D/g, '')) || 60 : 60)
            currentStartMinutes += svcDurationKeep
            continue
          }

          const serviceCategoryId = service.category_id || service.category?.id || null
          let serviceCategoryName =
            service.categoryName ||
            service.category?.name ||
            service.category_name ||
            service.group ||
            null
          if (!serviceCategoryName && serviceCategoryId && categories?.length) {
            const cat = categories.find((c) => String(c.id) === String(serviceCategoryId))
            if (cat?.name) serviceCategoryName = cat.name
          }

          let relevantProfessionals = []
          if (serviceCategoryId && service.id) {
            relevantProfessionals = filterProfessionalsForService(
              (specialists || []).filter((s) => s?.id && s.id !== 'any'),
              serviceCategoryId,
              service.id
            )
          }

          if (relevantProfessionals.length === 0) {
            relevantProfessionals = (specialists || []).filter((specialist) => {
              if (!specialist || specialist.id === 'any') return false
              if (!specialist.specializations?.length) return false
              return specialist.specializations.some((spec) => {
                if (typeof spec === 'string') {
                  const specLower = spec.toLowerCase()
                  return (
                    (serviceCategoryName && serviceCategoryName.toLowerCase() === specLower) ||
                    (serviceCategoryId && String(serviceCategoryId).toLowerCase() === specLower)
                  )
                }
                if (typeof spec === 'object' && spec !== null) {
                  const specName = spec.name || spec.category_name
                  const specId = spec.id || spec.category_id
                  return (
                    (specName &&
                      serviceCategoryName &&
                      serviceCategoryName.toLowerCase() === specName.toLowerCase()) ||
                    (specId && serviceCategoryId && String(serviceCategoryId) === String(specId))
                  )
                }
                return false
              })
            })
          }

          if (relevantProfessionals.length === 0) {
            relevantProfessionals = (specialists || []).filter((s) => s?.id && s.id !== 'any')
          }

          if (relevantProfessionals.length === 0) {
            setAlert({
              show: true,
              type: 'error',
              title: 'No Specialist Found',
              message: `No specialist is configured for "${service.name}". Please select a specific professional for this service.`,
            })
            return
          }

          const allRelevantIds = relevantProfessionals.map((prof) => prof.id)
          let preferredIds = allRelevantIds.filter((id) => !usedProfessionalIds.has(String(id)))
          if (preferredIds.length === 0) preferredIds = allRelevantIds

          const serviceDurationMinutes =
            service.duration_minutes ||
            (service.duration ? parseInt((service.duration || '0 min').replace(/\D/g, '')) || 60 : 60)
          const scheduledStart = service.scheduled_start_time || service.scheduledStartTime
          const serviceStartTime24 = scheduledStart
            ? (String(scheduledStart).length === 5 ? `${scheduledStart}:00` : String(scheduledStart))
            : minutesToTimeStringLocal(currentStartMinutes)
          const serviceId = service.id || service.service_id

          let bestProfessionalResult = await findBestAvailableProfessional(
            preferredIds,
            availabilityDate,
            serviceStartTime24,
            serviceDurationMinutes,
            serviceId,
            serviceCategoryId
          )

          // If "prefer unused" left only busy pros, retry the full eligible pool
          if (!bestProfessionalResult.success && preferredIds !== allRelevantIds) {
            bestProfessionalResult = await findBestAvailableProfessional(
              allRelevantIds,
              availabilityDate,
              serviceStartTime24,
              serviceDurationMinutes,
              serviceId,
              serviceCategoryId
            )
          }

          let svcProfessional = null
          if (bestProfessionalResult.success && bestProfessionalResult.data) {
            svcProfessional = specialists.find(
              (spec) => String(spec.id) === String(bestProfessionalResult.data.professionalId)
            )
          }

          if (!svcProfessional) {
            setAlert({
              show: true,
              type: 'error',
              title: 'No Available Specialist',
              message:
                bestProfessionalResult.error ||
                `No professionals are free for "${service.name}" at the selected time. Please choose another time.`,
            })
            return
          }

          const assignedPro = {
            id: svcProfessional.id,
            name: svcProfessional.name,
            specialty: svcProfessional.role || svcProfessional.specialty,
          }
          perServiceProfessionals[index] = assignedPro
          usedProfessionalIds.add(String(assignedPro.id))
          if (!scheduledStart) {
            currentStartMinutes += serviceDurationMinutes
          }
        }
      }

      // Determine a primary professional for the parent appointment:
      // use the first successfully assigned per-service professional.
      const firstAssigned = perServiceProfessionals.find((p) => p && p.id)
      if (firstAssigned) {
        selectedProfessionalId = firstAssigned.id
        selectedProfessionalData = {
          id: firstAssigned.id,
          name: firstAssigned.name,
          specialty: firstAssigned.specialty,
        }
      }

      // Write assigned pros onto services for createAppointment
      bookingData.services = (bookingData.services || []).map((service, index) => {
        const assigned = perServiceProfessionals[index]
        if (!assigned) return service
        return {
          ...service,
          professional: assigned,
          selectedProfessional: assigned,
          professional_id: assigned.id,
        }
      })
    }

    // Validate that we have a professional assigned
    if (!selectedProfessionalId) {
      console.error('❌ No professional assigned. Cannot create appointment.')
      setAlert({
        show: true,
        type: 'error',
        title: 'Missing Professional',
        message: 'No professional has been assigned to this booking. Please select a professional or try again.'
      })
      return
    }

    // Campaign demo + card: skip Stripe — save locally as paid
    if (bookingData.paymentMethod === 'card' && isDemoMode()) {
      const demoUserId = user?.id || DEMO_USER_ID
      const appointmentData = {
        userId: demoUserId,
        profileId: profile?.id || DEMO_USER_ID,
        professionalId: selectedProfessionalId,
        salonId: salonId || null,
        appointmentDate: bookingData.date,
        startTime: time24,
        durationMinutes: totalDuration,
        services: bookingData.services.map((service, index) => {
          const perServicePro = perServiceProfessionals[index]
          const finalProfessional = perServicePro || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name }
          return {
            id: service.id,
            name: service.name,
            price: service.price,
            duration: service.duration,
            duration_minutes: parseServiceDurationMinutes(service),
            category_id: service.category_id,
            professional: finalProfessional,
            professional_id: finalProfessional.id || service.professional_id || selectedProfessionalId,
            scheduled_start_time: service.scheduled_start_time || null,
            scheduled_end_time: service.scheduled_end_time || null,
            scheduled_date: service.scheduled_date || null,
            day_number: service.day_number || 1,
          }
        }),
        professionalData: {
          primary: selectedProfessionalData || null,
          professionals: bookingData.services
            .map((service, index) => perServiceProfessionals[index] || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name })
            .filter(prof => prof && prof.id && prof.id !== 'any'),
          professionalIds: [],
          original: bookingData.specialist?.id !== 'any' ? bookingData.specialist : null
        },
        totalAmount: totalAmount,
        paymentMethod: 'card',
        paymentStatus: 'paid',
        status: 'confirmed',
        notes: '[DEMO — fake card payment, not saved to database]'
      }
      appointmentData.professionalData.professionalIds = [
        ...new Set(appointmentData.professionalData.professionals.map(p => p.id).filter(Boolean))
      ]

      const result = await createAppointment(appointmentData)
      if (!result.success) {
        setAlert({
          show: true,
          type: 'error',
          title: 'Demo Booking Failed',
          message: result.error || 'Could not create demo booking'
        })
        return
      }

      setShowTeamBooking(false)
      setShowServiceBooking(false)
      setAlert({
        show: true,
        type: 'success',
        title: 'Demo booking confirmed',
        message: 'Payment was simulated. Nothing was saved to the real database. Close the tab to clear demo data.'
      })
      // Refresh local demo bookings list used for conflict checks
      try {
        const demoResult = await getUserAppointments(demoUserId, { upcomingOnly: false })
        if (demoResult.success) {
          const localBookings = (demoResult.data || []).map(apt => convertAppointmentToBooking(apt)).filter(Boolean)
          setBookings(localBookings)
        }
      } catch (_) { /* ignore */ }
      return
    }

    // Package reschedule: keep original payment, pick new days, cancel old group (do not re-charge full package)
    if (rescheduleGroupBookingId && bookingData.deal?.total_days > 1) {
      const oldSiblings = allBookingsForConflicts.filter(
        (b) => getGroupBookingId(b) === rescheduleGroupBookingId
      )
      const oldPrimary = getDealPrimaryBooking(oldSiblings) || oldSiblings[0] || null
      const preservedMethod =
        oldPrimary?.payment_method ||
        oldPrimary?.paymentMethod ||
        bookingData.paymentMethod ||
        'cash'
      const preservedStatus =
        oldPrimary?.payment_status || oldPrimary?.paymentStatus || 'unpaid'
      const wasPaid = String(preservedStatus).toLowerCase() === 'paid'
      const preservedIntent =
        oldPrimary?.payment_intent_id || oldPrimary?.paymentIntentId || null

      const mappedServices = bookingData.services.map((service, index) => {
        const perServicePro = perServiceProfessionals[index]
        const finalProfessional =
          perServicePro ||
          service.professional ||
          { id: selectedProfessionalId, name: selectedProfessionalData?.name }
        return {
          id: service.id,
          name: service.name,
          price: service.price,
          duration: service.duration,
          duration_minutes: parseServiceDurationMinutes(service),
          category_id: service.category_id,
          professional: finalProfessional,
          professional_id:
            finalProfessional.id || service.professional_id || selectedProfessionalId,
          day_number: service.day_number || 1,
        }
      })

      const days = Number(bookingData.deal.total_days) || 0
      const selections = bookingData.daySelections || []
      const allDaysReady =
        days > 0 &&
        Array.from({ length: days }).every((_, i) => selections[i]?.date && selections[i]?.time)
      if (!allDaysReady || mappedServices.length === 0) {
        setAlert({
          show: true,
          type: 'error',
          title: 'Invalid Booking Data',
          message: 'Please select a date and time for every package day before confirming.',
        })
        return
      }

      const multiDayData = {
        userId: user?.id || (isDemoMode() ? DEMO_USER_ID : null),
        profileId: profile?.id || (isDemoMode() ? DEMO_USER_ID : null),
        salonId: salonId || null,
        daySelections: selections,
        services: mappedServices,
        totalAmount: totalAmount,
        paymentMethod: preservedMethod,
        paymentStatus: wasPaid ? 'paid' : preservedStatus || 'unpaid',
        status: 'confirmed',
        notes: (() => {
          const oldNotesSource = String(oldPrimary?.notes || oldSiblings?.[0]?.notes || '')
          const countMatch = oldNotesSource.match(/\[RESCHEDULE_COUNT:(\d+)\]/)
          const nextCount = (countMatch?.[1] ? parseInt(countMatch[1], 10) : 0) + 1
          return `[RESCHEDULED_FROM_GROUP:${rescheduleGroupBookingId}]\n[RESCHEDULE_COUNT:${Number.isFinite(nextCount) ? nextCount : 1}]`
        })(),
        offerId: bookingData.deal?.isDurationSplit ? null : bookingData.deal?.id || null,
        paymentIntentId: wasPaid ? preservedIntent : null,
      }

      const result = await createMultiDayAppointments(multiDayData)
      if (!result.success) {
        setAlert({
          show: true,
          type: 'error',
          title: 'Reschedule Failed',
          message: result.error || 'Failed to reschedule package. Please try again.',
        })
        return
      }

      const oldGroupId = rescheduleGroupBookingId
      const newGroupId = result.groupBookingId || result.data?.group_booking_id || null
      const oldNotesSource = String(oldPrimary?.notes || oldSiblings?.[0]?.notes || '')
      const countMatch = oldNotesSource.match(/\[RESCHEDULE_COUNT:(\d+)\]/)
      const nextCount = (countMatch?.[1] ? parseInt(countMatch[1], 10) : 0) + 1
      const rescheduleToNotes = [
        `[RESCHEDULED_TO_GROUP:${newGroupId || 'new'}]`,
        `[RESCHEDULE_COUNT:${Number.isFinite(nextCount) ? nextCount : 1}]`,
      ].join('\n')

      try {
        const cancelOld = await cancelAppointmentsByGroupBookingId(oldGroupId, {
          primaryAppointmentId: oldPrimary?.id || oldSiblings?.[0]?.id || null,
          notes: rescheduleToNotes,
        })
        if (!cancelOld.success) {
          console.warn('⚠️ New package booked but failed to cancel old package days:', cancelOld.error)
        }
      } catch (oldCancelError) {
        console.warn('⚠️ Exception cancelling old package after reschedule:', oldCancelError)
      }
      setRescheduleGroupBookingId(null)
      setPendingPackageReschedule(null)

      if (user && isWebCustomer) {
        const appointmentsResult = await getUserAppointments(user.id, { upcomingOnly: false })
        if (appointmentsResult.success) {
          setAppointments(appointmentsResult.data || [])
        }
      }

      setShowTeamBooking(false)
      setShowServiceBooking(false)
      clearPendingBookingDraft()
      setBookingData({
        specialist: null,
        services: [],
        date: '',
        time: null,
        paymentMethod: 'card',
        discountCode: '',
        discount: 0,
        deal: null,
        daySelections: [],
        packageReschedule: null,
        schedulingMode: 'continuous',
        serviceTimeSelections: {},
      })
      window.dispatchEvent(new Event('clearServicesBookingSummary'))

      setAlert({
        show: true,
        type: 'success',
        title: 'Package Rescheduled',
        message: 'Your package dates were updated. The previous package days were cancelled.',
      })
      return
    }

    // Check if payment method is card - if so, show payment modal first
    if (bookingData.paymentMethod === 'card') {
      const mappedServices = bookingData.services.map((service, index) => {
        const perServicePro = perServiceProfessionals[index]
        const finalProfessional = perServicePro || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name }
        return {
          id: service.id,
          name: service.name,
          price: service.price,
          duration: service.duration,
          duration_minutes: parseServiceDurationMinutes(service),
          category_id: service.category_id,
          professional: finalProfessional,
          professional_id: finalProfessional.id || service.professional_id || selectedProfessionalId,
          day_number: service.day_number || 1,
          scheduled_start_time: service.scheduled_start_time || null,
          scheduled_end_time: service.scheduled_end_time || null,
          scheduled_date: service.scheduled_date || null,
        }
      })

      const serviceProfessionals = mappedServices
        .map((service) => service.professional)
        .filter(prof => prof && prof.id && prof.id !== 'any')
      const professionalIds = [...new Set(serviceProfessionals.map(prof => prof.id).filter(Boolean))]
      const professionalData = {
        primary: selectedProfessionalData || null,
        professionals: serviceProfessionals,
        professionalIds,
        original: bookingData.specialist?.id !== 'any' ? bookingData.specialist : null
      }

      if (isMultiDay) {
        const days = Number(bookingData.deal.total_days) || 0
        const selections = bookingData.daySelections || []
        const allDaysReady = days > 0 && Array.from({ length: days }).every((_, i) => selections[i]?.date && selections[i]?.time)
        if (!allDaysReady || mappedServices.length === 0) {
          setAlert({
            show: true,
            type: 'error',
            title: 'Invalid Booking Data',
            message: 'Please select a date and time for every package day before paying.',
          })
          return
        }

        const pendingMultiDay = {
          isMultiDay: true,
          userId: user.id,
          profileId: profile?.id || null,
          salonId: salonId || null,
          daySelections: selections,
          services: mappedServices,
          totalAmount: totalAmount,
          paymentMethod: 'card',
          paymentStatus: 'unpaid',
          status: 'pending',
          notes: null,
          offerId: bookingData.deal?.isDurationSplit ? null : (bookingData.deal?.id || null),
          appointmentDate: selections[0]?.date,
          startTime: time24,
          professionalData,
        }

        const holdResult = await createPaymentHoldFromPending(pendingMultiDay)
        if (!holdResult.success) {
          const errMsg = String(holdResult.error || '')
          const errLower = errMsg.toLowerCase()
          const slotTaken =
            errLower.includes('no longer available') ||
            errLower.includes('duplicate key') ||
            errLower.includes('already booked')
          const preferServerMessage =
            /(?:is|are) no longer available at the selected time/i.test(errMsg) ||
            /professional is already booked/i.test(errMsg)
          setAlert({
            show: true,
            type: 'warning',
            title: slotTaken ? 'Time Slot Unavailable' : 'Unable to proceed',
            message: slotTaken
              ? (preferServerMessage
                  ? errMsg
                  : 'This time slot is no longer available. Another user has already booked this time. Please select a different time slot.')
              : (holdResult.error || 'Could not reserve this time slot. Please try again.'),
          })
          return
        }

        setPendingAppointmentData({
          ...pendingMultiDay,
          paymentHold: holdResult.hold,
        })
        setPaymentMode('booking')
        setRescheduleFeeContext(null)
        setShowPaymentModal(true)
        return
      }

      const appointmentDataForPayment = {
        userId: user.id,
        profileId: profile?.id || null,
        professionalId: selectedProfessionalId,
        salonId: salonId || null,
        appointmentDate: bookingData.date,
        startTime: time24,
        durationMinutes: totalDuration,
        services: mappedServices,
        professionalData,
        totalAmount: totalAmount,
        paymentMethod: 'card',
        paymentStatus: 'unpaid',
        status: 'pending',
        notes: null
      }

      if (!appointmentDataForPayment.appointmentDate || !appointmentDataForPayment.startTime || 
          !appointmentDataForPayment.services || appointmentDataForPayment.services.length === 0) {
        setAlert({
          show: true,
          type: 'error',
          title: 'Invalid Booking Data',
          message: 'Missing required booking information. Please try booking again.',
        })
        return
      }

      const holdResult = await createPaymentHoldFromPending(appointmentDataForPayment)
      if (!holdResult.success) {
        const errMsg = String(holdResult.error || '')
        const errLower = errMsg.toLowerCase()
        const slotTaken =
          errLower.includes('no longer available') ||
          errLower.includes('duplicate key') ||
          errLower.includes('already booked')
        const preferServerMessage =
          /(?:is|are) no longer available at the selected time/i.test(errMsg) ||
          /professional is already booked/i.test(errMsg)
        setAlert({
          show: true,
          type: 'warning',
          title: slotTaken ? 'Time Slot Unavailable' : 'Unable to proceed',
          message: slotTaken
            ? (preferServerMessage
                ? errMsg
                : 'This time slot is no longer available. Another user has already booked this time. Please select a different time slot.')
            : (holdResult.error || 'Could not reserve this time slot. Please try again.'),
        })
        return
      }

      setPendingAppointmentData({
        ...appointmentDataForPayment,
        paymentHold: holdResult.hold,
      })
      setPaymentMode('booking')
      setRescheduleFeeContext(null)
      setShowPaymentModal(true)
      return
    }

    // For cash payments, proceed with normal flow
    // Prepare appointment data for database
    // Include professional assignment for each service (from multi-category booking)
    const appointmentData = {
      userId: user?.id || (isDemoMode() ? DEMO_USER_ID : null),
      profileId: profile?.id || (isDemoMode() ? DEMO_USER_ID : null),
      professionalId: selectedProfessionalId, // Primary professional (first one or "any")
      salonId: salonId || null, // Selected franchise location
      appointmentDate: bookingData.date,
      startTime: time24,
      durationMinutes: totalDuration,
      services: bookingData.services.map((service, index) => {
        const perServicePro = perServiceProfessionals[index]
        const finalProfessional = perServicePro || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name }
        return {
          id: service.id,
          name: service.name,
          price: service.price,
          duration: service.duration,
          duration_minutes: parseServiceDurationMinutes(service),
          category_id: service.category_id,
          professional: finalProfessional,
          professional_id: finalProfessional.id || service.professional_id || selectedProfessionalId,
          scheduled_start_time: service.scheduled_start_time || null,
          scheduled_end_time: service.scheduled_end_time || null,
          scheduled_date: service.scheduled_date || null,
          day_number: service.day_number || 1,
        }
      }),
      // Format professionalData to match mobile app structure: { primary, professionals, professionalIds, original }
      professionalData: (() => {
        const serviceProfessionals = bookingData.services
          .map((service, index) => {
            const perServicePro = perServiceProfessionals[index]
            return perServicePro || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name }
          })
          .filter(prof => prof && prof.id && prof.id !== 'any')
        
        const professionalIds = [...new Set(
          serviceProfessionals.map(prof => prof.id).filter(Boolean)
        )]
        
        return {
          primary: selectedProfessionalData || null,
          professionals: serviceProfessionals,
          professionalIds: professionalIds,
          original: bookingData.specialist?.id !== 'any' ? bookingData.specialist : null
        }
      })(),
      totalAmount: totalAmount,
      paymentMethod: bookingData.paymentMethod || 'cash',
      paymentStatus: 'unpaid',
      status: 'confirmed',
      notes: isDemoMode() ? '[DEMO — not saved to database]' : null
    }

    let result
    // Guard: long carts must use multi-day create, never one mega appointment
    const overflowGuard =
      !(bookingData.deal?.total_days > 1)
        ? evaluateDurationOverflow(
            bookingData.services,
            selectedSalon?.business_hours || salon?.businessHours || salon?.business_hours || null,
            {
              date:
                bookingData.date ||
                bookingData.daySelections?.[0]?.date ||
                null,
            }
          )
        : null
    if (overflowGuard?.needed) {
      setAlert({
        show: true,
        type: 'warning',
        title: 'Book across multiple days',
        message:
          overflowGuard.message +
          '\n\nGo back and continue with the multi-day schedule so each day is saved separately.',
      })
      return
    }

    if (bookingData.deal?.total_days > 1) {
      const multiDayData = {
        userId: user?.id || (isDemoMode() ? DEMO_USER_ID : null),
        profileId: profile?.id || (isDemoMode() ? DEMO_USER_ID : null),
        salonId: salonId || null,
        daySelections: bookingData.daySelections,
        services: bookingData.services.map((service, index) => {
          const perServicePro = perServiceProfessionals[index]
          const finalProfessional = perServicePro || service.professional || { id: selectedProfessionalId, name: selectedProfessionalData?.name }
          return {
            id: service.id,
            name: service.name,
            price: service.price,
            duration: service.duration,
            duration_minutes: parseServiceDurationMinutes(service),
            category_id: service.category_id,
            professional: finalProfessional,
            professional_id: finalProfessional.id || service.professional_id || selectedProfessionalId,
            day_number: service.day_number || 1,
            scheduled_start_time: service.scheduled_start_time || null,
            scheduled_end_time: service.scheduled_end_time || null,
            scheduled_date: service.scheduled_date || null,
          }
        }),
        totalAmount: totalAmount,
        paymentMethod: bookingData.paymentMethod || 'cash',
        paymentStatus: 'unpaid',
        status: 'confirmed',
        notes: isDemoMode() ? '[DEMO — multi-day, not saved to database]' : null,
        offerId: bookingData.deal?.isDurationSplit ? null : (bookingData.deal?.id || null)
      }
      console.log('📅 Creating multi-day appointments:', multiDayData)
      result = await createMultiDayAppointments(multiDayData)
    } else {
      console.log(isDemoMode() ? '🎭 DEMO MODE — creating local appointment:' : '📅 Creating appointment in database:', appointmentData)
      result = await createAppointment(appointmentData)
    }

    if (!result.success) {
      // Use a warning instead of an error to avoid noisy red console logs
      console.warn('⚠️ Failed to create appointment:', result.error)

      const errorMessage = result.error || ''
      const errorLower = errorMessage.toLowerCase()
      
      // Check for different types of conflicts
      const isUserConflict =
        errorLower.includes('already have a booking') ||
        errorLower.includes('already have an appointment')
      
      const isSlotUnavailable =
        errorLower.includes('time slot is no longer available') ||
        errorLower.includes('no longer available') ||
        errorLower.includes('already booked') ||
        errorLower.includes('duplicate key value violates unique constraint') ||
        errorLower.includes('idx_appointment_segments_unique_exact_slot') ||
        errorLower.includes('idx_appointment_segments_exact_slot_unique') ||
        errorLower.includes('prevent_overlapping_appointments') ||
        result.error?.code === '23505' // PostgreSQL unique constraint violation

      if (isSlotUnavailable) {
        const preferServerMessage =
          /(?:is|are) no longer available at the selected time/i.test(errorMessage) ||
          /professional is already booked/i.test(errorMessage)

        setAlert({
          show: true,
          type: 'warning',
          title: 'Time Slot Unavailable',
          message: preferServerMessage
            ? errorMessage
            : 'This time slot is no longer available. Another user has already booked this time. Please select a different time slot.'
        })
      } else if (isUserConflict) {
        setAlert({
          show: true,
          type: 'warning',
          title: 'You Already Have an Appointment',
          message: 'You already have an appointment at this time. Please choose a different slot.'
        })
      } else {
        setAlert({
          show: true,
          type: 'error',
          title: 'Booking Failed',
          message: result.error || 'Failed to create appointment. Please try again.'
        })
      }
      return
    }

    console.log('✅ Appointment created successfully:', result.data.id)

    const wasCashPackageReschedule = Boolean(rescheduleGroupBookingId)

    // Package reschedule: cancel the entire old deal group after the new one is created
    if (rescheduleGroupBookingId) {
      try {
        const cancelOld = await cancelAppointmentsByGroupBookingId(rescheduleGroupBookingId)
        if (!cancelOld.success) {
          console.warn('⚠️ New package booked but failed to cancel old package days:', cancelOld.error)
        } else {
          console.log('✅ Old package group cancelled after reschedule:', cancelOld.cancelledIds)
        }
      } catch (oldCancelError) {
        console.warn('⚠️ Exception cancelling old package after reschedule:', oldCancelError)
      }
      setRescheduleGroupBookingId(null)
    }

    // Send notification for appointment booking (demo notifications stay local)
    if (user?.id) {
    try {
      const servicesList = bookingData.services.map(s => s.name).join(', ')
      const professionalName = selectedProfessionalData?.name || 'your specialist'
      await sendNotification(
        user.id,
        'Appointment Confirmed',
        `Your appointment for ${servicesList} with ${professionalName} on ${bookingData.date} at ${bookingData.time?.slot || bookingData.time} has been confirmed.`,
        NOTIFICATION_TYPES.APPOINTMENT_BOOKED,
        result.data.id
      )
    } catch (notifError) {
      console.warn('⚠️ Failed to send notification:', notifError)
      // Don't fail the booking if notification fails
    }
    } else if (isDemoMode()) {
      setAlert({
        show: true,
        type: 'success',
        title: 'Demo booking confirmed',
        message: 'Nothing was saved to the real database. Close the tab to clear demo data.'
      })
    }

    // Refresh appointments list
    if (user && isWebCustomer) {
      const appointmentsResult = await getUserAppointments(user.id, { upcomingOnly: true })
      if (appointmentsResult.success) {
        setAppointments(appointmentsResult.data || [])
      }
    }

    // Also add to local bookings for immediate UI update
    if (appointmentData.isMultiDay && result.allData) {
      const newBookings = result.allData.map(dbApt => ({
        ...dbApt,
        services: Array.isArray(dbApt.services) ? dbApt.services : bookingData.services,
        status: 'confirmed',
        createdAt: dbApt.created_at || new Date().toISOString(),
        statusUpdatedAt: dbApt.updated_at || new Date().toISOString(),
        professionalData: appointmentData.professionalData,
        professionalIds: appointmentData.professionalData?.professionalIds || [],
        time: dbApt.start_time || dbApt.appointment_time || bookingData.time
      }))
      setBookings(prev => [...prev, ...newBookings])
    } else {
      const newBooking = {
        id: result.data.id,
        ...bookingData,
        status: 'confirmed',
        createdAt: result.data.created_at,
        statusUpdatedAt: result.data.updated_at,
        professionalData: appointmentData.professionalData,
        professionalIds: appointmentData.professionalData?.professionalIds || []
      }
      setBookings(prev => [...prev, newBooking])
    }

    // Close modals
    setShowTeamBooking(false)
    setShowServiceBooking(false)
    clearPendingBookingDraft()
    setBookingData({
      specialist: null,
      services: [],
      date: '',
      time: null,
      paymentMethod: 'card',
      discountCode: '',
      discount: 0,
      deal: null,
      daySelections: [],
      schedulingMode: 'continuous',
      serviceTimeSelections: {},
    })

    // Clear Services sticky selection panel (selected services should not persist after a successful booking)
    window.dispatchEvent(new Event('clearServicesBookingSummary'))

    // Cash path previously closed quietly; card already shows Booking Confirmed after Stripe.
    if (!isDemoMode()) {
      setAlert({
        show: true,
        type: 'success',
        title: wasCashPackageReschedule ? 'Package Rescheduled!' : 'Booking Confirmed!',
        message: wasCashPackageReschedule
          ? 'Your package was rescheduled successfully.'
          : 'Your appointment is booked. Please pay at the salon.',
      })
    }
  }

  // Handle payment success - create appointment with paid status
  const handlePaymentSuccess = async (paymentResult) => {
    const pending = pendingAppointmentData
    if (!pending) {
      console.error('❌ No pending appointment data for payment success')
      setAlert({
        show: true,
        type: 'error',
        title: 'Error',
        message: 'Payment succeeded but booking data is missing. Please contact support.',
      })
      return
    }

    try {
      const appointmentData = {
        ...pending,
        paymentMethod: 'card',
        paymentStatus: 'paid',
        status: 'confirmed',
        paymentIntentId: paymentResult.paymentIntentId || null // Store payment intent ID for reference
      }

      console.log('📅 Creating appointment after payment:', {
        ...appointmentData,
        services: appointmentData.services?.length || 0,
        professionalId: appointmentData.professionalId,
        date: appointmentData.appointmentDate,
        time: appointmentData.startTime,
        isMultiDay: !!appointmentData.isMultiDay,
        hasHold: !!pending.paymentHold,
      })

      // Prefer confirming a pre-payment hold (locks the slot before Stripe charge).
      // Fall back to create-after-pay for older pending payloads / reschedule paths.
      const createPaidBooking = (payload) =>
        payload.isMultiDay
          ? createMultiDayAppointments(payload)
          : createAppointment(payload)

      let result = pending.paymentHold
        ? await confirmPaymentHold(
            pending.paymentHold,
            paymentResult.paymentIntentId || null,
            pending.notes || null,
          )
        : await createPaidBooking(appointmentData)
      let retryCount = 0
      const maxRetries = 2

      // Retry logic for transient errors (create-after-pay path only)
      while (!result.success && !pending.paymentHold && retryCount < maxRetries) {
        const errorMsg = result.error?.toLowerCase() || ''
        // Only retry for specific errors (not validation errors)
        if (errorMsg.includes('network') || errorMsg.includes('timeout') || errorMsg.includes('connection')) {
          retryCount++
          console.log(`🔄 Retrying appointment creation (attempt ${retryCount}/${maxRetries})...`)
          await new Promise(resolve => setTimeout(resolve, 400 * retryCount)) // Exponential backoff
          result = await createPaidBooking(appointmentData)
        } else {
          break // Don't retry for validation or business logic errors
        }
      }

      if (!result.success) {
        console.error('❌ Appointment creation failed after payment:', result.error)
        const errorMessage = String(result.error || '')
        const errorLower = errorMessage.toLowerCase()
        const isSlotUnavailable =
          errorLower.includes('time slot is no longer available') ||
          errorLower.includes('duplicate key value violates unique constraint') ||
          errorLower.includes('idx_appointment_segments_exact_slot_unique') ||
          errorLower.includes('idx_appointment_segments_unique_exact_slot') ||
          errorLower.includes('already booked')

        let refunded = false
        if (paymentResult.paymentIntentId) {
          try {
            const { processRefund } = await import('./services/refundService')
            const refundResult = await processRefund(
              paymentResult.paymentIntentId,
              typeof paymentResult.amountUSD === 'number' ? paymentResult.amountUSD : null,
            )
            refunded = !!refundResult?.success
          } catch (refundErr) {
            console.warn('Automatic refund failed after booking error:', refundErr)
          }
        }

        await releasePaymentHold(pending.paymentHold)

        setAlert({
          show: true,
          type: isSlotUnavailable ? 'warning' : 'error',
          title: isSlotUnavailable ? 'Time Slot Unavailable' : 'Booking Failed',
          message: isSlotUnavailable
            ? (refunded
              ? 'This time slot is no longer available. Your payment has been refunded automatically. Please select a different time.'
              : 'This time slot is no longer available. If you were charged, contact support with payment ID: ' + (paymentResult.paymentIntentId || 'N/A') + '.')
            : (refunded
              ? (result.error || 'Payment was taken but booking failed, so a refund was issued.')
              : (result.error || 'Payment succeeded but booking failed. Please contact support with your payment ID: ' + (paymentResult.paymentIntentId || 'N/A') + '.')),
        })
        setShowPaymentModal(false)
        setPendingAppointmentData(null)
        return
      }

      console.log('✅ Appointment created successfully after payment (already paid):', result.data.id)

      // Package reschedule: cancel the entire old deal group after the new one is created
      const wasPackageReschedule = !!rescheduleGroupBookingId
      if (rescheduleGroupBookingId) {
        try {
          const cancelOld = await cancelAppointmentsByGroupBookingId(rescheduleGroupBookingId)
          if (!cancelOld.success) {
            console.warn('⚠️ New package booked but failed to cancel old package days:', cancelOld.error)
          } else {
            console.log('✅ Old package group cancelled after paid reschedule:', cancelOld.cancelledIds)
          }
        } catch (oldCancelError) {
          console.warn('⚠️ Exception cancelling old package after paid reschedule:', oldCancelError)
        }
        setRescheduleGroupBookingId(null)
        setPendingPackageReschedule(null)
      }

      const created = result.data
      const createdAll = result.allData || (created ? [created] : [])
      const bookingSnapshot = bookingData

      setShowPaymentModal(false)
      setPendingAppointmentData(null)
      setShowTeamBooking(false)
      setShowServiceBooking(false)
      setTeamBookingStep(1)
      setServiceBookingStep(1)
      clearPendingBookingDraft()
      setBookingData({
        specialist: null,
        services: [],
        date: '',
        time: null,
        paymentMethod: 'card',
        discountCode: '',
        discount: 0,
        deal: null,
        daySelections: [],
        schedulingMode: 'continuous',
        serviceTimeSelections: {},
      })
      window.dispatchEvent(new Event('clearServicesBookingSummary'))

      setAlert({
        show: true,
        type: 'success',
        title: wasPackageReschedule ? 'Package Rescheduled!' : 'Booking Confirmed!',
        message: wasPackageReschedule
          ? 'Your package was rescheduled and payment is complete.'
          : 'Your appointment is booked and payment is complete.',
      })

      if (appointmentData.isMultiDay && createdAll.length > 0) {
        setBookings((prev) => [
          ...prev,
          ...createdAll.map((dbApt) => ({
            ...dbApt,
            services: Array.isArray(dbApt.services) ? dbApt.services : bookingSnapshot.services,
            status: 'confirmed',
            paymentStatus: dbApt.payment_status || 'paid',
            paymentMethod: dbApt.payment_method || 'card',
            payment_status: dbApt.payment_status || 'paid',
            payment_method: dbApt.payment_method || 'card',
            createdAt: dbApt.created_at,
            statusUpdatedAt: dbApt.updated_at,
            professionalData: pending.professionalData,
            professionalIds: pending.professionalData?.professionalIds || [],
            time: dbApt.start_time || dbApt.appointment_time || bookingSnapshot.time,
          })),
        ])
      } else {
        setBookings((prev) => [
          ...prev,
          {
            id: created.id,
            ...bookingSnapshot,
            status: 'confirmed',
            paymentStatus: created.payment_status || 'paid',
            paymentMethod: created.payment_method || 'card',
            payment_status: created.payment_status || 'paid',
            payment_method: created.payment_method || 'card',
            payment_intent_id: created.payment_intent_id || null,
            createdAt: created.created_at,
            statusUpdatedAt: created.updated_at,
            professionalData: pending.professionalData,
            professionalIds: pending.professionalData?.professionalIds || [],
          },
        ])
      }

      void (async () => {
        try {
          await recordAppointmentPayment({
            appointmentId: created.id,
            userId: user.id,
            paymentType: 'booking_charge',
            direction: 'charge',
            amountUSD: paymentResult.amountUSD,
            paymentIntentId: paymentResult.paymentIntentId,
            meta: {
              services: pending.services?.map((s) => s.name).join(', ') || '',
              professional_name: pending.professionalData?.primary?.name || '',
              appointment_date: pending.appointmentDate,
              appointment_time: pending.startTime,
            },
          })
        } catch (paymentRecordError) {
          console.warn('⚠️ Exception recording payment in appointment_payments:', paymentRecordError)
        }

        try {
          const servicesList = (pending.services || []).map((s) => s.name).join(', ')
          const professionalName = pending.professionalData?.primary?.name || 'your specialist'
          const date = pending.appointmentDate
          const timeStr = pending.startTime || ''
          const [hours, minutes] = timeStr.split(':')
          const hour24 = parseInt(hours) || 0
          const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
          const period = hour24 >= 12 ? 'PM' : 'AM'
          const time = `${hour12}:${minutes || '00'} ${period}`

          await Promise.allSettled([
            sendNotification(
              user.id,
              'Appointment Confirmed',
              `Your appointment for ${servicesList} with ${professionalName} on ${date} at ${time} has been confirmed.`,
              NOTIFICATION_TYPES.APPOINTMENT_BOOKED,
              created.id
            ),
            sendNotification(
              user.id,
              'Payment Successful',
              `Your payment of ${formatPrice(paymentResult.amountUSD, currency)} has been processed successfully.`,
              NOTIFICATION_TYPES.PAYMENT_SUCCESS,
              created.id
            ),
          ])
        } catch (notifError) {
          console.warn('⚠️ Failed to send notifications:', notifError)
        }

        if (user && isWebCustomer) {
          try {
            const appointmentsResult = await getUserAppointments(user.id, { upcomingOnly: false })
            if (appointmentsResult.success) {
              setAppointments(appointmentsResult.data || [])
            }
          } catch (refreshError) {
            console.warn('⚠️ Failed to refresh appointments after payment:', refreshError)
          }
        }
      })()
    } catch (error) {
      console.error('❌ Exception creating appointment after payment:', error)
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        paymentIntentId: paymentResult?.paymentIntentId
      })

      let refunded = false
      if (paymentResult?.paymentIntentId) {
        try {
          const { processRefund } = await import('./services/refundService')
          const refundResult = await processRefund(
            paymentResult.paymentIntentId,
            typeof paymentResult.amountUSD === 'number' ? paymentResult.amountUSD : null,
          )
          refunded = !!refundResult?.success
        } catch (refundErr) {
          console.warn('Automatic refund failed after booking exception:', refundErr)
        }
      }

      await releasePaymentHold(pending?.paymentHold)

      setAlert({
        show: true,
        type: 'error',
        title: 'Booking Failed',
        message: refunded
          ? `Payment was taken but booking failed, so a refund was issued. ${error.message || ''}`
          : `Payment succeeded but booking failed due to an unexpected error: ${error.message || 'Unknown error'}. Please contact support with payment ID: ${paymentResult?.paymentIntentId || 'N/A'}.`,
      })
      setShowPaymentModal(false)
      setPendingAppointmentData(null)
    }
  }

  // Triggered from NavigationWrapper when a reschedule fee needs to be charged for a card-paid booking
  const openRescheduleFeePayment = (booking, feeInfo, closeSidebar) => {
    const amountUSD =
      Number(feeInfo.feeAmountUSD) ||
      Number(feeInfo.feeAmount) ||
      0

    setPaymentMode('reschedule_fee')
    setRescheduleFeeContext({
      amountUSD,
      feePercentage: feeInfo.feePercentage,
      appointmentId: booking.id,
      userId: user?.id || null,
    })
    setRescheduleAfterFeeBooking(booking)
    setShowPaymentModal(true)

    // Close sidebar (if provided) so payment modal is on top of the main content
    if (typeof closeSidebar === 'function') {
      closeSidebar()
    }
  }

  // Handle payment success for reschedule fee (card-paid)
  const handleRescheduleFeePaymentSuccess = async (paymentResult) => {
    const booking = rescheduleAfterFeeBooking
    const feeContext = rescheduleFeeContext
    const packagePayload = pendingPackageReschedule
    setShowPaymentModal(false)
    setRescheduleAfterFeeBooking(null)

    if (!booking) {
      console.error('❌ Reschedule fee paid but no booking stored')
      setPendingPackageReschedule(null)
      return
    }

    try {
      window.dispatchEvent(
        new CustomEvent('rescheduleFeePaid', {
          detail: { bookingId: booking.id },
        }),
      )
    } catch (e) {
      console.error('Failed to dispatch rescheduleFeePaid event:', e)
    }

    void (async () => {
      try {
        await recordAppointmentPayment({
          appointmentId: booking.id,
          userId: feeContext?.userId || user?.id,
          paymentType: 'reschedule_fee',
          direction: 'charge',
          amountUSD: feeContext?.amountUSD || paymentResult.amountUSD,
          paymentIntentId: paymentResult.paymentIntentId,
          meta: {
            fee_percentage: feeContext?.feePercentage || null,
            reason: 'reschedule_inside_window',
          },
        })
      } catch (paymentRecordError) {
        console.warn('⚠️ Exception recording reschedule fee payment in appointment_payments:', paymentRecordError)
      }

      try {
        const { supabase } = await import('./config/supabase')
        const { data: existingAppointment } = await supabase
          .from('appointments')
          .select('notes')
          .eq('id', booking.id)
          .single()

        const existingNotes = existingAppointment?.notes || ''
        const timestamp = Date.now()
        const feePaidTag = `[RESCHEDULE_FEE_PAID:${timestamp}:${paymentResult.paymentIntentId}]`
        const updatedNotes = existingNotes
          ? `${existingNotes}\n${feePaidTag}`
          : feePaidTag

        const { data: { session: updateSession } } = await supabase.auth.getSession()
        if (!updateSession?.user) {
          throw new Error('Not authenticated')
        }

        const { error: updateError } = await supabase
          .from('appointments')
          .update({ notes: updatedNotes })
          .eq('id', booking.id)
          .eq('user_id', updateSession.user.id)

        if (updateError) {
          throw updateError
        }
      } catch (notesError) {
        console.warn('⚠️ Failed to add RESCHEDULE_FEE_PAID tag to notes:', notesError)
      }
    })()

    // Multi-day package: after fee, open the package date/time picker (not single BookingModal)
    if (packagePayload) {
      setPendingPackageReschedule(null)
      openServiceBookingWithServices(packagePayload)
      setAlert({
        show: true,
        type: 'success',
        title: 'Fee paid',
        message: 'Reschedule fee received. Pick new dates and times for each package day.',
      })
    }
  }

  // Helper function to convert time to 24-hour format
  const convertTo24Hour = (time12h) => {
    if (!time12h) return '09:00:00'
    
    // Handle formats like "9:00 AM" or "09:00 AM"
    const [time, period] = time12h.split(' ')
    const [hours, minutes] = time.split(':')
    let hour24 = parseInt(hours)
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minutes || '00'}:00`
  }

  const scrollToServices = () => {
    // Match mobile: open the dedicated all-services page
    if (window.location.pathname === '/services') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    window.location.assign('/services')
  }

  /** Open specialist profile to pick services into the shared cart (mobile-like). */
  const openSpecialistForSharedCart = (specialist) => {
    if (!specialist) return
    setSelectedSpecialist(specialist)
    setShowSpecialistProfile(true)
    setShowTeamBooking(false)
  }

  /**
   * Merge professional-first service picks into the shared cart, keep other pros' lines,
   * then open the unified ServiceBookingModal (supports mixed professionals).
   */
  const continueTeamServicesIntoSharedCart = (selectedServices = [], specialist = null) => {
    const pro = specialist || bookingData.specialist
    if (!pro || !selectedServices.length) return

    const stampedItems = selectedServices.map((service) => {
      const category = findCategoryForService(categories, service)
      const linePro =
        service.professional ||
        service.selectedProfessional ||
        (pro.id !== 'any' ? pro : null) ||
        pro
      return buildCartItem(service, linePro, category)
    })

    const existing = readBookingCart()
    const selectedIds = new Set(stampedItems.map((item) => String(item.service.id)))
    const kept = existing.filter((item) => {
      const sid = String(item.service?.id)
      if (selectedIds.has(sid)) return false
      if (pro?.id && String(item.professional?.id) === String(pro.id)) return false
      return true
    })

    const merged = mergeCartItems(kept, stampedItems)
    try {
      localStorage.setItem(BOOKING_CART_STORAGE_KEY, JSON.stringify(merged))
    } catch (_) {
      // no-op
    }
    dispatchRestoreBookingCart(merged)

    setShowTeamBooking(false)
    setShowSpecialistProfile(false)
    setTeamBookingStep(1)

    openServiceBookingWithServices({
      services: cartToBookingServices(merged),
      specialist: resolveCartSpecialist(merged),
      startStep: 2,
    })
  }

  const handleHeroOfferRef = useRef(null)

  const openServiceBookingWithServices = ({
    services,
    specialist,
    startStep = 2,
    skipOverflowCheck = false,
    deal: forcedDeal = null,
    rescheduleGroupId = null,
    paymentMethod: forcedPaymentMethod = null,
    packageReschedule = null,
  }) => {
    const businessHours =
      selectedSalon?.business_hours ||
      salon?.businessHours ||
      salon?.business_hours ||
      null

    const overflow = skipOverflowCheck
      ? null
      : evaluateDurationOverflow(services, businessHours)

    const startBooking = (finalServices, deal = null) => {
      const totalDays = Number(deal?.total_days) || 1
      const packedServices =
        totalDays > 1
          ? distributeServicesForMultiDay(finalServices, totalDays)
          : finalServices
      if (rescheduleGroupId) {
        setRescheduleGroupBookingId(rescheduleGroupId)
      }
      const lockedMethod =
        packageReschedule?.paymentMethod ||
        forcedPaymentMethod ||
        null
      setBookingData((prev) => ({
        ...prev,
        services: packedServices,
        specialist: specialist || prev.specialist || null,
        date: '',
        time: null,
        deal,
        discount: deal ? computeDealDiscount(packedServices, deal) : 0,
        discountCode: deal?.isDurationSplit ? '' : (deal?.title || ''),
        paymentMethod: lockedMethod || prev.paymentMethod || 'card',
        packageReschedule: packageReschedule || null,
        daySelections:
          totalDays > 1
            ? Array.from({ length: totalDays }, () => ({ date: '', time: null }))
            : [],
      }))
      setShowCategoryPage(false)
      setShowMultiCategoryBooking(false)
      setShowTeamBooking(false)
      setShowServiceBooking(true)
      setServiceBookingStep(startStep)
    }

    if (forcedDeal?.total_days > 1) {
      startBooking(services, forcedDeal)
      return
    }

    if (overflow?.needed) {
      setAlert({
        show: true,
        type: 'confirm',
        title: 'Book across multiple days?',
        message: overflow.message,
        confirmText: `Continue with ${overflow.totalDays} days`,
        cancelText: 'Edit services',
        onConfirm: () => {
          setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
          startBooking(overflow.services, overflow.deal)
        },
        onCancel: () => {
          setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null })
        },
      })
      return
    }

    startBooking(services, null)
  }

  const handleHeroOffer = (offer) => {
    // If booking started on a deals page, stay there (don't open over home).
    try {
      const returnPath = sessionStorage.getItem('dealBookingReturnPath')
      if (
        returnPath &&
        returnPath.startsWith('/offers') &&
        window.location.pathname !== returnPath.split('?')[0]
      ) {
        try {
          sessionStorage.setItem('pendingBookOffer', JSON.stringify(offer))
        } catch (_) {
          // no-op
        }
        window.dispatchEvent(new CustomEvent('ensureDealBookingPath'))
        return
      }
    } catch (_) {
      // no-op
    }

    const catalogServices = (categories || []).flatMap((cat) =>
      (cat.services || []).map((service) => ({
        ...service,
        category_id: service.category_id || cat.id,
        categoryName: cat.name,
      }))
    )
    const offerServices = (offer?.services || [])
      .map((service) => {
        const fromCatalog =
          catalogServices.find((item) => String(item.id) === String(service.id)) ||
          catalogServices.find(
            (item) =>
              item.name &&
              service.name &&
              item.name.toLowerCase().trim() === service.name.toLowerCase().trim()
          )
        const transformed = transformService(service)
        if (!fromCatalog && !transformed) return null
        const nestedCategory =
          service.service_categories?.name ||
          service.service_categories?.[0]?.name ||
          null
        return {
          ...(fromCatalog || transformed),
          category_id:
            fromCatalog?.category_id ||
            service.category_id ||
            transformed?.category_id ||
            null,
          categoryName: fromCatalog?.categoryName || nestedCategory,
          group: fromCatalog?.categoryName || nestedCategory || fromCatalog?.group || '',
          day_number: service.day_number || 1,
          duration_minutes:
            service.duration_minutes ??
            fromCatalog?.duration_minutes ??
            (typeof service.duration_minutes === 'number' ? service.duration_minutes : undefined),
          duration:
            fromCatalog?.duration ||
            transformed?.duration ||
            (service.duration_minutes ? `${service.duration_minutes} min` : undefined),
        }
      })
      .filter(Boolean)
    if (offerServices.length === 0) {
      // Don't yank the user to the home services section for deals.
      console.warn('Deal booking: no matching services found for offer', offer?.id)
      return
    }
    const totalDays = Number(offer.total_days) || 1
    const distributedOfferServices = distributeServicesForMultiDay(offerServices, totalDays)
    const deal = {
      id: offer.id,
      title: offer.title,
      discountPercent: Number(offer.discount_percent) || 0,
      serviceIds: distributedOfferServices.map((service) => service.id),
      total_days: totalDays,
      ...buildOfferPolicyFields(offer),
    }
    setBookingData((prev) => ({
      ...prev,
      specialist: null,
      services: distributedOfferServices,
      date: '',
      time: null,
      deal,
      discount: computeDealDiscount(offerServices, deal),
      discountCode: offer.title || '',
      daySelections: totalDays > 1
        ? Array.from({ length: totalDays }, () => ({ date: '', time: null }))
        : [],
    }))
    setShowTeamBooking(false)
    setShowServiceBooking(true)
    setServiceBookingStep(1)
  }

  handleHeroOfferRef.current = handleHeroOffer

  useEffect(() => {
    const openBookOffer = (offer) => {
      if (!offer || typeof handleHeroOfferRef.current !== 'function') return
      if (!categories?.length) {
        try {
          sessionStorage.setItem('pendingBookOffer', JSON.stringify(offer))
        } catch (_) {
          // no-op
        }
        return
      }
      handleHeroOfferRef.current(offer)
    }

    const onBookPackageOffer = (event) => {
      openBookOffer(event?.detail)
    }

    window.addEventListener('bookPackageOffer', onBookPackageOffer)
    return () => window.removeEventListener('bookPackageOffer', onBookPackageOffer)
  }, [categories])

  useEffect(() => {
    if (!categories?.length) return
    if (!user && !isDemoMode()) return
    try {
      const raw = sessionStorage.getItem('pendingBookOffer')
      if (!raw) return

      // Prefer opening the booking modal on the deals page, not home.
      const returnPath = sessionStorage.getItem('dealBookingReturnPath')
      const returnPathname = returnPath ? returnPath.split('?')[0] : null
      if (returnPathname && returnPathname.startsWith('/offers') && window.location.pathname !== returnPathname) {
        window.dispatchEvent(new CustomEvent('ensureDealBookingPath'))
        return
      }

      sessionStorage.removeItem('pendingBookOffer')
      const offer = JSON.parse(raw)
      if (offer) handleHeroOfferRef.current?.(offer)
    } catch (_) {
      sessionStorage.removeItem('pendingBookOffer')
    }
  }, [categories, user])

  const handleSearchBooking = ({ service, specialist, date, time }) => {
    if (!service || !date || !time) return
    const serviceCategory = categories.find(cat => 
      cat.services.some(s => s.id === service.id)
    )?.name

    setBookingData(prev => ({
      ...prev,
      services: [service],
      serviceCategory,
      specialist: specialist || null,
      date,
      time,
      deal: null,
      discount: 0,
      discountCode: '',
    }))

    setShowTeamBooking(false)
    setShowServiceBooking(true)
    setServiceBookingStep(specialist ? 2 : 1)
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <CurrencyProvider>
      <ScrollToTopOnRouteChange />
      <DealBookingLocationGuard />

      {/* Global loader overlay while initial data is loading */}
      {dataLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <img
              src="/fav-icon3.png"
              alt="Loading..."
              className="w-20 h-20 animate-pulse"
            />
            <p className="text-sm font-medium text-gray-600">Loading...</p>
          </div>
        </div>
      )}

      <div className="bg-gray-50">
        <NavigationWrapper 
          user={user}
          appointments={appointments}
          favorites={favorites}
          specialists={specialists}
          categories={categories}
          services={services}
          toggleFavorite={toggleFavorite}
          scrollToServices={scrollToServices}
          setAuthMode={setAuthMode}
          setShowAuth={setShowAuth}
          bookings={bookings}
          setBookings={setBookings}
          setAppointments={setAppointments}
          setBookingData={setBookingData}
          setShowServiceBooking={setShowServiceBooking}
          setServiceBookingStep={setServiceBookingStep}
          favoriteServicesWithAssociations={favoriteServicesWithAssociations}
          favoriteProfessionalsWithAssociations={favoriteProfessionalsWithAssociations}
          setFavoriteServicesWithAssociations={setFavoriteServicesWithAssociations}
          setFavoriteProfessionalsWithAssociations={setFavoriteProfessionalsWithAssociations}
          favoriteAppointments={favoriteAppointments}
          setFavoriteAppointments={setFavoriteAppointments}
          cancellationPolicy={cancellationPolicy}
          reschedulePolicy={reschedulePolicy}
          openRescheduleFeePayment={openRescheduleFeePayment}
          openServiceBookingWithServices={openServiceBookingWithServices}
          rescheduleGroupBookingId={rescheduleGroupBookingId}
          setRescheduleGroupBookingId={setRescheduleGroupBookingId}
          pendingPackageReschedule={pendingPackageReschedule}
          setPendingPackageReschedule={setPendingPackageReschedule}
          pendingPackageRescheduleRef={pendingPackageRescheduleRef}
        />

        {authSuccessMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
            <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-3 rounded-lg shadow-lg">
              <p className="text-sm font-medium">{authSuccessMessage}</p>
            </div>
          </div>
        )}
        
        <main>
        <Routes>
          <Route path="/" element={
            <HomePage 
              scrollToServices={scrollToServices}
              onOfferClick={handleHeroOffer}
              onRequireLogin={() => {
                setAuthMode('login')
                setShowAuth(true)
              }}
              categories={categories}
              specialists={specialists}
              favoriteSpecialists={favorites.specialists}
              favoriteServices={favorites.services}
              bookings={bookings}
              onCategoryClick={(category) => {
                // This is now handled within Services component
                // Keep for backward compatibility but don't open modal
              }}
              onBookServiceFromFavorites={(serviceData) => {
                // Handle favorite service click - navigate to home and pre-select service
                // Don't open modal automatically - let user click "Book" on the service
                // The service will be pre-selected and highlighted in Services component
                // User can then click "Book" button on that service to proceed
              }}
              onBookSpecialistFromFavorites={(specialistData) => {
                openSpecialistForSharedCart(specialistData)
              }}
              onConfirmBooking={(bookingSummary) => {
                // Convert booking summary to booking data format
                // Ensure professional objects include all fields (especially image)
                const allServices = bookingSummary.map(item => ({
                  ...item.service,
                  professional: {
                    ...item.professional,
                    // Ensure image fields are preserved
                    image: item.professional.image || item.professional.image_url || null,
                    image_url: item.professional.image_url || item.professional.image || null,
                    role: item.professional.role || item.professional.specialty || null,
                    specialty: item.professional.specialty || item.professional.role || null
                  },
                  categoryName: item.category?.name || item.service?.category?.name
                }))
                
                // Determine top-level specialist (use first professional if all same, otherwise "any")
                const professionals = [...new Set(bookingSummary.map(item => item.professional.id))]
                const selectedSpecialist = professionals.length === 1 
                  ? {
                      ...bookingSummary[0].professional,
                      // Ensure image fields are preserved
                      image: bookingSummary[0].professional.image || bookingSummary[0].professional.image_url || null,
                      image_url: bookingSummary[0].professional.image_url || bookingSummary[0].professional.image || null
                    }
                  : { id: 'any', name: 'Any Specialist', image: null, image_url: null }
                
                openServiceBookingWithServices({
                  services: allServices,
                  specialist: selectedSpecialist,
                  startStep: 2,
                })
              }}
              onFavoriteSpecialist={(id) => toggleFavorite('specialists', id)}
              onFavoriteService={(id) => toggleFavorite('services', id)}
              onSpecialistClick={(specialist) => {
                openSpecialistForSharedCart(specialist)
              }}
              onBookClick={(specialist) => {
                openSpecialistForSharedCart(specialist)
              }}
              reviews={reviews}
              salon={salon}
            />
          } />
          <Route
            path="/services"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AllServicesPage
                  categories={categories}
                  specialists={specialists}
                  salon={salon}
                  favoriteServices={favorites.services}
                  onFavoriteService={(id) => toggleFavorite('services', id)}
                  onConfirmBooking={(bookingSummary) => {
                    // Same path as home Services — includes multi-day overflow prompt
                    const allServices = (bookingSummary || []).map((item) => ({
                      ...item.service,
                      professional: {
                        ...item.professional,
                        image: item.professional?.image || item.professional?.image_url || null,
                        image_url: item.professional?.image_url || item.professional?.image || null,
                        role: item.professional?.role || item.professional?.specialty || null,
                        specialty: item.professional?.specialty || item.professional?.role || null,
                      },
                      categoryName: item.category?.name || item.service?.category?.name,
                    }))
                    const professionals = [
                      ...new Set(
                        (bookingSummary || [])
                          .map((item) => item.professional?.id)
                          .filter(Boolean),
                      ),
                    ]
                    const selectedSpecialist =
                      professionals.length === 1
                        ? {
                            ...bookingSummary[0].professional,
                            image:
                              bookingSummary[0].professional.image ||
                              bookingSummary[0].professional.image_url ||
                              null,
                            image_url:
                              bookingSummary[0].professional.image_url ||
                              bookingSummary[0].professional.image ||
                              null,
                          }
                        : { id: 'any', name: 'Any Specialist', image: null, image_url: null }

                    openServiceBookingWithServices({
                      services: allServices,
                      specialist: selectedSpecialist,
                      startStep: 2,
                    })
                  }}
                />
              </Suspense>
            }
          />
          <Route path="/app-reset-password" element={<AppResetPasswordBridge />} />
          <Route path="/verify-reset-code" element={<VerifyResetCode />} />
          <Route path="/verify-email" element={<VerifyEmailCode />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route 
            path="/auth/callback" 
            element={<AuthCallback />}
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <Dashboard 
                  user={user}
                  profile={profile}
                  bookings={bookings}
                  setBookings={setBookings}
                  refreshProfessionals={refreshProfessionals}
                  cancellationPolicy={cancellationPolicy}
                  reschedulePolicy={reschedulePolicy}
                  appointments={appointments}
                  setAppointments={setAppointments}
                  favorites={favorites}
                  toggleFavorite={toggleFavorite}
                  specialists={specialists}
                  categories={categories}
                  services={services}
                  favoriteAppointments={favoriteAppointments}
                  setFavoriteAppointments={setFavoriteAppointments}
                  favoriteServicesWithAssociations={favoriteServicesWithAssociations}
                  favoriteProfessionalsWithAssociations={favoriteProfessionalsWithAssociations}
                  onBookNow={() => {
                    window.location.assign('/services')
                  }}
                />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/support"
            element={<Navigate to="/dashboard?tab=support" replace />}
          />
          <Route
            path="/privacypolicy"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />
          <Route path="/privacy-policy" element={<Navigate to="/privacypolicy" replace />} />
          <Route
            path="/termsofservice"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfService />
              </Suspense>
            }
          />
          <Route path="/terms-of-service" element={<Navigate to="/termsofservice" replace />} />
          <Route
            path="/termsofuse"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfUse />
              </Suspense>
            }
          />
          <Route path="/terms-of-use" element={<Navigate to="/termsofuse" replace />} />
          <Route
            path="/contact"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ContactPage />
              </Suspense>
            }
          />
          <Route
            path="/delete-account"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <DeleteAccountPage />
              </Suspense>
            }
          />
          <Route path="/deleteaccount" element={<Navigate to="/delete-account" replace />} />
          <Route
            path="/offers/:campaignSlug/package/:packageId"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PackageDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/offers/:campaignSlug"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <OffersPage />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>

        {showAuth && (
          <AuthModal
            mode={authMode}
            onClose={() => setShowAuth(false)}
            onSwitchMode={() => setAuthMode(mode => mode === 'login' ? 'signup' : 'login')}
            onAuth={handleAuth}
            onForgotPassword={() => {
              setShowAuth(false)
              setShowForgotPassword(true)
            }}
          />
        )}

        {showForgotPassword && (
          <ForgotPasswordModal
            onClose={() => setShowForgotPassword(false)}
            onBackToLogin={() => {
              setShowForgotPassword(false)
              setAuthMode('login')
              setShowAuth(true)
            }}
          />
        )}

        {showCategoryPage && selectedCategory && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <CategoryModal
            category={selectedCategory}
            onClose={() => {
              setShowCategoryPage(false)
              // Don't reset multiCategoryBooking on close, allow user to continue
            }}
            favoriteServices={favorites.services}
            onToggleFavorite={(id) => toggleFavorite('services', id)}
            specialists={specialists}
            multiCategoryMode={showMultiCategoryBooking}
            multiCategoryBooking={multiCategoryBooking}
            onServiceSelect={(data) => {
              if (showMultiCategoryBooking) {
                // Multi-category mode: data is {category, services, professional}
                if (data.category && data.services && data.professional) {
                  setMultiCategoryBooking(prev => {
                    // Check if category already exists
                    const existingIndex = prev.findIndex(item => item.category.id === data.category.id)
                    if (existingIndex >= 0) {
                      // Update existing category
                      const updated = [...prev]
                      updated[existingIndex] = {
                        category: data.category,
                        services: data.services.map(service => ({
                          ...service,
                          professional: data.professional
                        }))
                      }
                      return updated
                    } else {
                      // Add new category
                      return [...prev, {
                        category: data.category,
                        services: data.services.map(service => ({
                          ...service,
                          professional: data.professional
                        }))
                      }]
                    }
                  })
                  // Close modal to allow selecting another category
                  setShowCategoryPage(false)
                  setSelectedCategory(null)
                }
              } else {
                // Single category mode: data is array of services
                if (!data || (Array.isArray(data) && data.length === 0)) return
                
                if (Array.isArray(data)) {
                  // Legacy single category flow
              const serviceCategory = categories.find(cat => 
                    cat.services.some(s => data.some(selectedService => selectedService.id === s.id))
              )
              setBookingData(prev => ({
                ...prev,
                    services: data,
                    serviceCategory: serviceCategory?.name
              }))
              setShowCategoryPage(false)
              setShowServiceBooking(true)
                  setServiceBookingStep(1)
                }
              }
            }}
            onAddMoreCategories={() => {
              setShowCategoryPage(false)
              setSelectedCategory(null)
            }}
            onProceedToDateTime={() => {
              // Flatten multi-category booking to single services array for date/time selection
              // and PRESERVE the professional chosen per category so each service knows its professional
              const allServices = multiCategoryBooking.flatMap(item => 
                item.services.map(service => ({
                  ...service,
                  professional: item.professional || service.professional || null,
                  categoryName: item.category.name
                }))
              )
              
              // Determine top-level specialist:
              // - if all services share same professional -> that pro
              // - if multiple different professionals -> treat as "Any Specialist" at top, but keep per-service pros
              const professionals = [...new Set(
                multiCategoryBooking.flatMap(item => 
                  (item.professional ? [item.professional.id] : [])
                )
              )]
              
              const selectedProfessional = professionals.length === 1 
                ? specialists.find(s => s.id === professionals[0]) || { id: professionals[0], name: 'Any Specialist' }
                : { id: 'any', name: 'Any Specialist' }
              
              setBookingData(prev => ({
                ...prev,
                services: allServices,
                specialist: selectedProfessional,
                deal: null,
                discount: 0,
                discountCode: '',
              }))
              
              setShowCategoryPage(false)
              setShowMultiCategoryBooking(false)
              setShowServiceBooking(true)
              setServiceBookingStep(2) // Start at Date & Time step
            }}
          />
          </Suspense>
        )}

        {showSpecialistProfile && selectedSpecialist && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <SpecialistModal
            specialist={selectedSpecialist}
            favorites={favorites.specialists}
            categories={categories}
            onClose={() => setShowSpecialistProfile(false)}
            onFavoriteClick={() => toggleFavorite('specialists', selectedSpecialist.id)}
            onViewCart={() => {
              setShowSpecialistProfile(false)
              // Cart already updated by SpecialistModal — land on home sticky sidebar
              // so the user can keep adding more services. Do NOT open booking modal
              // and do NOT call scrollToServices() (that opens /services).
              const revealStickyCart = () => {
                document.getElementById('services')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
                window.dispatchEvent(new Event('expandServicesBookingCart'))
              }
              const path = window.location.pathname
              if (path === '/' || path === '') {
                setTimeout(revealStickyCart, 50)
                return
              }
              sessionStorage.setItem('keepBookingCart', '1')
              window.location.assign('/#services')
            }}
          />
          </Suspense>
        )}

        {showTeamBooking && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <BookingModal
            booking={bookingData}
            setBooking={setBookingData}
            step={teamBookingStep}
            setStep={setTeamBookingStep}
            specialists={specialists}
            categories={categories}
            bookings={allBookingsForConflicts}
            onContinueFromServices={continueTeamServicesIntoSharedCart}
            onClose={() => {
              if (showPaymentModal) return
              setShowTeamBooking(false)
              setTeamBookingStep(1)
              clearPendingBookingDraft()
              setBookingData((prev) => ({
                ...prev,
                deal: null,
                discount: 0,
                discountCode: '',
              }))
            }}
            lockClose={showPaymentModal}
            onConfirm={confirmBooking}
            onLoginTrigger={() => {
              savePendingBookingDraft({
                bookingData,
                serviceBookingStep,
                teamBookingStep,
                showServiceBooking: false,
                showTeamBooking: true,
              })
              setShowAuth(true)
              setAuthMode('login')
            }}
            onFavoriteAppointment={async (booking, isAdding) => {
              if (!user?.id) return
              
              try {
                const { addAppointmentToFavorites, removeAppointmentFromFavorites } = await import('./services/favoritesService')
                
                if (isAdding) {
                  const result = await addAppointmentToFavorites(user.id, booking)
                  if (result.success) {
                    setAlert({
                      show: true,
                      type: 'success',
                      title: 'Success',
                      message: 'Appointment added to favorites!'
                    })
                    // Refresh favorite appointments
                    const { getFavoriteAppointments } = await import('./services/favoritesService')
                    const appointmentsResult = await getFavoriteAppointments(user.id)
                    setFavoriteAppointments(appointmentsResult.data || [])
                  } else {
                    throw new Error(result.error)
                  }
                } else {
                  // For removal, we'd need the appointment favorite ID
                  // For now, we'll just show a message
                  setAlert({
                    show: true,
                    type: 'info',
                    title: 'Info',
                    message: 'To remove, go to Favorites > Appointments'
                  })
                }
              } catch (error) {
                console.error('Error favoriting appointment:', error)
                throw error
              }
            }}
          />
          </Suspense>
        )}

        {showServiceBooking && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <ServiceBookingModal
            booking={bookingData}
            setBooking={setBookingData}
            step={serviceBookingStep}
            setStep={setServiceBookingStep}
            specialists={specialists}
            categories={categories}
            bookings={allBookingsForConflicts}
            businessHours={
              selectedSalon?.business_hours ||
              salon?.businessHours ||
              salon?.business_hours ||
              null
            }
            onClose={() => {
              if (showPaymentModal) return
              setShowServiceBooking(false)
              setServiceBookingStep(1)
              clearPendingBookingDraft()
              setRescheduleGroupBookingId(null)
              setPendingPackageReschedule(null)
              try {
                sessionStorage.removeItem('dealBookingReturnPath')
                sessionStorage.removeItem('pendingBookOffer')
              } catch (_) {
                // no-op
              }
              setBookingData((prev) => ({
                ...prev,
                deal: null,
                discount: 0,
                discountCode: '',
                daySelections: [],
                packageReschedule: null,
                schedulingMode: canUsePerServiceScheduling(null, prev.services)
                  ? (prev.schedulingMode || 'continuous')
                  : 'continuous',
                serviceTimeSelections:
                  canUsePerServiceScheduling(null, prev.services)
                    ? (prev.serviceTimeSelections || {})
                    : {},
              }))
            }}
            lockClose={showPaymentModal}
            onConfirm={confirmBooking}
            onLoginTrigger={() => {
              savePendingBookingDraft({
                bookingData,
                serviceBookingStep,
                teamBookingStep,
                showServiceBooking: true,
                showTeamBooking: false,
              })
              setShowAuth(true)
              setAuthMode('login')
            }}
            onFavoriteAppointment={async (booking, isAdding) => {
              if (!user?.id) return
              
              try {
                const { addAppointmentToFavorites, removeAppointmentFromFavorites } = await import('./services/favoritesService')
                
                if (isAdding) {
                  const result = await addAppointmentToFavorites(user.id, booking)
                  if (result.success) {
                    setAlert({
                      show: true,
                      type: 'success',
                      title: 'Success',
                      message: 'Appointment added to favorites!'
                    })
                    // Refresh favorite appointments
                    const { getFavoriteAppointments } = await import('./services/favoritesService')
                    const appointmentsResult = await getFavoriteAppointments(user.id)
                    setFavoriteAppointments(appointmentsResult.data || [])
                  } else {
                    throw new Error(result.error)
                  }
                } else {
                  // For removal, we'd need the appointment favorite ID
                  // For now, we'll just show a message
                  setAlert({
                    show: true,
                    type: 'info',
                    title: 'Info',
                    message: 'To remove, go to Favorites > Appointments'
                  })
                }
              } catch (error) {
                console.error('Error favoriting appointment:', error)
                throw error
              }
            }}
          />
          </Suspense>
        )}

        <Suspense fallback={null}>
          <AlertModal
          show={alert.show}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          confirmText={alert.confirmText || (alert.type === 'confirm' ? 'Continue' : 'OK')}
          cancelText={alert.cancelText || 'Cancel'}
          loading={!!alert.loading}
          loadingText={alert.loadingText || 'Please wait…'}
          onConfirm={() => {
            if (alert.loading) return
            if (alert.onConfirm) {
              alert.onConfirm()
            } else {
              setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null, loading: false })
            }
          }}
          onCancel={
            alert.loading
              ? undefined
              : alert.onCancel ||
                (() => setAlert({ show: false, type: 'info', title: '', message: '', onConfirm: null, onCancel: null, loading: false }))
          }
        />
        </Suspense>

        {/* Payment Modal for Stripe payments */}
        <Suspense fallback={null}>
          <PaymentModal
          show={showPaymentModal}
          onClose={() => {
            // User abandoned Stripe checkout — free the reserved slot.
            if (pendingAppointmentData?.paymentHold) {
              releasePaymentHold(pendingAppointmentData.paymentHold)
            }
            setShowPaymentModal(false)
            setPendingAppointmentData(null)
              setRescheduleFeeContext(null)
              setRescheduleAfterFeeBooking(null)
              setPendingPackageReschedule(null)
              setPaymentMode('booking')
          }}
            onSuccess={paymentMode === 'reschedule_fee' ? handleRescheduleFeePaymentSuccess : handlePaymentSuccess}
          bookingData={bookingData}
          appointmentData={pendingAppointmentData}
            mode={paymentMode}
            feeContext={rescheduleFeeContext}
        />
        </Suspense>

        {/* Multi-Category Booking Summary Floating Button */}
        {showMultiCategoryBooking && multiCategoryBooking.length > 0 && !showCategoryPage && (
          <div className="fixed bottom-6 right-6 z-40">
            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[300px]">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold text-lg text-gray-900">Booking Summary</h3>
                <button
                  onClick={() => {
                    setShowMultiCategoryBooking(false)
                    setMultiCategoryBooking([])
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {multiCategoryBooking.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-gray-900 mb-1">{item.category.name}</p>
                    <p className="text-gray-600">
                      {item.services.length} service{item.services.length !== 1 ? 's' : ''} • {item.services[0]?.professional?.name || 'Any Specialist'}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Allow selecting another category
                    setShowCategoryPage(true)
                  }}
                  className="flex-1 px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10 transition text-sm font-semibold"
                >
                  Add More
                </button>
                <button
                  onClick={() => {
                    // Flatten and proceed to date/time
                    const allServices = multiCategoryBooking.flatMap(item => 
                      item.services.map(service => ({
                        ...service,
                        categoryName: item.category.name
                      }))
                    )
                    
                    const professionals = [...new Set(multiCategoryBooking.flatMap(item => 
                      item.services.map(s => s.professional?.id).filter(Boolean)
                    ))]
                    
                    const selectedProfessional = professionals.length === 1 
                      ? specialists.find(s => s.id === professionals[0]) || { id: 'any', name: 'Any Specialist' }
                      : { id: 'any', name: 'Any Specialist' }

                    setShowMultiCategoryBooking(false)
                    setMultiCategoryBooking([])
                    openServiceBookingWithServices({
                      services: allServices,
                      specialist: selectedProfessional,
                      startStep: 2,
                    })
                  }}
                  className="flex-1 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Favorite Modals */}
        <FavoriteServiceModal
          isOpen={showFavoriteServiceModal}
          onClose={() => {
            setShowFavoriteServiceModal(false)
            setPendingFavoriteService(null)
          }}
          service={pendingFavoriteService}
          specialists={specialists}
          categories={categories}
          onConfirm={handleFavoriteServiceConfirm}
        />

        <FavoriteProfessionalModal
          isOpen={showFavoriteProfessionalModal}
          onClose={() => {
            setShowFavoriteProfessionalModal(false)
            setPendingFavoriteProfessional(null)
          }}
          professional={pendingFavoriteProfessional}
          services={services}
          categories={categories}
          onConfirm={handleFavoriteProfessionalConfirm}
        />

      </div>
      </CurrencyProvider>
    </Router>
  )
}

export default App

